// eslint-disable
// graphql typescript definitions

export interface IGraphQLResponseRoot {
  data: IQuery | IMutation | ISubscription;
  errors: Array<IGraphQLResponseError>;
}

export interface IGraphQLResponseError {
  /** Required for all errors */
  message: string;
  locations: Array<IGraphQLResponseErrorLocation>;
  /** 7.2.2 says 'GraphQL servers may provide additional entries to error' */
  [propName: string]: any;
}

export interface IGraphQLResponseErrorLocation {
  line: number;
  column: number;
}

export interface IQuery {
  __typename: 'Query';
  viewer: IViewer;
}

/**
 * The base node for all queries
 */
export interface IViewer {
  __typename: 'Viewer';

  /**
   * Calendar Insights
   */
  calendarInsights: CalendarInsightsErrorable;

  /**
   * Search external groups against a query string
   */
  groupQueryResult: ExternalGroupSearchResultErrorable;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * Non-User Defrag Email Survey Response
   */
  nonUserDefragEmailResponse: NonUserDefragEmailResponseErrorable;

  /**
   * Search org persons against a query string
   */
  orgPersonQueryResultErrorable: OrgPersonQueryResultErrorable;

  /**
   * Search persons against a query string
   */
  personQueryResultErrorable: PersonQueryResultErrorable;

  /**
   * Search external rooms against a query string
   */
  roomQueryResult: ExternalRoomSearchResultErrorable;

  /**
   * The details of a booked scheduling link time.
   */
  schedulingLinkBooking: PublicBookingErrorable;

  /**
   * All the content for a scheduling link, including the available times.
   */
  schedulingLinkDetails: SchedulingLinkDetailsErrorable;
  sudo: ISudo;

  /**
   * Search teams within an org
   */
  teamQueryResultErrorable: TeamQueryResultErrorable;

  /**
   * A Clockwise user
   */
  user: IUser;

  /**
   * Check whether a given user shortname is available to be set
   */
  validateUserShortname: boolean;

  /**
   * Checks that a verification code is valid and which external client it will connect
   */
  verificationCodeResponse: IVerificationCodeResponse;
}

export interface IGroupQueryResultOnViewerArguments {
  /**
   * Calendars to not search for
   * @default []
   */
  excludeCalendars?: Array<string>;

  /**
   * @default 5
   */
  limit?: number;
  orgRelayId: string;
  query: string;
}

export interface INonUserDefragEmailResponseOnViewerArguments {
  emailInstanceId: string;
}

export interface IOrgPersonQueryResultErrorableOnViewerArguments {
  /**
   * @default []
   */
  excludeCalendars?: Array<string>;

  /**
   * @default 5
   */
  limit?: number;
  orgRelayId: string;
  query: string;
}

export interface IPersonQueryResultErrorableOnViewerArguments {
  /**
   * @default []
   */
  excludeCalendars?: Array<string>;

  /**
   * @default 5
   */
  limit?: number;
  orgRelayId: string;
  query: string;
}

export interface IRoomQueryResultOnViewerArguments {
  excludeCalendars: Array<string>;

  /**
   * @default 5
   */
  limit?: number;
  orgRelayId?: string;
  query: string;
}

export interface ISchedulingLinkBookingOnViewerArguments {
  /**
   * Id of the booking.
   */
  bookingId: string;
}

export interface ISchedulingLinkDetailsOnViewerArguments {
  body?: IZonedDayTimeSlotRangeInput;

  /**
   * A user's configurable id, like a username, for their scheduling links. Unique among all Clockwise users.
   */
  linkName: string;
  slug: string;
}

export interface ITeamQueryResultErrorableOnViewerArguments {
  /**
   * @default false
   */
  excludeMyTeams?: boolean;

  /**
   * @default 5
   */
  limit?: number;
  orgRelayId: string;
  query: string;
}

export interface IValidateUserShortnameOnViewerArguments {
  shortname: string;
}

export type CalendarInsightsErrorable = ICalendarInsights | IGraphEntityError;

/**
 * Insights about the fetched calendar
 */
export interface ICalendarInsights {
  __typename: 'CalendarInsights';
  aggregateStats: Array<IAggregateCalendarStats>;
  topCollaborators: Array<string>;
  userStats: ICalendarFocusTimeStats;
}

/**
 * Aggregate Calendar Stats
 */
export interface IAggregateCalendarStats {
  __typename: 'AggregateCalendarStats';
  distribution: Array<IFocusTimeMeetingDistribution>;

  /**
   * The ID of an object
   */
  id: string;
  job: string;
  role: string;
  stats: ICalendarFocusTimeStats;
}

/**
 * Focus Time Meeting Distribution
 */
export interface IFocusTimeMeetingDistribution {
  __typename: 'FocusTimeMeetingDistribution';
  focusTimeHours: number;

  /**
   * The ID of an object
   */
  id: string;
  meetingHours: number;
  percentage: number;
  weight: number;
}

/**
 * Calendar Focus Time Stats
 */
export interface ICalendarFocusTimeStats {
  __typename: 'CalendarFocusTimeStats';
  adHocMeetingMinutes: number;
  externalMeetingMinutes: number;
  focusMinutes: number;
  fragmentedMinutes: number;
  meetingMinutes: number;
  oneOnOneMeetingMinutes: number;
  teamSyncMeetingMinutes: number;
}

/**
 * An error wrapper
 */
export interface IGraphEntityError {
  __typename: 'GraphEntityError';
  body: string;
  message: string;
  statusCode: number;
}

export type ExternalGroupSearchResultErrorable =
  | IExternalGroupSearchResult
  | IGraphEntityError;

/**
 * Result of searching for external groups
 */
export interface IExternalGroupSearchResult {
  __typename: 'ExternalGroupSearchResult';
  groups: Array<IExternalGroupSearchMetadata>;
  inputQuery: string;
}

/**
 * Metadata about an external group
 */
export interface IExternalGroupSearchMetadata {
  __typename: 'ExternalGroupSearchMetadata';
  emailAddress: string;
  groupSize: number;
}

export type NonUserDefragEmailResponseErrorable =
  | IGraphEntityError
  | INonUserDefragEmailResponse;

/**
 * Response to non-user defrag email survey
 */
export interface INonUserDefragEmailResponse {
  __typename: 'NonUserDefragEmailResponse';
  additionalInformation: string;

  /**
   * The ID of an object
   */
  id: string;
  responseType: NonUserDefragEmailResponseType;
}

export enum NonUserDefragEmailResponseType {
  No = 'No',
  NotSure = 'NotSure',
  Yes = 'Yes',
}

export type OrgPersonQueryResultErrorable =
  | IGraphEntityError
  | IOrgPersonQueryResult;

/**
 * Search results for org persons
 */
export interface IOrgPersonQueryResult {
  __typename: 'OrgPersonQueryResult';

  /**
   * Persons that match the query
   */
  persons: Array<IOrgPerson>;

  /**
   * The input query string
   */
  query: string;
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IOrgPerson {
  __typename: 'OrgPerson';
  calendarAliases: Array<string>;
  eligibleForTeams: boolean;

  /**
   * The ID of an object
   */
  id: string;
  isYou: boolean;
  personId: string;
  primaryBillingGroupIdErrorable: PrimaryBillingGroupIdErrorable;
  primaryCalendarId: string;
  profile: IUserProfile;
  redirectPersonId: string;
  signUpMillis: any;
  userId: string;
}

export type PrimaryBillingGroupIdErrorable =
  | IGraphEntityError
  | IPrimaryBillingGroupId;

export interface IPrimaryBillingGroupId {
  __typename: 'PrimaryBillingGroupId';
  primaryBillingGroupId: string;
}

/**
 * A User Profile includes just the basic information about a Calendar User (not necessarily a Clockwise user)
 */
export interface IUserProfile {
  __typename: 'UserProfile';

  /**
   * The URL for a User's profile image
   */
  externalImageUrl: string;

  /**
   * A user's last name
   */
  familyName: string;

  /**
   * A user's first name
   */
  givenName: string;
}

export type PersonQueryResultErrorable = IGraphEntityError | IPersonQueryResult;

/**
 * Search results for persons
 */
export interface IPersonQueryResult {
  __typename: 'PersonQueryResult';

  /**
   * Persons that match the query
   */
  persons: Array<IPerson>;

  /**
   * The input query string
   */
  query: string;
}

/**
 * A person
 */
export interface IPerson {
  __typename: 'Person';

  /**
   * The emails of the person
   */
  emails: Array<string>;

  /**
   * The ID of an object
   */
  id: string;
  isOutsideOrg: boolean;
  isUser: boolean;
  isYou: boolean;

  /**
   * The primary email of the person
   */
  primaryEmail: string;

  /**
   * The profile of the person, may be null
   */
  profile: IUserProfile;
  userId: string;
}

export type ExternalRoomSearchResultErrorable =
  | IExternalRoomSearchResult
  | IGraphEntityError;

/**
 * Result of searching for a room by name
 */
export interface IExternalRoomSearchResult {
  __typename: 'ExternalRoomSearchResult';

  /**
   * Whether the result of the query is cacheable (e.g. was there an error)
   */
  cacheable: boolean;
  rooms: Array<IRoom>;
  searchQuery: string;
}

/**
 * A room
 */
export interface IRoom {
  __typename: 'Room';
  estimatedCapacity: number;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * @deprecated "Not populated, always null"
   */
  locationId: string;
  name: string;
  orgId: string;
  reportedCapacity: number;
  roomId: string;
}

export type PublicBookingErrorable = IGraphEntityError | IPublicBooking;

/**
 * Detailed of a scheduling link booking
 */
export interface IPublicBooking {
  __typename: 'PublicBooking';
  attendeeEmail: string;
  description: string;
  endTime: string;
  id: string;
  locationType: BookingLocationType;
  meetingName: string;
  ownerEmail: string;
  ownerProfile: IUserProfile;

  /**
   * Details of the scheduling link this booking was created with
   */
  schedulingLinkDetails: ISchedulingLinkDetails;
  startTime: string;
}

export interface ISchedulingLinkDetailsOnPublicBookingArguments {
  /**
   * End date for link times. An ISO date in the YYYY-MM-DD format (e.g. 2022-04-21)
   */
  endDate: string;

  /**
   * Starting date for link times. An ISO date in the YYYY-MM-DD format (e.g. 2022-04-21)
   */
  startDate: string;

  /**
   * A timezone in the standard format, e.g. ('America/Los_Angeles')
   */
  timeZone: string;
}

export enum BookingLocationType {
  InDescription = 'InDescription',
  Meet = 'Meet',
  Zoom = 'Zoom',
}

/**
 * Meeting title, owner name, duration, other details, and suggested times of a scheduling link.
 */
export interface ISchedulingLinkDetails {
  __typename: 'SchedulingLinkDetails';

  /**
   * The user shortname for the owner of this Scheduling Link
   */
  linkName: string;

  /**
   * The user profile of the meeting owner
   */
  ownerProfile: IUserProfile;
  publicMeetingSettings: IPublicMeetingSettings;

  /**
   * The slug for this SchedulingLink, unique for the given user linkName
   */
  slug: string;
  suggestedSlotsByDate: Array<ISuggestedSlotsByDate>;
}

/**
 * Public settings of a scheduling link for use by the scheduling link page.
 */
export interface IPublicMeetingSettings {
  __typename: 'PublicMeetingSettings';
  bookingTimeBufferHours: number;
  description: string;
  durationMinutes: number;
  endOfAvailability: IEventTime;
  highlightBestTimes: boolean;
  id: string;
  locationType: PublicLinkLocationType;
  name: string;
}

/**
 * A rich description of a time
 */
export interface IEventTime {
  __typename: 'EventTime';

  /**
   * String representation of the date or datetime
   */
  dateOrDateTime: string;

  /**
   * Time represented in milliseconds (DateTime is )
   */
  millis: number;

  /**
   * Timezone offset in minutes
   */
  offsetMinutes: number;

  /**
   * Standard IANA timezone string
   */
  timeZone: string;

  /**
   * The type of date
   */
  type: EventTimeType;
}

export enum EventTimeType {
  Date = 'Date',
  DateTime = 'DateTime',
}

export enum PublicLinkLocationType {
  InDescription = 'InDescription',
  Meet = 'Meet',
  Zoom = 'Zoom',
}

export interface ISuggestedSlotsByDate {
  __typename: 'SuggestedSlotsByDate';
  date: string;
  suggestedSlots: Array<ISuggestedSlot>;
}

/**
 * Start and end time of a suggested slot.
 */
export interface ISuggestedSlot {
  __typename: 'SuggestedSlot';
  tag: Tag;
  timeRange: ITimeRangeNonNull;
}

export enum Tag {
  Best = 'Best',
  Inconvenient = 'Inconvenient',
}

/**
 * A time range with non null times
 */
export interface ITimeRangeNonNull {
  __typename: 'TimeRangeNonNull';

  /**
   * The end time in the range, non null
   */
  endTime: IEventTime;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The start time in the range, non null
   */
  startTime: IEventTime;
}

export interface IZonedDayTimeSlotRangeInput {
  dayTimeSlotRange: IDayTimeSlotRangeInput;
  timeZone?: string;
}

export interface IDayTimeSlotRangeInput {
  maxSlot: IDayTimeSlotInput;
  minSlot: IDayTimeSlotInput;
}

export interface IDayTimeSlotInput {
  date: string;
  timeSlot: TimeSlot;
}

/**
 * A time slot
 */

export enum TimeSlot {
  T_00_00 = 'T_00_00',
  T_00_30 = 'T_00_30',
  T_01_00 = 'T_01_00',
  T_01_30 = 'T_01_30',
  T_02_00 = 'T_02_00',
  T_02_30 = 'T_02_30',
  T_03_00 = 'T_03_00',
  T_03_30 = 'T_03_30',
  T_04_00 = 'T_04_00',
  T_04_30 = 'T_04_30',
  T_05_00 = 'T_05_00',
  T_05_30 = 'T_05_30',
  T_06_00 = 'T_06_00',
  T_06_30 = 'T_06_30',
  T_07_00 = 'T_07_00',
  T_07_30 = 'T_07_30',
  T_08_00 = 'T_08_00',
  T_08_30 = 'T_08_30',
  T_09_00 = 'T_09_00',
  T_09_30 = 'T_09_30',
  T_10_00 = 'T_10_00',
  T_10_30 = 'T_10_30',
  T_11_00 = 'T_11_00',
  T_11_30 = 'T_11_30',
  T_12_00 = 'T_12_00',
  T_12_30 = 'T_12_30',
  T_13_00 = 'T_13_00',
  T_13_30 = 'T_13_30',
  T_14_00 = 'T_14_00',
  T_14_30 = 'T_14_30',
  T_15_00 = 'T_15_00',
  T_15_30 = 'T_15_30',
  T_16_00 = 'T_16_00',
  T_16_30 = 'T_16_30',
  T_17_00 = 'T_17_00',
  T_17_30 = 'T_17_30',
  T_18_00 = 'T_18_00',
  T_18_30 = 'T_18_30',
  T_19_00 = 'T_19_00',
  T_19_30 = 'T_19_30',
  T_20_00 = 'T_20_00',
  T_20_30 = 'T_20_30',
  T_21_00 = 'T_21_00',
  T_21_30 = 'T_21_30',
  T_22_00 = 'T_22_00',
  T_22_30 = 'T_22_30',
  T_23_00 = 'T_23_00',
  T_23_30 = 'T_23_30',
}

export type SchedulingLinkDetailsErrorable =
  | IGraphEntityError
  | ISchedulingLinkDetails;

export interface ISudo {
  __typename: 'Sudo';

  /**
   * Org domains that are explicitly removed from the whitelist.
   */
  blacklistedDomains: Array<string>;

  /**
   * Last update times for the chrome app
   */
  chromeAppUpdateTimes: IChromeAppUpdateTimes;

  /**
   * All emails for all users
   */
  claimedEmails: Array<string>;

  /**
   * The ID of an object
   */
  id: string;
  listCliCommands: CliCommandListErrorable;
  modifyUser: IUserAndExtendedInfo;
  resetFlowState: IFlowState;
  waitlistEnabled: boolean;
  waitlistUsers: Array<IWaitlistedUser>;

  /**
   * An array of whitelisted domains that get you a preview of the app.
   */
  whitelistedDomains: Array<string>;

  /**
   * An array of explicitly whitelisted users.
   */
  whitelistedUsers: Array<string>;
}

export interface IResetFlowStateOnSudoArguments {
  /**
   * @default ""
   */
  email?: string;
}

export interface IChromeAppUpdateTimes {
  __typename: 'ChromeAppUpdateTimes';

  /**
   * Last update of the chrome app with immediate priority
   */
  forceTime: any;

  /**
   * Last update of the chrome app with idle priority
   */
  idleTime: any;
}

export type CliCommandListErrorable = ICliCommandList | IGraphEntityError;

export interface ICliCommandList {
  __typename: 'CliCommandList';
  list: Array<ICliCommand>;
}

/**
 * The schema for a CLI command
 */
export interface ICliCommand {
  __typename: 'CliCommand';

  /**
   * The ID of an object
   */
  id: string;
  name: string;
  options: Array<ICliOption>;
}

/**
 * The schema for an option of a CLI command
 */
export interface ICliOption {
  __typename: 'CliOption';
  description: string;
  name: string;
  type: CliOptionType;
}

export enum CliOptionType {
  Boolean = 'Boolean',
  String = 'String',
}

export interface IUserAndExtendedInfo {
  __typename: 'UserAndExtendedInfo';

  /**
   * User's feature grid
   */
  featureStatesJson: string;

  /**
   * Org's current accounting period
   */
  orgAccountingPeriod: OrgAccountingPeriodErrorable;

  /**
   * User's subscriptions
   */
  subscriptions: PaymentSubscriptionListErrorable;

  /**
   * User
   */
  user: IUser;

  /**
   * User's teams
   */
  userTeams: TeamListErrorable;
}

export type OrgAccountingPeriodErrorable =
  | IGraphEntityError
  | IOrgAccountingPeriod;

/**
 * Freemium Org accounting period info
 */
export interface IOrgAccountingPeriod {
  __typename: 'OrgAccountingPeriod';
  enforcementStarted: boolean;
  enforcementState: EnforcementStateEnum;
  periodRange: IDateRange;
  quotaMax: number;
  quotaUsed: number;
}

export enum EnforcementStateEnum {
  ApproachingLimit = 'ApproachingLimit',
  NoEnforcement = 'NoEnforcement',
  ReachedLimit = 'ReachedLimit',
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IDateRange {
  __typename: 'DateRange';
  maxDate: string;
  minDate: string;
}

export type PaymentSubscriptionListErrorable =
  | IGraphEntityError
  | IPaymentSubscriptionList;

export interface IPaymentSubscriptionList {
  __typename: 'PaymentSubscriptionList';
  list: Array<IPaymentSubscription>;
}

/**
 * A product subscription under an org for a particular subjectt
 */
export interface IPaymentSubscription {
  __typename: 'PaymentSubscription';
  endMillis: number;

  /**
   * The ID of an object
   */
  id: string;
  productType: ProductTypeEnum;
  seatCount: number;
  startMillis: number;
  subjectUrn: IUrn;
  type: SubscriptionTypeEnum;
}

export enum ProductTypeEnum {
  Business_NonStandard = 'Business_NonStandard',
  Business_Standard = 'Business_Standard',
  Enterprise_NonStandard = 'Enterprise_NonStandard',
  Enterprise_Standard = 'Enterprise_Standard',
  Pro_NonStandard = 'Pro_NonStandard',
  Pro_Standard = 'Pro_Standard',
}

/**
 * A Uniform Resource Name
 */
export interface IUrn {
  __typename: 'Urn';

  /**
   * The ID of an object
   */
  id: string;
  scope: IUrn;
  type: UrnType;
  value: string;
}

export enum UrnType {
  Date = 'Date',
  Email = 'Email',
  Event = 'Event',
  FlowState = 'FlowState',
  Guest = 'Guest',
  Org = 'Org',
  Phone = 'Phone',
  Services = 'Services',
  Setting = 'Setting',
  Team = 'Team',
  Teammate = 'Teammate',
  TimeZone = 'TimeZone',
  User = 'User',
  WorkingGroup = 'WorkingGroup',
  WorkingHours = 'WorkingHours',
  WorkOsOrg = 'WorkOsOrg',
}

export enum SubscriptionTypeEnum {
  Expired = 'Expired',
  FreemiumOrg = 'FreemiumOrg',
  LegacyPrePaywall = 'LegacyPrePaywall',
  PaidBillingGroup = 'PaidBillingGroup',
  PaidOrgPro = 'PaidOrgPro',
  PaidTeamPro = 'PaidTeamPro',
  PaidUserPro = 'PaidUserPro',
  SelfServePremiumOrgQ42020 = 'SelfServePremiumOrgQ42020',
  SelfServePremiumTeamQ12021 = 'SelfServePremiumTeamQ12021',
}

/**
 * A person who uses our app
 */
export interface IUser {
  __typename: 'User';

  /**
   * The user's bucket o' booleans for the chrome extension
   */
  chromeExtensionFlagsErrorable: FlagsErrorable;
  createdTime: any;

  /**
   * A user's collection of data sources
   */
  dataSources: IDataSourceConnection;
  emails: Array<string>;

  /**
   * The profile image of the user
   */
  externalImageUrl: string;

  /**
   * The last name of the user
   */
  familyName: string;

  /**
   * The collection of flow states for the user
   */
  flowStates: IFlowStateConnection;

  /**
   * The first name of the user
   */
  givenName: string;

  /**
   * The ID of an object
   */
  id: string;
  jwtIssuedTime: any;

  /**
   * A user's configurable id, like a username, for their scheduling links. Unique among all Clockwise users.
   */
  linkNameErrorable: LinkNameErrorable;

  /**
   * Settings of a given scheduling link for consumption by the owner of the link
   */
  meetingSettingsErrorable: MeetingSettingsErrorable;

  /**
   * A list of primary email/org urns for this user
   */
  members: Array<IUrn>;

  /**
   * The collection of orgs the user belongs to
   */
  orgs: IOrgConnection;
  schedulingLinks: Array<ISchedulingLink>;

  /**
   * The user's bucket o' booleans for the services
   */
  servicesFlagsErrorable: FlagsErrorable;

  /**
   * Status of the user
   */
  status: UserStatusEnum;

  /**
   * The user's bucket o' booleans for the web app
   */
  webAppFlagsErrorable: FlagsErrorable;
}

export interface IDataSourcesOnUserArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;

  /**
   * @default "all"
   */
  name?: string;
}

export interface IFlowStatesOnUserArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface ILinkNameErrorableOnUserArguments {
  after?: string;
  before?: string;

  /**
   * Whether to create a new linkName if none exists.
   */
  createLinkNameIfNone?: boolean;
  first?: number;
  last?: number;
}

export interface IMeetingSettingsErrorableOnUserArguments {
  slug: string;
}

export interface IOrgsOnUserArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
  orgRelayId?: string;
}

export interface ISchedulingLinksOnUserArguments {
  after?: string;
  before?: string;

  /**
   * Whether to create a new linkName if none exists before fetching the links.
   */
  createLinkNameIfNone?: boolean;
  first?: number;
  last?: number;
}

export type FlagsErrorable = IFlags | IGraphEntityError;

/**
 * bucket o' booleans
 */
export interface IFlags {
  __typename: 'Flags';
  flags: Array<string>;

  /**
   * The ID of an object
   */
  id: string;
}

/**
 * A connection to a list of items.
 */
export interface IDataSourceConnection {
  __typename: 'DataSourceConnection';

  /**
   * A list of edges.
   */
  edges: Array<IDataSourceEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IDataSourceEdge {
  __typename: 'DataSourceEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IDataSource;
}

/**
 * A data source
 */
export interface IDataSource {
  __typename: 'DataSource';

  /**
   * List of calendarIds associated with this datasource
   */
  calendarIds: Array<string>;

  /**
   * The email associated with a data source
   */
  email: string;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The name of the data source
   */
  name: string;

  /**
   * The provider of the data source
   * @deprecated "use type enum instead"
   */
  provider: number;
  state: DataSourceStatus;

  /**
   * The status of the data source
   * @deprecated "use state enum instead"
   */
  status: number;

  /**
   * other calendars this data-source is syncing to, if any
   */
  syncTargets: Array<ICrawlTarget>;
  type: DataSourceProvider;

  /**
   * The username associated with a data source
   */
  username: string;
}

export enum DataSourceStatus {
  Okay = 'Okay',
  RequiresReAuth = 'RequiresReAuth',
}

/**
 * a calendar that another calendar's events are synced to
 */
export interface ICrawlTarget {
  __typename: 'CrawlTarget';

  /**
   * The target calendarId
   */
  calendarId: string;

  /**
   * The target emailAddress
   */
  emailAddress: string;
}

export enum DataSourceProvider {
  Google = 'Google',
  Office365 = 'Office365',
}

/**
 * Information about pagination in a connection.
 */
export interface IPageInfo {
  __typename: 'PageInfo';

  /**
   * When paginating forwards, the cursor to continue.
   */
  endCursor: string;

  /**
   * When paginating forwards, are there more items?
   */
  hasNextPage: boolean;

  /**
   * When paginating backwards, are there more items?
   */
  hasPreviousPage: boolean;

  /**
   * When paginating backwards, the cursor to continue.
   */
  startCursor: string;
}

/**
 * A connection to a list of items.
 */
export interface IFlowStateConnection {
  __typename: 'FlowStateConnection';

  /**
   * A list of edges.
   */
  edges: Array<IFlowStateEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IFlowStateEdge {
  __typename: 'FlowStateEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IFlowState;
}

/**
 * A state for a flow defined by a flowKey for a given user/org
 */
export interface IFlowState {
  __typename: 'FlowState';

  /**
   * The current status of the flow
   */
  current: IFlowStatus;

  /**
   * The key for looking up the state
   */
  flowKey: string;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The pending/next status of the flow
   */
  pending: IFlowStatus;

  /**
   * The previous state of the flow
   */
  previousState: string;
}

/**
 * A state for a flow defined by a flowKey for a given user/org
 */
export interface IFlowStatus {
  __typename: 'FlowStatus';

  /**
   * Any associated error with the current state
   */
  errorMessage: string;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * A percentage of completion for the state
   */
  percentComplete: number;

  /**
   * The current state of flow
   */
  state: string;
}

export type LinkNameErrorable = IGraphEntityError | ILinkName;

export interface ILinkName {
  __typename: 'LinkName';

  /**
   * A user's configurable id, like a username, for their scheduling links. Unique among all Clockwise users.
   */
  linkName: string;
}

export type MeetingSettingsErrorable = IGraphEntityError | IMeetingSettings;

/**
 * A scheduling link settings
 */
export interface IMeetingSettings {
  __typename: 'MeetingSettings';
  allowFifteenMinuteMarkBooking: boolean;
  availabilityType: AvailabilityType;
  bookableHours: BookableHoursEnum;
  bookingTimeBufferHours: number;
  bookOnNoMeetingDay: boolean;
  description: string;
  highlightBestTimes: boolean;
  id: string;
  isActive: boolean;
  locationSettings: ILocationSettings;
  name: string;

  /**
   * Will be non-empty for an existing MeetingSetting, will be empty for a new MeetingSetting
   */
  slug: string;
  timingSettings: ITimingSettings;
}

export enum AvailabilityType {
  BalanceAvailabilityAndFocus = 'BalanceAvailabilityAndFocus',
  MaximizeAvailability = 'MaximizeAvailability',
  MaximizeFocusTime = 'MaximizeFocusTime',
}

export enum BookableHoursEnum {
  MeetingHours = 'MeetingHours',
  WorkingHours = 'WorkingHours',
}

/**
 * Location details of a scheduling link
 */
export interface ILocationSettings {
  __typename: 'LocationSettings';
  locationDetails: string;
  locationType: LinkLocationType;
}

export enum LinkLocationType {
  InDescription = 'InDescription',
  Meet = 'Meet',
  Zoom = 'Zoom',
}

export interface ITimingSettings {
  __typename: 'TimingSettings';
  durationMinutes: number;
  periodType: PeriodType;
  timeRange: ILinkTimeRange;
}

export enum PeriodType {
  Range = 'Range',
  RollingWindow = 'RollingWindow',
}

export interface ILinkTimeRange {
  __typename: 'LinkTimeRange';
  rangeEndTimeMillis: number;
  rangeStartTimeMillis: number;
  timeZone: string;
  windowSizeDays: number;
}

/**
 * A connection to a list of items.
 */
export interface IOrgConnection {
  __typename: 'OrgConnection';

  /**
   * A list of edges.
   */
  edges: Array<IOrgEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IOrgEdge {
  __typename: 'OrgEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IOrg;
}

/**
 * A collection of users with the same email domain.
 */
export interface IOrg {
  __typename: 'Org';

  /**
   * A list of all active people in org
   * @deprecated "Use currentOrgPersons"
   */
  activeOrgPersons: OrgPersonListErrorable;

  /**
   * Details of a users Asana account (never actual credentials) and related settings
   */
  asanaAccountErrorable: AsanaAccountErrorable;

  /**
   * Autopilot events for the user
   */
  autopilotEventsResponse: AutopilotEventsResponseErrorable;

  /**
   * Autopilot onboarding suggestions for the user
   */
  autopilotOnboardingSuggestionsResponse: AutopilotOnboardingSuggestionsResponseErrorable;

  /**
   * a billing group for given id
   */
  billingGroupForId: BillingGroupErrorable;

  /**
   * a list of billing groups for current org
   */
  billingGroups: BillingGroupListErrorable;

  /**
   * Calculates smart hold events without reserving them on a user calendar
   */
  calculateSmartHoldsErrorable: SmartHoldCalculationResultErrorable;

  /**
   * The calendar status for a given calendarId
   */
  calendarStatusErrorable: CalendarStatusErrorable;

  /**
   * Teammate time stats for a given time period
   */
  calendarTimeStats: ICalendarTimeStats;

  /**
   * Teammate time stats for a given time period and set of calendars
   */
  calendarTimeStatsList: CalendarTimeStatsListErrorable;

  /**
   * For a given user and event can an event be rescheduled
   * @deprecated "use canScheduleResponse"
   */
  canRescheduleEvent: boolean;

  /**
   * For a given user and event can an event be resheduled or put on autopilot
   */
  canScheduleResponse: CanScheduleResponseListErrorable;

  /**
   * Teammate time stats with other teammates for a given time period
   * @deprecated "In favor of new Errorable version"
   */
  collaboratorStats: ICollaboratorStats;

  /**
   * Errorable teammate time stats with other teammates for a given time period
   */
  collaboratorStatsErrorable: CollaboratorStatsErrorable;

  /**
   * Compute pain points on an event's currently scheduled slot
   */
  computeCurrentSlotImpactErrorable: CurrentScheduledSlotImpactErrorable;

  /**
   * Crawl status for calendarIds in this org
   */
  crawledCalendarInfo: ICrawledCalendarInfo;

  /**
   * Crawl status for given calendarIds
   */
  crawledCalendarInfoForCalendars: ICrawledCalendarInfo;
  crawlTest: OrgCrawlTest;
  createdTime: any;

  /**
   * A list of all `active` users in an org
   */
  currentOrgPersons: OrgPersonListErrorable;

  /**
   * The domains associated with this org
   */
  domains: Array<string>;

  /**
   * The user's event coloring settings for the org
   */
  eventColoringSettingsErrorable: EventColoringSettingsErrorable;

  /**
   * Event with a given id on the given calendar (unlike
   * forceFetchEventParentErrorable does not do a pull from Google for the latest info)
   */
  eventForCalendar: EventErrorable;

  /**
   * Event parents and event parent bases
   */
  eventParentBaseWrapperByDates: EventParentBaseWrapperErrorable;

  /**
   * Event parents and event parent bases by week
   * @deprecated "In favor of object version which is supported by both Apollo and Relay"
   */
  eventParentBaseWrapperByWeeks: EventParentBaseWrapperByWeekListErrorable;

  /**
   * Event parents and event parent bases by week
   */
  eventParentBaseWrapperByWeeksObject: EventParentBaseWrapperByWeekObjectListErrorable;

  /**
   * An org's collection of events from all queried calendars and dates
   */
  eventParentsByDates: IEventParentConnection;

  /**
   * An org's collection of events from all queried calendars and dates
   */
  eventParentWrappersByDatesErrorable: EventParentWrapperConnectionErrorable;

  /**
   * A list of all experiment strings enabled for this org
   */
  experiments: Array<string>;

  /**
   * External groups for emails
   */
  externalGroups: ExternalGroupListErrorable;
  featureGrid: FeatureGridErrorable;

  /**
   * The user's feed suggestion results
   */
  feedSuggestionsErrorable: FeedSuggestionResultsErrorable;

  /**
   * Fetch heatmap for set of calendar ids for time range
   */
  fetchHeatmapErrorable: HeatmapResultErrorable;

  /**
   * The collection of flow states for the user
   */
  flowStates: IFlowStateConnection;

  /**
   * The total number of hours saved across an org
   */
  focusTimeSavedInHours: number;

  /**
   * Focus Time stats for the user
   */
  focusTimeStats: FocusTimeStatsErrorable;

  /**
   * Force fetch an event from Google (does a pull from Google to ensure the event has latest info)
   */
  forceFetchEventParentErrorable: EventParentErrorable;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The meeting hours for the current org, a set of users (via userIds), or any set of non-users (via calendarIds)
   */
  meetingHoursQueryResult: IMeetingHoursQueryResult;

  /**
   * The name of the org
   */
  name: string;

  /**
   * The next defrag time for the current org
   */
  nextDefragTime: DefragTimeErrorable;

  /**
   * List of a user's one on ones
   */
  oneOnOnesForCalendar: EventListErrorable;

  /**
   * Optimistic savings for a user, used to determine how much time can be saved in a week
   */
  optimisticSavingsErrorable: OptimisticSavingsErrorable;

  /**
   * Optimistic savings for current user for next week
   */
  optimisticSavingsForCurrentUserNextWeekErrorable: OptimisticSavingsErrorable;
  orgAccountingPeriod: OrgAccountingPeriodErrorable;

  /**
   * The collection of flow states for the org
   */
  orgFlowStates: FlowStateListErrorable;

  /**
   * The default meeting hours for the current org
   */
  orgMeetingHours: IOrgMeetingHours;

  /**
   * Overview of organization-wide Clockwise usage
   */
  orgOverview: OrgOverviewErrorable;
  orgPersonForUserErrorable: OrgPersonErrorable;

  /**
   * Object defining a list of org persons and how to paginate the list
   */
  orgPersonListPaginatedErrorable: OrgPersonListPaginatedErrorable;

  /**
   * Pending org invites outstanding
   */
  pendingInvites: IOrgInviteConnection;

  /**
   * A collection of teammates, suggestions, invitees and you
   */
  personCorpusErrorable: PersonListErrorable;
  personForUserErrorable: PersonErrorable;

  /**
   * Look up persons by calendar id
   */
  personsByCalendarIdsErrorable: PersonListErrorable;

  /**
   * Look up persons by user id
   */
  personsByUserIdsErrorable: PersonListErrorable;

  /**
   * The users primary calendar in this org
   */
  primaryOrgCalendar: string;

  /**
   * The users email thats part of this org
   */
  primaryOrgEmail: string;

  /**
   * Primary team for user
   */
  primaryTeam: TeamErrorable;

  /**
   * A list of profile time zones
   */
  profileTimeZonesErrorable: ProfileTimeZoneListErrorable;

  /**
   * List of public Slack channels for the slack workspace for a given Clockwise team
   */
  publicChannelsForSlackForTeam: SlackChannelListErrorable;

  /**
   * A set of rescheduling suggestions for the given query
   */
  reschedulingResponseErrorable: SchedulingResponseErrorable;

  /**
   * A rewind history for a calendarId/week
   */
  rewindHistoryErrorable: RewindHistoryErrorable;

  /**
   * External rooms for the given ids
   */
  rooms: RoomListErrorable;

  /**
   * A set of scheduling suggestions for the given query
   */
  schedulingResponseErrorable: SchedulingResponseErrorable;

  /**
   * The collection of settings for this user in this org
   */
  settings: ISettingConnection;

  /**
   * The user's shadow calendar settings for the org
   */
  shadowCalendarSettingsErrorable: ShadowCalendarSettingsErrorable;

  /**
   * Slack Settings for a given team
   */
  slackAccountForTeamErrorable: SlackAccountErrorable;

  /**
   * The user's list of Slack Accounts
   */
  slackAccountsErrorable: SlackAccountListErrorable;

  /**
   * The user's list of Slack Account Settings
   */
  slackSettingsErrorable: SlackSettingsListErrorable;
  smartHoldEventInfo: SmartHoldEventInfoErrorable;

  /**
   * A link, which expires in 5 minutes, that can be used to configure sso for a billing group.
   */
  ssoAdminPortalLink: SsoAdminPortalLinkErrorable;

  /**
   * Suggestions for scheduling a high priority moving with defrag
   */
  suggestDefragForHighPriorityMeeting: DefragConflictResolutionErrorable;

  /**
   * Suggested invites to make for this org
   */
  suggestedInvites: ISuggestedOrgInviteConnection;

  /**
   * The suggested and selected room preferences
   */
  suggestedRoomPreferences: SuggestedRoomPreferencesErrorable;

  /**
   * A list of all suggested teams for user
   */
  suggestedTeams: SuggestedTeamListErrorable;

  /**
   * Working preferences for a teammate
   */
  summarizedWorkingPreferences: ISummarizedWorkingPreferences;

  /**
   * Event with a given id on the given calendar
   */
  syncedOriginEvent: EventErrorable;

  /**
   * Fetches (most recent) tailored defrag summary
   */
  tailoredDefragCardSummary: TailoredDefragCardSummaryErrorable;

  /**
   * Calendar statuses for everyone on the team
   */
  teamCalendarStatusErrorable: CalendarStatusListErrorable;

  /**
   * A team for org from given teamId
   */
  teamForTeamId: TeamErrorable;

  /**
   * Team health score for calendars for a given time period
   */
  teamHealthScoreForCalendarsErrorable: TeamHealthScoreErrorable;

  /**
   * Team health scorefor team for a given time period
   */
  teamHealthScoreForTeamErrorable: TeamHealthScoreErrorable;

  /**
   * Team health score for team for a given time period
   */
  teamHealthScoreForTeamWithSubTeamsErrorable: TeamHealthScoreWithSubTeamsErrorable;

  /**
   * Team health score just for user for a given time period
   */
  teamHealthScoreJustForUserErrorable: TeamHealthScoreErrorable;

  /**
   * Overall settings for a team
   */
  teamSettings: TeamSettingsErrorable;

  /**
   * A list of all teams for org
   */
  teamsForOrg: TeamListErrorable;

  /**
   * User's settings for a team
   */
  teamUserSettings: TeamUserSettingsErrorable;

  /**
   * The user's team
   */
  userDefinedTeam: IUserDefinedTeam;

  /**
   * The users's goals
   */
  userGoals: IGoalConnection;

  /**
   * The meeting hours for the current user
   */
  userMeetingHours: IUserMeetingHours;

  /**
   * The current user's profile
   */
  userPerson: PersonErrorable;

  /**
   * The user responses from the onboarding survey
   */
  userSurveyResponseErrorable: UserSurveyResponseListErrorable;

  /**
   * The user responses wrapped by ID for the onboarding survey
   */
  userSurveyResponseWrapperErrorable: UserSurveyResponseWrapperErrorable;

  /**
   * A list of all teams for user
   */
  userTeams: TeamListErrorable;

  /**
   * The working hours for the current user
   */
  userWorkingHours: IUserWorkingHours;

  /**
   * The user work address for this org
   */
  workAddressForUser: AddressErrorable;

  /**
   * Working groups for this user for this org
   */
  workingGroups: IWorkingGroupConnection;

  /**
   * The working hours for any given set of users
   */
  workingHoursQueryResult: IWorkingHoursQueryResult;

  /**
   * Connected account-level zoom account for org
   */
  zoomAccount: ZoomAccountErrorable;

  /**
   * Connected user-level zoom account for org
   */
  zoomUserLevelAccount: ZoomAccountErrorable;
}

export interface IAutopilotEventsResponseOnOrgArguments {
  timeZone: string;
}

export interface IBillingGroupForIdOnOrgArguments {
  billingGroupId: string;
}

export interface ICalculateSmartHoldsErrorableOnOrgArguments {
  week: any;
}

export interface ICalendarStatusErrorableOnOrgArguments {
  calendarId: string;
  timeZone: string;
}

export interface ICalendarTimeStatsOnOrgArguments {
  calendarId: string;

  /**
   * The maximum time slot of the time range
   */
  rangeMax: IDayTimeSlotInput;

  /**
   * The minimum time slot of the time range
   */
  rangeMin: IDayTimeSlotInput;
  timeZone: string;
}

export interface ICalendarTimeStatsListOnOrgArguments {
  calendarIds: Array<string>;
  dates: Array<any>;
  timeZone: string;
}

export interface ICanRescheduleEventOnOrgArguments {
  calendarIds?: Array<string>;
  eventParentRelayId?: string;
}

export interface ICanScheduleResponseOnOrgArguments {
  calendarIds?: Array<string>;
  externalEventIds: Array<string>;
}

export interface ICollaboratorStatsOnOrgArguments {
  calendarId: string;

  /**
   * The maximum time slot of the time range
   */
  rangeMax: IDayTimeSlotInput;

  /**
   * The minimum time slot of the time range
   */
  rangeMin: IDayTimeSlotInput;
  timeZone: string;
}

export interface ICollaboratorStatsErrorableOnOrgArguments {
  calendarId: string;

  /**
   * The maximum time slot of the time range
   */
  rangeMax: IDayTimeSlotInput;

  /**
   * The minimum time slot of the time range
   */
  rangeMin: IDayTimeSlotInput;
  timeZone: string;
}

export interface IComputeCurrentSlotImpactErrorableOnOrgArguments {
  /**
   * The external event id
   */
  externalEventId: string;
}

export interface ICrawledCalendarInfoForCalendarsOnOrgArguments {
  calendarIds: Array<string>;
}

export interface IEventForCalendarOnOrgArguments {
  calendarId: string;
  externalEventId: string;
}

export interface IEventParentBaseWrapperByDatesOnOrgArguments {
  calendarIds: Array<string>;
  dates: Array<any>;
  includeAllDay?: boolean;
  timeZone: string;
}

export interface IEventParentBaseWrapperByWeeksOnOrgArguments {
  calendarIds: Array<string>;
  includeAllDay?: boolean;
  timeZone: string;
  weekStartDates: Array<any>;
}

export interface IEventParentBaseWrapperByWeeksObjectOnOrgArguments {
  calendarIds: Array<string>;
  includeAllDay?: boolean;
  timeZone: string;
  weekStartDates: Array<any>;
}

export interface IEventParentsByDatesOnOrgArguments {
  after?: string;
  before?: string;
  calendarIds: Array<string>;
  dates: Array<any>;
  first?: number;
  last?: number;
  timeZone: string;
}

export interface IEventParentWrappersByDatesErrorableOnOrgArguments {
  after?: string;
  before?: string;
  calendarIds: Array<string>;
  dates: Array<any>;
  first?: number;
  includeAllDay?: boolean;
  last?: number;
  timeZone: string;
}

export interface IExternalGroupsOnOrgArguments {
  emails: Array<string>;
}

export interface IFeedSuggestionsErrorableOnOrgArguments {
  /**
   * For debugging only. Returns feed suggestions as though you're on a date as specified in millis.
   */
  debugFetchDate?: any;

  /**
   * A list of relay suggestion Ids of those currently being seen by the user (ordering matters)
   */
  suggestionRelayIds?: string;
}

export interface IFetchHeatmapErrorableOnOrgArguments {
  calendarIds: Array<string>;
  dateRange: IDateRangeInput;
  timeZone: string;
}

export interface IFlowStatesOnOrgArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface IForceFetchEventParentErrorableOnOrgArguments {
  calendarId: string;
  externalEventId: string;
}

export interface IMeetingHoursQueryResultOnOrgArguments {
  nonUserCalendarIds?: Array<string>;

  /**
   * @default true
   */
  resolveOverrides?: boolean;
  userIds?: Array<string>;
}

export interface IOptimisticSavingsErrorableOnOrgArguments {
  calendarId: string;

  /**
   * The LocalDate representation of the start of a week (Sunday)
   */
  week: any;
}

export interface IOrgFlowStatesOnOrgArguments {
  flowKeys: Array<string>;
}

export interface IOrgPersonListPaginatedErrorableOnOrgArguments {
  activityFilter?: ActivityFilter;
  billingGroupId?: string;
  excludeNonUsers?: boolean;
  fetchNext?: boolean;
  freePersonsOnly?: boolean;
  limit?: number;

  /**
   * pageToken as first personId in the current page along with fetchNext false
   * fetches previous page, pageToken as last personId in the current page along
   * with fetchNext true fetches next page.
   */
  pageToken?: string;
  primaryBillingGroupOnly?: boolean;
  searchQuery?: string;
  sortDirection: SortDirection;
  sortType: SortType;
  teamId?: string;
}

export interface IPendingInvitesOnOrgArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface IPersonsByCalendarIdsErrorableOnOrgArguments {
  calendarIds: Array<string>;

  /**
   * @default 50
   */
  limit?: number;
}

export interface IPersonsByUserIdsErrorableOnOrgArguments {
  /**
   * @default 50
   */
  limit?: number;
  userIds: Array<string>;
}

export interface IProfileTimeZonesErrorableOnOrgArguments {
  calendarIds: Array<string>;
}

export interface IPublicChannelsForSlackForTeamOnOrgArguments {
  teamId: string;
}

export interface IReschedulingResponseErrorableOnOrgArguments {
  /**
   * The external event id
   */
  externalEventId: string;

  /**
   * The maximum time slot to schedule in
   */
  rangeMax: IDayTimeSlotInput;

  /**
   * The minimum time slot to schedule in
   */
  rangeMin: IDayTimeSlotInput;
  timeZone: string;
}

export interface IRewindHistoryErrorableOnOrgArguments {
  calendarId: string;
  week: string;
}

export interface IRoomsOnOrgArguments {
  roomIds: Array<string>;
}

export interface ISchedulingResponseErrorableOnOrgArguments {
  query?: ISchedulingQueryInput;
}

export interface ISettingsOnOrgArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface ISlackAccountForTeamErrorableOnOrgArguments {
  teamId: string;
}

export interface ISmartHoldEventInfoOnOrgArguments {
  externalEventId: string;
}

export interface ISsoAdminPortalLinkOnOrgArguments {
  /**
   * BillingGroup id to configure
   */
  bgId: string;

  /**
   * One of Dsync (default, used for billing) or Sso (used for an extra login screen, requires Dsync to work)
   */
  intent?: SsoIntent;
}

export interface ISuggestDefragForHighPriorityMeetingOnOrgArguments {
  attendees: Array<string>;
  duration: number;
  externalEventId?: string;
  slot: IDayTimeSlotInput;
  timeZone: string;
}

export interface ISuggestedInvitesOnOrgArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface ISummarizedWorkingPreferencesOnOrgArguments {
  calendarId: string;
}

export interface ISyncedOriginEventOnOrgArguments {
  externalEventId: string;
  startTime: IEventTimeInput;
}

export interface ITailoredDefragCardSummaryOnOrgArguments {
  newerThan?: any;
}

export interface ITeamCalendarStatusErrorableOnOrgArguments {
  timeZone: string;
}

export interface ITeamForTeamIdOnOrgArguments {
  teamId: string;
}

export interface ITeamHealthScoreForCalendarsErrorableOnOrgArguments {
  calendarIds: Array<string>;

  /**
   * The maximum time slot of the time range
   */
  rangeMax: IDayTimeSlotInput;

  /**
   * The minimum time slot of the time range
   */
  rangeMin: IDayTimeSlotInput;
  timeZone: string;
}

export interface ITeamHealthScoreForTeamErrorableOnOrgArguments {
  /**
   * The maximum time slot of the time range
   */
  rangeMax: IDayTimeSlotInput;

  /**
   * The minimum time slot of the time range
   */
  rangeMin: IDayTimeSlotInput;
  teamId: string;
  timeZone: string;
}

export interface ITeamHealthScoreForTeamWithSubTeamsErrorableOnOrgArguments {
  /**
   * The maximum time slot of the time range
   */
  rangeMax: IDayTimeSlotInput;

  /**
   * The minimum time slot of the time range
   */
  rangeMin: IDayTimeSlotInput;
  teamId: string;
  timeZone: string;
}

export interface ITeamHealthScoreJustForUserErrorableOnOrgArguments {
  /**
   * The maximum time slot of the time range
   */
  rangeMax: IDayTimeSlotInput;

  /**
   * The minimum time slot of the time range
   */
  rangeMin: IDayTimeSlotInput;
  timeZone: string;
}

export interface ITeamSettingsOnOrgArguments {
  teamId: string;
}

export interface ITeamUserSettingsOnOrgArguments {
  teamId: string;
}

export interface IUserGoalsOnOrgArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface IUserMeetingHoursOnOrgArguments {
  /**
   * @default true
   */
  resolveOverrides?: boolean;
}

export interface IUserSurveyResponseErrorableOnOrgArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface IUserSurveyResponseWrapperErrorableOnOrgArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface IUserWorkingHoursOnOrgArguments {
  /**
   * @default true
   */
  resolveOverrides?: boolean;
}

export interface IWorkingGroupsOnOrgArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface IWorkingHoursQueryResultOnOrgArguments {
  nonUserCalendarIds?: Array<string>;

  /**
   * @default true
   */
  resolveOverrides?: boolean;
  userIds?: Array<string>;
}

export type OrgPersonListErrorable = IGraphEntityError | IOrgPersonList;

export interface IOrgPersonList {
  __typename: 'OrgPersonList';
  list: Array<IOrgPerson>;
}

export type AsanaAccountErrorable = IAsanaAccount | IGraphEntityError;

/**
 * Asana Account Details
 */
export interface IAsanaAccount {
  __typename: 'AsanaAccount';

  /**
   * boolean describing if the user has Asana oauth credentials stored.
   */
  hasAsanaCredentials: boolean;

  /**
   * The ID of an object
   */
  id: string;
}

export type AutopilotEventsResponseErrorable =
  | IAutopilotEventsResponse
  | IGraphEntityError;

/**
 * A list of autopilot events that are enabled or suggested
 */
export interface IAutopilotEventsResponse {
  __typename: 'AutopilotEventsResponse';
  enabledAttending: Array<IEvent>;
  enabledOrganized: Array<IEvent>;
  suggestedAttending: Array<IEvent>;
  suggestedOrganized: Array<IEvent>;
}

/**
 * An event on a calendar
 */
export interface IEvent {
  __typename: 'Event';

  /**
   * The annotations for this event
   */
  annotatedEvent: IAnnotatedEvent;

  /**
   * The annotations for the recurring version of this event
   * @deprecated "Use annotatedEvent"
   */
  annotatedRecurringEvent: IAnnotatedEvent;
  attendeePeople: PersonListErrorable;

  /**
   * The attendees that are associated with this event
   */
  attendees: Array<IAttendee>;

  /**
   * For autopilot candidates, the status of the current autopilot
   */
  autopilotEventStatus: AutopilotEventStatusErrorable;

  /**
   * A list of calendar ids that the event is associated with
   */
  calendarIds: Array<string>;

  /**
   * When the event was created, in millis
   */
  createdMillis: any;

  /**
   * Attendee that created the event
   */
  creator: IAttendee;

  /**
   * A description of the event
   */
  description: string;

  /**
   * Indicates the description has been omitted
   */
  descriptionOmitted: boolean;

  /**
   * EventTime object representing the end time of the event
   */
  endTime: IEventTime;

  /**
   * Not sure what this is...
   */
  etag: string;

  /**
   * A calendar event's autopilot history
   */
  eventAutopilotHistory: EventAutopilotHistoryErrorable;

  /**
   * Contains all flex permutations of the event flexibility string
   * @deprecated "Use EventFlexibility.description instead"
   */
  eventFlexibilityErrorable: EventFlexibilityNewListErrorable;

  /**
   * The unique event key for this event
   */
  eventKey: IEventKey;
  eventParentBaseId: string;
  eventParentBaseTimestamp: any;

  /**
   * The id of the event parent that contains this event as a version
   */
  eventParentRelayId: string;

  /**
   * The feature grid feature states for the organizer of this event
   */
  featureStatesForOrganizer: FeatureStatesErrorable;

  /**
   * Indicates that attendees are allowed to invite others
   */
  guestsCanInviteOthers: boolean;

  /**
   * Indicates that attendees can modify the event
   */
  guestsCanModify: boolean;

  /**
   * Indicates that attendees can see other attendees
   */
  guestsCanSeeOtherGuests: boolean;

  /**
   * A Google Hangout link for the event
   */
  hangoutLink: string;

  /**
   * A link associated with the event
   */
  htmlLink: string;

  /**
   * Indicates that this is an all-day event
   */
  isAllDay: boolean;

  /**
   * Is this a Clockwise generated event (eg. Smart Hold)
   */
  isClockwiseEvent: boolean;

  /**
   * The location of the event
   */
  location: string;

  /**
   * Indicates that this event is locked
   */
  locked: boolean;
  moveRangeInLocalTimeV3: MoveRangeInLocalTimeV3Errorable;

  /**
   * The attendee who organized the event
   */
  organizer: IAttendee;

  /**
   * Additionally resolve the OrgPerson for the organizer
   */
  organizerPerson: PersonErrorable;

  /**
   * Whether or not this event is paused
   */
  paused: boolean;

  /**
   * Indicates that this is a private copy...?
   */
  privateCopy: boolean;

  /**
   * The recurrence rules for the event
   */
  recurrenceRules: IRecurrenceRules;

  /**
   * The recurring id for the event, if it recurs.
   */
  recurringEventId: string;
  scrubbedEvent: boolean;

  /**
   * EventTime object representing the start time of the event
   */
  startTime: IEventTime;
  suggestedAutopilotSettingsResponseV3: SuggestedAutopilotSettingsResponseV3Errorable;

  /**
   * Description of the event time & recurrence, ignoring flexibility. Null if unable to compute
   */
  timeDescription: string;

  /**
   * The name of the event
   */
  title: string;

  /**
   * Can this time be scheduled over? (None = 0; Opaque = 1; Transparent = 2)
   */
  transparency: TransparencyEnum;

  /**
   * When the event was updated, in millis
   */
  updatedMillis: any;
}

export interface IAutopilotEventStatusOnEventArguments {
  calendarId?: string;
}

/**
 * An event annotated with user and org tags
 */
export interface IAnnotatedEvent {
  __typename: 'AnnotatedEvent';
  externalEventId: string;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The org tags for this event
   */
  orgTags: Array<IOrgTag>;

  /**
   * The user tags for this event
   */
  userTags: Array<IUserTag>;
}

/**
 * A per org tag on an event
 */
export interface IOrgTag {
  __typename: 'OrgTag';

  /**
   * The TagState for this tag
   */
  state: ITagState;

  /**
   * The tag name
   */
  tag: string;
}

/**
 * The state of a tag on an event
 */
export interface ITagState {
  __typename: 'TagState';

  /**
   * Whether a tag is active or deleted
   */
  active: boolean;

  /**
   * Timestamp in millis of last change
   */
  lastModified: number;

  /**
   * Type of attendee urn
   */
  subjectType: SubjectUrnTypeEnum;

  /**
   * Attendee's urn (note this relates to urnType)
   */
  subjectValue: string;

  /**
   * If a tag has an associated value, it goes here
   */
  value: string;
}

export enum SubjectUrnTypeEnum {
  Email = 'Email',
  Integration = 'Integration',
  Org = 'Org',
  Person = 'Person',
  Phone = 'Phone',
  Services = 'Services',
  Team = 'Team',
  User = 'User',
}

/**
 * A per user tag on an event
 */
export interface IUserTag {
  __typename: 'UserTag';

  /**
   * The aggregate count for this tag
   */
  count: number;

  /**
   * The states of this user tag
   */
  states: Array<ITagState>;

  /**
   * The tag name
   */
  tag: string;
}

export type PersonListErrorable = IGraphEntityError | IPersonList;

export interface IPersonList {
  __typename: 'PersonList';
  list: Array<IPerson>;
}

/**
 * A person or room attached to a meeting
 */
export interface IAttendee {
  __typename: 'Attendee';

  /**
   * What to call the attendee
   */
  displayName: string;

  /**
   * Whether the attendee has been marked as optional for the meeting
   */
  optional: boolean;

  /**
   * Whether the attendee is the organizer of the meeting
   */
  organizer: boolean;

  /**
   * The ID of the calendar user's profile
   */
  profileId: string;

  /**
   * Indicates that the attendee is a resource
   */
  resource: boolean;

  /**
   * Invite response status
   */
  responseStatus: ResponseStatusEnum;

  /**
   * Type of attendee urn
   */
  urnType: UrnTypeEnum;

  /**
   * Attendee's urn (note this relates to urnType)
   */
  urnValue: string;
}

export enum ResponseStatusEnum {
  Accepted = 'Accepted',
  Declined = 'Declined',
  NeedsAction = 'NeedsAction',
  NotApplicable = 'NotApplicable',
  Tentative = 'Tentative',
}

export enum UrnTypeEnum {
  Email = 'Email',
  Integration = 'Integration',
  Org = 'Org',
  Phone = 'Phone',
  User = 'User',
}

export type AutopilotEventStatusErrorable =
  | IAutopilotEventStatus
  | IGraphEntityError;

/**
 * the current status of an autopilot event candidate
 */
export interface IAutopilotEventStatus {
  __typename: 'AutopilotEventStatus';

  /**
   * the reason why autopilot cannot move this event
   */
  status: AutopilotEventStatusEnum;
}

export enum AutopilotEventStatusEnum {
  CanMove = 'CanMove',
  EventExceedsDefragRange = 'EventExceedsDefragRange',
  EventOutsideTimeRange = 'EventOutsideTimeRange',
  External = 'External',
  NoDuration = 'NoDuration',
  OverQuota = 'OverQuota',
  Paused = 'Paused',
  Pinned = 'Pinned',
  PrivateEvent = 'PrivateEvent',
  SmartHold = 'SmartHold',
}

export type EventAutopilotHistoryErrorable =
  | IEventAutopilotHistory
  | IGraphEntityError;

/**
 * An event's AP history
 */
export interface IEventAutopilotHistory {
  __typename: 'EventAutopilotHistory';

  /**
   * AP history logs including next defrag run info
   */
  actions: Array<IAutopilotHistoryActionLog>;

  /**
   * AP history status: On/Off/Incomplete/Not Visible
   */
  status: StatusEnum;
}

/**
 * A single action performed on an AP event
 */
export interface IAutopilotHistoryActionLog {
  __typename: 'AutopilotHistoryActionLog';
  actionInfo: Array<string>;
  actionString: string;
  actionType: ActionTypeEnum;
  timestamp: any;
}

export enum ActionTypeEnum {
  AutopilotTurnedOff = 'AutopilotTurnedOff',
  AutopilotTurnedOn = 'AutopilotTurnedOn',
  ClockwiseCouldNotResolveConflict = 'ClockwiseCouldNotResolveConflict',
  ClockwiseMeetingMove = 'ClockwiseMeetingMove',
  ClockwiseMovePaused = 'ClockwiseMovePaused',
  ClockwiseMoveUnpaused = 'ClockwiseMoveUnpaused',
  EventCreated = 'EventCreated',
  EventRescheduled = 'EventRescheduled',
  NextAutopilotRun = 'NextAutopilotRun',
}

export enum StatusEnum {
  AutopilotHistoryIncomplete = 'AutopilotHistoryIncomplete',
  AutopilotHistoryNotVisible = 'AutopilotHistoryNotVisible',
  AutopilotHistoryOff = 'AutopilotHistoryOff',
  AutopilotHistoryOn = 'AutopilotHistoryOn',
}

export type EventFlexibilityNewListErrorable =
  | IEventFlexibilityNewList
  | IGraphEntityError;

export interface IEventFlexibilityNewList {
  __typename: 'EventFlexibilityNewList';
  list: Array<IEventFlexibilityNew>;
}

/**
 * An event flex and corresponding flex string
 */
export interface IEventFlexibilityNew {
  __typename: 'EventFlexibilityNew';
  flex: string;
  flexString: string;
}

/**
 * A query object for fetching events
 */
export interface IEventKey {
  __typename: 'EventKey';

  /**
   * The unique event key for an event
   */
  calendarIds: Array<string>;

  /**
   * The non unique event id
   */
  externalEventId: string;

  /**
   * A hash of all the calendarIds deduped and sorted
   */
  hashedCalendars: string;

  /**
   * The ID of an object
   */
  id: string;
  orgId: string;
}

export type FeatureStatesErrorable = IFeatureStates | IGraphEntityError;

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IFeatureStates {
  __typename: 'FeatureStates';
  meetingReliefSettingVisibility: MeetingReliefSettingVisibility;
  mixedFreemiumOrgUIState: IMixedFreemiumOrgUiState;

  /**
   * @deprecated "use the entity within mixedFreemiumOrgUIState"
   */
  quotaAutopilotStatusCallout: QuotaAutopilotStatusCalloutEnumType;

  /**
   * @deprecated "use the entity within mixedFreemiumOrgUIState"
   */
  quotaStatusBanner: QuotaStatusBannerEnumType;
}

export enum MeetingReliefSettingVisibility {
  DisabledByPaywall = 'DisabledByPaywall',
  HideSetting = 'HideSetting',
  ShowWithBadge = 'ShowWithBadge',
  ShowWithoutBadge = 'ShowWithoutBadge',
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IMixedFreemiumOrgUiState {
  __typename: 'MixedFreemiumOrgUiState';
  autopilotPaused: boolean;
  moveRangesEnabled: boolean;
  paymentStatusBadge: IPaymentStatusBadge;
  quotaStatusBanner: QuotaStatusBannerEnum;
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IPaymentStatusBadge {
  __typename: 'PaymentStatusBadge';
  text: PaymentStatusBadgeTextEnum;
  type: PaymentStatusBadgeTypeEnum;
}

export enum PaymentStatusBadgeTextEnum {
  Business = 'Business',
  Enterprise = 'Enterprise',
  Free = 'Free',
  Pro = 'Pro',
}

export enum PaymentStatusBadgeTypeEnum {
  Blue = 'Blue',
  Gradient = 'Gradient',
}

export enum QuotaStatusBannerEnum {
  ApproachingLimit = 'ApproachingLimit',
  NoEnforcement = 'NoEnforcement',
  ReachedLimit = 'ReachedLimit',
}

export enum QuotaAutopilotStatusCalloutEnumType {
  ApproachingLimit = 'ApproachingLimit',
  NoEnforcement = 'NoEnforcement',
  ReachedLimit = 'ReachedLimit',
}

export enum QuotaStatusBannerEnumType {
  ApproachingLimit = 'ApproachingLimit',
  NoEnforcement = 'NoEnforcement',
  ReachedLimit = 'ReachedLimit',
}

export type MoveRangeInLocalTimeV3Errorable =
  | IGraphEntityError
  | IMoveRangeInLocalTimeV3;

/**
 * A move range specified by two day offset time slots
 */
export interface IMoveRangeInLocalTimeV3 {
  __typename: 'MoveRangeInLocalTimeV3';
  allowedEndTimeInclusive: IDayOffsetTimeSlot;
  allowedStartTimeInclusive: IDayOffsetTimeSlot;
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IDayOffsetTimeSlot {
  __typename: 'DayOffsetTimeSlot';
  dayOffset: number;
  timeSlot: TimeSlot;
}

export type PersonErrorable = IGraphEntityError | IPerson;

/**
 * Full set of rules for how an event recurs
 */
export interface IRecurrenceRules {
  __typename: 'RecurrenceRules';

  /**
   * Instances that have been removed from this set of recurrence rules
   */
  cancelledInstances: Array<string>;

  /**
   * The start time of the recurrence rules
   */
  firstInstanceStartTime: IEventTime;

  /**
   * The interval definition for the recurrence
   */
  interval: IIntervalDefinition;

  /**
   * The timezone of the recurrence rules
   */
  timeZone: string;
}

/**
 * The definition of the interval on which a recurrence recurs
 */
export interface IIntervalDefinition {
  __typename: 'IntervalDefinition';

  /**
   * Which days the recurrence falls on — e.g., “MO,TU,FR”
   */
  byDay: string;

  /**
   * Defines the poisition in the given frequence set that recurs; used for things like the first Monday of every month
   */
  bySetPos: Array<number>;

  /**
   * How many times it recurs (null here AND for untilTime means it recurs forever)
   */
  count: number;

  /**
   * The frequency type of the interval
   */
  freqType: IntervalDefinitionFreqTypeEnum;

  /**
   * The pacing of the interval — e.g., a bi-weekly interval is 2
   */
  interval: number;

  /**
   * When the recurrence terminates (null here AND for count means it recurs forever)
   */
  untilTime: IEventTime;
}

export enum IntervalDefinitionFreqTypeEnum {
  Daily = 'Daily',
  Monthly = 'Monthly',
  Weekly = 'Weekly',
  Yearly = 'Yearly',
}

export type SuggestedAutopilotSettingsResponseV3Errorable =
  | IGraphEntityError
  | ISuggestedAutopilotSettingsResponseV3;

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface ISuggestedAutopilotSettingsResponseV3 {
  __typename: 'SuggestedAutopilotSettingsResponseV3';
  calendarIdToMeetingHours: Array<ISuggestedAutotpilotSettingsResponseV3CalendarIdMeetingHours>;
  intersectionOfAttendeesMeetingHours: IDayOffsetTimeSlotRange;
  suggestedAutopilotSettings: ISuggestedAutopilotSettingsV3;
}

export interface ISuggestedAutotpilotSettingsResponseV3CalendarIdMeetingHours {
  __typename: 'SuggestedAutotpilotSettingsResponseV3CalendarIdMeetingHours';
  calendarId: string;
  dayOffsetTimeSlotRanges: Array<IDayOffsetTimeSlotRange>;
}

/**
 * A range between two day offset time slots
 */
export interface IDayOffsetTimeSlotRange {
  __typename: 'DayOffsetTimeSlotRange';
  maxSlot: IDayOffsetTimeSlot;
  minSlot: IDayOffsetTimeSlot;
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface ISuggestedAutopilotSettingsV3 {
  __typename: 'SuggestedAutopilotSettingsV3';
  allowedEndTime: IDayOffsetTimeSlot;
  allowedStartTime: IDayOffsetTimeSlot;
  isRoomFlexible: boolean;
  movementType: SuggestedAutopilotSettingsV3MovementType;
}

export enum SuggestedAutopilotSettingsV3MovementType {
  Day = 'Day',
  Week = 'Week',
}

export enum TransparencyEnum {
  None = 'None',
  Opaque = 'Opaque',
  Transparent = 'Transparent',
}

export type AutopilotOnboardingSuggestionsResponseErrorable =
  | IAutopilotOnboardingSuggestionsResponse
  | IGraphEntityError;

/**
 * Represents a set of suggestion events to put on autopilot
 */
export interface IAutopilotOnboardingSuggestionsResponse {
  __typename: 'AutopilotOnboardingSuggestionsResponse';
  flexible: Array<IEvent>;
  notFlexible: Array<IEvent>;
  veryFlexible: Array<IEvent>;
}

export type BillingGroupErrorable = IBillingGroup | IGraphEntityError;

/**
 * Represents a billing group
 */
export interface IBillingGroup {
  __typename: 'BillingGroup';
  adminOrgPersons: OrgPersonListErrorable;
  createdAtMillis: any;
  dsyncConfigured: DsyncConfiguredErrorable;
  id: string;
  invitePolicy: InvitePolicyEnum;
  isAutoSyncEnabled: boolean;
  manualMembers: BillingGroupManualMemberListErrorable;
  name: string;
  personListPaginatedErrorable: OrgPersonListPaginatedErrorable;

  /**
   * A link, which expires in 5 minutes, that can be used to configure sso for a billing group.
   */
  ssoAdminPortalLink: SsoAdminPortalLinkErrorable;
  stripeCustomerPortalSession: StripeCustomerPortalSessionErrorable;
  subscription: SubscriptionDetailsErrorable;
  syncSources: Array<IUrn>;
  type: BillingGroupTypeEnum;
}

export interface ISsoAdminPortalLinkOnBillingGroupArguments {
  /**
   * One of Dsync (default, used for billing) or Sso (used for an extra login screen, requires Dsync to work)
   */
  intent?: SsoIntentInput;
}

export type DsyncConfiguredErrorable = IDsyncConfigured | IGraphEntityError;

/**
 * Given a billing group, is SSO Dsync configured
 */
export interface IDsyncConfigured {
  __typename: 'DsyncConfigured';
  isDsyncConfigured: boolean;
}

export enum InvitePolicyEnum {
  AdminOnly = 'AdminOnly',
  AnyMember = 'AnyMember',
}

export type BillingGroupManualMemberListErrorable =
  | IBillingGroupManualMemberList
  | IGraphEntityError;

export interface IBillingGroupManualMemberList {
  __typename: 'BillingGroupManualMemberList';
  list: Array<IBillingGroupManualMember>;
}

/**
 * Represents a manual member of a billing group
 */
export interface IBillingGroupManualMember {
  __typename: 'BillingGroupManualMember';
  calendarId: string;
  externalUserId: string;

  /**
   * The associated person
   */
  orgPerson: OrgPersonErrorable;
  personId: string;
  userId: string;
}

export type OrgPersonErrorable = IGraphEntityError | IOrgPerson;

export type OrgPersonListPaginatedErrorable =
  | IGraphEntityError
  | IOrgPersonListPaginated;

/**
 * List of org persons along with a nexPersonId and previousPersonId for pagination.
 */
export interface IOrgPersonListPaginated {
  __typename: 'OrgPersonListPaginated';
  count: number;
  inputQuery: string;
  nextPersonId: string;
  persons: Array<IOrgPerson>;
  previousPersonId: string;
  searchPersons: Array<ISearchPerson>;
}

/**
 * A wrapper around org person for the admin panel members tab
 */
export interface ISearchPerson {
  __typename: 'SearchPerson';
  person: IOrgPerson;
  status: SearchPersonStatusResponse;
}

export enum SearchPersonStatusResponse {
  Deactivated = 'Deactivated',
  Invited = 'Invited',
  NonUser = 'NonUser',
  User = 'User',
}

export enum SsoIntentInput {
  Dsync = 'Dsync',
  Sso = 'Sso',
}

export type SsoAdminPortalLinkErrorable =
  | IGraphEntityError
  | ISsoAdminPortalLink;

/**
 * Generated link to WorkOS admin portal
 */
export interface ISsoAdminPortalLink {
  __typename: 'SsoAdminPortalLink';
  link: string;
}

export type StripeCustomerPortalSessionErrorable =
  | IGraphEntityError
  | IStripeCustomerPortalSession;

/**
 * Generated stripe customer portal session for a given subscription
 */
export interface IStripeCustomerPortalSession {
  __typename: 'StripeCustomerPortalSession';
  account: IAccountStripe;
  sessionUrl: string;
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IAccountStripe {
  __typename: 'AccountStripe';
  accountId: string;
  stripeCustomerId: string;
}

export type SubscriptionDetailsErrorable =
  | IGraphEntityError
  | ISubscriptionDetails;

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface ISubscriptionDetails {
  __typename: 'SubscriptionDetails';
  subscription: IPaymentSubscription;
}

export enum BillingGroupTypeEnum {
  Manual = 'Manual',
  SSO = 'SSO',
}

export type BillingGroupListErrorable = IBillingGroupList | IGraphEntityError;

export interface IBillingGroupList {
  __typename: 'BillingGroupList';
  list: Array<IBillingGroup>;
}

export type SmartHoldCalculationResultErrorable =
  | IGraphEntityError
  | ISmartHoldCalculationResult;

/**
 * Calculates smart hold slots without reserving them on the calendar
 */
export interface ISmartHoldCalculationResult {
  __typename: 'SmartHoldCalculationResult';
  events: Array<IEvent>;
  smartHolds: Array<IEvent>;
}

export type CalendarStatusErrorable = ICalendarStatus | IGraphEntityError;

/**
 * Current Calendar Status
 */
export interface ICalendarStatus {
  __typename: 'CalendarStatus';
  calendarId: string;

  /**
   * The ID of an object
   */
  id: string;
  moonPhase: MoonPhase;
  status: CalendarStatusResponse;
}

export enum MoonPhase {
  FirstQuarterMoon = 'FirstQuarterMoon',
  FullMoon = 'FullMoon',
  LastQuarterMoon = 'LastQuarterMoon',
  NewMoon = 'NewMoon',
  WaningCrescentMoon = 'WaningCrescentMoon',
  WaningGibbousMoon = 'WaningGibbousMoon',
  WaxingCrescentMoon = 'WaxingCrescentMoon',
  WaxingGibbousMoon = 'WaxingGibbousMoon',
}

export enum CalendarStatusResponse {
  AfterHours = 'AfterHours',
  BeforeWorkingHours = 'BeforeWorkingHours',
  DayOff = 'DayOff',
  FocusTime = 'FocusTime',
  Holiday = 'Holiday',
  Lunch = 'Lunch',
  Meeting = 'Meeting',
  Other = 'Other',
  OutOfOffice = 'OutOfOffice',
  Weekend = 'Weekend',
  WorkingHours = 'WorkingHours',
}

/**
 * Calendar stats for a teammate
 */
export interface ICalendarTimeStats {
  __typename: 'CalendarTimeStats';
  busyMinutes: number;
  calendarId: string;
  focusMinutes: number;
  fragmentedMinutes: number;
  scoresByDate: Array<ICalendarFocusTimeScore>;
  scoresByDay: Array<ICalendarFocusTimeScore>;
  totalMinutes: number;
  unclassifiedMinutes: number;
}

/**
 * Health Score for a given calendarId
 */
export interface ICalendarFocusTimeScore {
  __typename: 'CalendarFocusTimeScore';
  busyMinutes: number;
  calendarId: string;
  categoryAdHoc: number;
  categoryExternal: number;
  categoryFocusTime: number;
  categoryIndividualHold: number;
  categoryOneOnOne: number;
  categoryOther: number;
  categoryOutOfOffice: number;
  categoryPaceSetting: number;
  categoryRecruiting: number;
  crawledStatus: CrawledStatus;
  focusMinutes: number;
  fragmentedMinutes: number;
  secondaryKey: string;
  totalMinutes: number;
}

export enum CrawledStatus {
  NoAccess = 'NoAccess',
  None = 'None',
  Pending = 'Pending',
  Stale = 'Stale',
  Synced = 'Synced',
  VeryStale = 'VeryStale',
}

export type CalendarTimeStatsListErrorable =
  | ICalendarTimeStatsList
  | IGraphEntityError;

export interface ICalendarTimeStatsList {
  __typename: 'CalendarTimeStatsList';
  list: Array<ICalendarTimeStats>;
}

export type CanScheduleResponseListErrorable =
  | ICanScheduleResponseList
  | IGraphEntityError;

export interface ICanScheduleResponseList {
  __typename: 'CanScheduleResponseList';
  list: Array<ICanScheduleResponse>;
}

/**
 * An object representing whether a user can reschedule or enable autopilot
 */
export interface ICanScheduleResponse {
  __typename: 'CanScheduleResponse';
  attendeeNotInDomain: boolean;
  canAutopilot: boolean;
  canReschedule: boolean;
  externalEventId: string;
  isAllDayEvent: boolean;
  organizerNotClockwiseUser: boolean;
}

/**
 * A list of teammates that you work with the most
 */
export interface ICollaboratorStats {
  __typename: 'CollaboratorStats';
  calendarId: string;
  topCollaborators: Array<ICollaboratorScore>;
}

/**
 * A teammates shared minutes
 */
export interface ICollaboratorScore {
  __typename: 'CollaboratorScore';
  calendarId: string;
  sharedMinutes: number;
}

export type CollaboratorStatsErrorable = ICollaboratorStats | IGraphEntityError;

export type CurrentScheduledSlotImpactErrorable =
  | ICurrentScheduledSlotImpact
  | IGraphEntityError;

/**
 * Current Scheduled Slot Impact
 */
export interface ICurrentScheduledSlotImpact {
  __typename: 'CurrentScheduledSlotImpact';

  /**
   * The current slot associated to the event
   */
  currentSlot: ISchedulingSlot;

  /**
   * The calendar Ids that have not been crawled yet
   */
  uncrawledCalendars: Array<string>;
}

/**
 * Scheduling Slot
 */
export interface ISchedulingSlot {
  __typename: 'SchedulingSlot';
  attendanceImpacts: Array<ISchedulingAttendanceImpact>;
  gapSlot: IGapSlot;
  roomImpacts: Array<ISchedulingRoomImpact>;
  summary: ISchedulingAttendanceSummary;
  timeRange: ITimeRange;
}

/**
 * Scheduling Attendance Impact
 */
export interface ISchedulingAttendanceImpact {
  __typename: 'SchedulingAttendanceImpact';
  calendarId: string;
  conflictingMeetings: Array<string>;
  focusTimeMinutesCost: number;
  hasConflict: boolean;
  inconveniences: Array<Inconveniences>;
  unavailabilities: Array<Unavailabilities>;
}

export enum Inconveniences {
  External = 'External',
  MissedLunch = 'MissedLunch',
  NoMeetingRelief = 'NoMeetingRelief',
  OnNoMeetingDay = 'OnNoMeetingDay',
  Other = 'Other',
  OutsideMeetingHours = 'OutsideMeetingHours',
  OutsideWorkingHours = 'OutsideWorkingHours',
  Uncrawled = 'Uncrawled',
}

export enum Unavailabilities {
  FarOutsideWorkingHours = 'FarOutsideWorkingHours',
}

/**
 * Gap Slot
 */
export interface IGapSlot {
  __typename: 'GapSlot';
  dayTimeSlot: IDayTimeSlot;
  numSlots: number;
}

/**
 * A 30 minute interval on a given day
 */
export interface IDayTimeSlot {
  __typename: 'DayTimeSlot';

  /**
   * The date represented as a string
   */
  date: string;

  /**
   * An enum representing a 30 minute time slot
   */
  timeSlot: TimeSlot;
}

/**
 * Scheduling Room Impact
 */
export interface ISchedulingRoomImpact {
  __typename: 'SchedulingRoomImpact';
  available: boolean;
  roomId: string;
  tooLarge: boolean;
  tooSmall: boolean;
}

/**
 * Scheduling Attendance Summary
 */
export interface ISchedulingAttendanceSummary {
  __typename: 'SchedulingAttendanceSummary';
  availableRooms: Array<string>;
  conflictingAttendees: Array<string>;
  focusTimeMinutesCost: number;
  freeAttendees: Array<string>;
  inconvenientForAttendees: Array<string>;
  tags: Array<SchedulingAttendanceSummaryTag>;
  unavailableAttendees: Array<string>;
}

export enum SchedulingAttendanceSummaryTag {
  Best = 'Best',
  Conflict = 'Conflict',
  Current = 'Current',
  Good = 'Good',
  Inconvenient = 'Inconvenient',
  InFocusTime = 'InFocusTime',
  MostFocus = 'MostFocus',
  OverLunch = 'OverLunch',
  Soonest = 'Soonest',
}

/**
 * A time range
 */
export interface ITimeRange {
  __typename: 'TimeRange';

  /**
   * The end time in the range
   */
  endTime: IEventTime;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The start time in the range
   */
  startTime: IEventTime;
}

/**
 * For a given org, which crawl states calendar Ids fall into
 */
export interface ICrawledCalendarInfo {
  __typename: 'CrawledCalendarInfo';

  /**
   * The ID of an object
   */
  id: string;

  /**
   * Calendar Ids that have crawl status of 'None'
   */
  none: Array<string>;

  /**
   * Calendar Ids that have crawl status of 'Pending'
   */
  pending: Array<string>;

  /**
   * Calendar Ids that have crawl status of 'Stale'
   */
  stale: Array<string>;

  /**
   * Calendar Ids that have crawl status of 'Synced'
   */
  synced: Array<string>;
}

export enum OrgCrawlTest {
  GoogleApiAccessDisabled = 'GoogleApiAccessDisabled',
  GoogleContactSharingDisabled = 'GoogleContactSharingDisabled',
  NoCrawl = 'NoCrawl',
  NoExternalOrganization = 'NoExternalOrganization',
  ShouldCrawl = 'ShouldCrawl',
  Skipped = 'Skipped',
  StartedCrawl = 'StartedCrawl',
}

export type EventColoringSettingsErrorable =
  | IEventColoringSettings
  | IGraphEntityError;

/**
 * A User's settings for how their events should be colored
 */
export interface IEventColoringSettings {
  __typename: 'EventColoringSettings';

  /**
   * A user's collection of event-category coloring preferences
   */
  eventCategoryColorings: Array<IEventCategoryColoring>;

  /**
   * The ID of an object
   */
  id: string;
}

/**
 * Color settings for a given event category
 */
export interface IEventCategoryColoring {
  __typename: 'EventCategoryColoring';
  active: boolean;
  eventCategory: string;
  googleColorKey: string;
  isUserSet: boolean;
}

export type EventErrorable = IEvent | IGraphEntityError;

export type EventParentBaseWrapperErrorable =
  | IEventParentBaseWrapper
  | IGraphEntityError;

/**
 * Event parents and event parent bases for those event parents
 */
export interface IEventParentBaseWrapper {
  __typename: 'EventParentBaseWrapper';
  eventParentBases: Array<IEventParentBase>;
  eventParents: Array<IEventParent>;
}

/**
 * A record indicating which version of an event is current
 */
export interface IEventParentBase {
  __typename: 'EventParentBase';

  /**
   * The ID of an object
   */
  id: string;

  /**
   * Ignore all event parents with a eventParentBaseTimestamp less than this
   */
  timestamp: any;
}

/**
 * An object that holds all event versions for a given externalEventId
 */
export interface IEventParent {
  __typename: 'EventParent';

  /**
   * All calendarIds found on any version of this event
   */
  calendarIds: Array<string>;
  eventParentBaseId: string;
  eventParentBaseTimestamp: any;

  /**
   * The services parentEventId shared by event parents referring to the same recurrence
   * @deprecated "EventParentWrapper shouldn't be used"
   */
  eventParentWrapperId: string;

  /**
   * All active versions of an event for an externalEventId
   */
  events: Array<IEvent>;

  /**
   * The externalEventId shared by all versions, used in the id as well
   */
  externalEventId: string;

  /**
   * All calendarIds used to fetch this EventParent.  An empty array represents all calendarIds in a working group
   */
  fetchedWithCalendarIds: Array<string>;

  /**
   * Event flexibility, if this event is flexible
   */
  flexibility: EventFlexibilityErrorable;

  /**
   * Constraints around setting flexibility, for the given event
   */
  flexibilityConstraints: FlexibilityConstraintsErrorable;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * For personal calendar synced events, the actual event from the personal calendar
   */
  syncedOriginEventErrorable: EventErrorable;
}

export interface IFlexibilityOnEventParentArguments {
  calendarId: any;
}

export interface IFlexibilityConstraintsOnEventParentArguments {
  calendarId: any;
}

export interface ISyncedOriginEventErrorableOnEventParentArguments {
  /**
   * Calendar that event is synced to. If null, an unspecified event version is used
   */
  calendarId?: string;
}

export type EventFlexibilityErrorable = IEventFlexibility | IGraphEntityError;

/**
 * Flexibility for a given event
 */
export interface IEventFlexibility {
  __typename: 'EventFlexibility';

  /**
   * String describing the events flexibility. Null if unable to fetch
   */
  description: string;

  /**
   * Whether the event can change rooms, null if the event has no room
   */
  isRoomFlexible: boolean;

  /**
   * How an event can move within a day, null if not configured
   */
  timeOfDayFlexibility: ITimeOfDayFlexibility;

  /**
   * How an event can move between days
   */
  timeRangeFlexibility: TimeRangeFlexibility;
}

export interface ITimeOfDayFlexibility {
  __typename: 'TimeOfDayFlexibility';

  /**
   * End of allowed time range for moving an event, null if not configured
   */
  end: any;

  /**
   * Beginning of allowed time range for moving an event, null if not configured
   */
  start: any;
}

export type TimeRangeFlexibility = IDayOfWeekFlexibility | IRangeFlexibility;

export interface IDayOfWeekFlexibility {
  __typename: 'DayOfWeekFlexibility';
  allowedDays: Array<DayOfWeek>;
}

export enum DayOfWeek {
  Friday = 'Friday',
  Monday = 'Monday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
  Thursday = 'Thursday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
}

export interface IRangeFlexibility {
  __typename: 'RangeFlexibility';
  range: FlexibilityRange;
}

export enum FlexibilityRange {
  /**
   * The event can only move within its currently scheduled day
   */
  Day = 'Day',

  /**
   * The event can move anywhere within its currently scheduled week
   */
  Week = 'Week',
}

export type FlexibilityConstraintsErrorable =
  | IFlexibilityConstraints
  | IGraphEntityError;

/**
 * Constraints for modifying event flexibilty
 */
export interface IFlexibilityConstraints {
  __typename: 'FlexibilityConstraints';

  /**
   * Whether the user is allowed to modify the flexibility settings for this event. Null if unable to compute
   */
  allowedToModify: boolean;

  /**
   *               Days of the week that are available for all attendees of an
   * event; the intersection of working days for attendees.
   *               Null if unable to compute.
   */
  availableMeetingDays: Array<DayOfWeek>;

  /**
   * The intersection of meeting hours, on the day the event is currently scheduled. Null if there are no available hours
   */
  availableMeetingHours: IAvailableMeetingHours;

  /**
   * Whether this event is eligible for day of week flexibility. Null if unable to compute
   */
  dayOfWeekFlexibilityEligible: boolean;

  /**
   * Whether this event is external, and is ineligible for flexibility. Null if unable to compute
   */
  externalEvent: boolean;

  /**
   * Whether time of day flexibility is allowed by the user's plan. Null if unable to compute
   */
  timeOfDayAllowedByPlan: boolean;
}

/**
 * Start and end of hours that are available to meet, in the querying user's time zone
 */
export interface IAvailableMeetingHours {
  __typename: 'AvailableMeetingHours';
  end: any;
  start: any;
}

export type EventParentBaseWrapperByWeekListErrorable =
  | IEventParentBaseWrapperByWeekList
  | IGraphEntityError;

export interface IEventParentBaseWrapperByWeekList {
  __typename: 'EventParentBaseWrapperByWeekList';
  list: Array<IEventParentBaseWrapperByWeek>;
}

/**
 * Event parent bases and parents grouped by day
 */
export interface IEventParentBaseWrapperByWeek {
  __typename: 'EventParentBaseWrapperByWeek';
  eventParentBases: Array<IEventParentBase>;
  eventParentsByDay: Array<Array<IEventParent>>;
}

export type EventParentBaseWrapperByWeekObjectListErrorable =
  | IEventParentBaseWrapperByWeekObjectList
  | IGraphEntityError;

export interface IEventParentBaseWrapperByWeekObjectList {
  __typename: 'EventParentBaseWrapperByWeekObjectList';
  list: Array<IEventParentBaseWrapperByWeekObject>;
}

/**
 * Event parent bases and parents grouped by day
 */
export interface IEventParentBaseWrapperByWeekObject {
  __typename: 'EventParentBaseWrapperByWeekObject';
  eventParentBases: Array<IEventParentBase>;
  eventParentsByDay: Array<IEventParentsForDay>;
}

export interface IEventParentsForDay {
  __typename: 'EventParentsForDay';
  eventParents: Array<IEventParent>;
}

/**
 * A connection to a list of items.
 */
export interface IEventParentConnection {
  __typename: 'EventParentConnection';

  /**
   * A list of edges.
   */
  edges: Array<IEventParentEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IEventParentEdge {
  __typename: 'EventParentEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IEventParent;
}

export type EventParentWrapperConnectionErrorable =
  | IEventParentWrapperConnection
  | IGraphEntityError;

/**
 * A connection to a list of items.
 */
export interface IEventParentWrapperConnection {
  __typename: 'EventParentWrapperConnection';

  /**
   * A list of edges.
   */
  edges: Array<IEventParentWrapperEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IEventParentWrapperEdge {
  __typename: 'EventParentWrapperEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IEventParentWrapper;
}

/**
 * A top level entity that encompasses a group of evereturn eventParents
 */
export interface IEventParentWrapper {
  __typename: 'EventParentWrapper';
  deleted: boolean;
  eventParents: Array<IEventParent>;

  /**
   * The ID of an object
   */
  id: string;
}

export type ExternalGroupListErrorable = IExternalGroupList | IGraphEntityError;

export interface IExternalGroupList {
  __typename: 'ExternalGroupList';
  list: Array<IExternalGroup>;
}

/**
 * A group of people defined in an external system
 */
export interface IExternalGroup {
  __typename: 'ExternalGroup';
  email: string;

  /**
   * The ID of an object
   */
  id: string;
  memberEmails: Array<string>;
  members: PersonListErrorable;
}

export type FeatureGridErrorable = IFeatureGrid | IGraphEntityError;

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IFeatureGrid {
  __typename: 'FeatureGrid';
  featureStates: IFeatureStates;
  stateMetadata: IPaymentStateMetadata;
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IPaymentStateMetadata {
  __typename: 'PaymentStateMetadata';
  primaryBillingGroupMetadata: IPrimaryBillingGroupMetadata;
}

/**
 * Primary billing group metadata field
 */
export interface IPrimaryBillingGroupMetadata {
  __typename: 'PrimaryBillingGroupMetadata';
  billingGroup: IBillingGroup;
  subscription: IPaymentSubscription;
}

export type FeedSuggestionResultsErrorable =
  | IFeedSuggestionResults
  | IGraphEntityError;

/**
 * A list of feed suggestions for this user/org
 */
export interface IFeedSuggestionResults {
  __typename: 'FeedSuggestionResults';
  suggestions: Array<IFeedSuggestion>;
}

/**
 * A suggestion in the feed
 */
export interface IFeedSuggestion {
  __typename: 'FeedSuggestion';
  expiresMillis: any;

  /**
   * The ID of an object
   */
  id: string;
  occurredMillis: any;
  primaryAction: ISuggestionActionResult;
  suggestionId: string;
  suggestionPayload: SuggestionResultPayload;
  suggestionType: SuggestionType;
  text: IFeedSuggestionText;
}

/**
 * Suggestion Action Result
 */
export interface ISuggestionActionResult {
  __typename: 'SuggestionActionResult';
  display: string;
  isPrimary: boolean;
  payload: SuggestionActionPayload;
  type: SuggestionActionResultType;
}

export type SuggestionActionPayload =
  | ISuggestionActionAutopilotPayload
  | ISuggestionActionConfigureSettingsPayload
  | ISuggestionActionDeclinePayload
  | ISuggestionActionFyiPayload
  | ISuggestionActionInvitePayload
  | ISuggestionActionRemoveAttendeePayload
  | ISuggestionActionRequestToMovePayload
  | ISuggestionActionReschedulePayload
  | ISuggestionActionViewMeetingPayload
  | ISuggestionActionViewProfilePayload
  | ISuggestionActionViewTeamDashboardPayload;

/**
 * Suggestion Action Autopilot Payload
 */
export interface ISuggestionActionAutopilotPayload {
  __typename: 'SuggestionActionAutopilotPayload';
  calendarId: string;
  externalEventId: string;
  hasRoom: boolean;
}

/**
 * Payload for suggestions that require the user to configure a setting
 */
export interface ISuggestionActionConfigureSettingsPayload {
  __typename: 'SuggestionActionConfigureSettingsPayload';
  configureSettingsType: ConfigureSettingsType;
}

export enum ConfigureSettingsType {
  MeetingColors = 'MeetingColors',
  PersonalCalendarSync = 'PersonalCalendarSync',
  SettingUpTeam = 'SettingUpTeam',
  SettingUpWorkingHours = 'SettingUpWorkingHours',
  ShadowCalendar = 'ShadowCalendar',
  SlackStatusSync = 'SlackStatusSync',
  TravelTime = 'TravelTime',
}

/**
 * Suggestion Action Decline Payload
 */
export interface ISuggestionActionDeclinePayload {
  __typename: 'SuggestionActionDeclinePayload';
  nop: string;
}

/**
 * Suggestion Action FYI Payload
 */
export interface ISuggestionActionFyiPayload {
  __typename: 'SuggestionActionFyiPayload';
  nop: string;
}

/**
 * Suggestion Action Invite Payload
 */
export interface ISuggestionActionInvitePayload {
  __typename: 'SuggestionActionInvitePayload';
  calendarId: string;
}

/**
 * Suggestion Action Remove Attendee Payload
 */
export interface ISuggestionActionRemoveAttendeePayload {
  __typename: 'SuggestionActionRemoveAttendeePayload';
  nop: string;
}

/**
 * Suggestion Action Request To Move Payload
 */
export interface ISuggestionActionRequestToMovePayload {
  __typename: 'SuggestionActionRequestToMovePayload';
  betterTime: IDayTimeSlot;
  nearestAlternateTime: IDayTimeSlot;
}

/**
 * Suggestion Action Reschedule Payload
 */
export interface ISuggestionActionReschedulePayload {
  __typename: 'SuggestionActionReschedulePayload';
  betterTime: IDayTimeSlot;
  calendarId: string;
  event: IEvent;
  eventStartWeek: string;
  externalEventId: string;
  nearestAlternateTime: IDayTimeSlot;
}

/**
 * Suggestion Action View Meeting Payload
 */
export interface ISuggestionActionViewMeetingPayload {
  __typename: 'SuggestionActionViewMeetingPayload';
  calendarId: string;
  externalEventId: string;
}

/**
 * Suggestion Action View Profile Payload
 */
export interface ISuggestionActionViewProfilePayload {
  __typename: 'SuggestionActionViewProfilePayload';
  calendarId: string;
  viewWeek: string;
}

/**
 * Payload (currently empty) for the action to view the team dashboard
 */
export interface ISuggestionActionViewTeamDashboardPayload {
  __typename: 'SuggestionActionViewTeamDashboardPayload';
  nop: string;
}

export enum SuggestionActionResultType {
  Autopilot = 'Autopilot',
  ConfigureSettings = 'ConfigureSettings',
  Decline = 'Decline',
  Fyi = 'Fyi',
  Invite = 'Invite',
  RemoveAttendee = 'RemoveAttendee',
  RequestToMove = 'RequestToMove',
  Reschedule = 'Reschedule',
  ViewMeeting = 'ViewMeeting',
  ViewProfile = 'ViewProfile',
  ViewTeamDashboard = 'ViewTeamDashboard',
}

export type SuggestionResultPayload =
  | ISuggestionResultAutopilotAttendeePayload
  | ISuggestionResultChromeExtensionTipPayload
  | ISuggestionResultConflictingMeetingPayload
  | ISuggestionResultExpensiveMeetingPayload
  | ISuggestionResultExpensiveOrganizerPayload
  | ISuggestionResultLowFocusTimePayload
  | ISuggestionResultMeetingNotOnAutopilotPayload
  | ISuggestionResultMissingAttendancePayload
  | ISuggestionResultMissingUserConfigurationPayload
  | ISuggestionResultRecurringOneOnOneOrganizerPayload
  | ISuggestionResultRunningOutOfFocusTimePayload
  | ISuggestionResultShellPayload
  | ISuggestionResultTeammateOutOfOfficePayload
  | ISuggestionResultWeekInReviewPayload
  | ISuggestionResultWeeklyAutopilotSummaryPayload;

/**
 * Payload for an attendee on an autopiloted meeting
 */
export interface ISuggestionResultAutopilotAttendeePayload {
  __typename: 'SuggestionResultAutopilotAttendeePayload';
  attendee: string;
}

/**
 * Suggestion Result Chrome Extension Tip Payload
 */
export interface ISuggestionResultChromeExtensionTipPayload {
  __typename: 'SuggestionResultChromeExtensionTipPayload';
  tipType: TipType;
}

export enum TipType {
  CheckOutProfile = 'CheckOutProfile',
  CheckOutRescheduler = 'CheckOutRescheduler',
  CheckOutTeamDashboard = 'CheckOutTeamDashboard',
  FeedWelcome = 'FeedWelcome',
}

/**
 * Suggestion Result Conflicting Meeting Payload
 */
export interface ISuggestionResultConflictingMeetingPayload {
  __typename: 'SuggestionResultConflictingMeetingPayload';
  conflictingEvent: IEvent;
  targetEvent: IEvent;
}

/**
 * Suggestion Result Expensive Meeting Payload
 */
export interface ISuggestionResultExpensiveMeetingPayload {
  __typename: 'SuggestionResultExpensiveMeetingPayload';
  focusMinutesCost: number;
  targetEvent: IEvent;
}

/**
 * Suggestion Result Expensive Organizer Payload
 */
export interface ISuggestionResultExpensiveOrganizerPayload {
  __typename: 'SuggestionResultExpensiveOrganizerPayload';
  approximateMonthlyFocusHoursCost: number;
  calendarId: string;
}

/**
 * Suggestion Result Low Focus Time Payload
 */
export interface ISuggestionResultLowFocusTimePayload {
  __typename: 'SuggestionResultLowFocusTimePayload';
  calendarId: string;
  totalFocusTimeMinutes: number;
  totalMinutes: number;
  week: string;
}

/**
 * Suggestion Result Meetinging Not On Autopilot Payload
 */
export interface ISuggestionResultMeetingNotOnAutopilotPayload {
  __typename: 'SuggestionResultMeetingNotOnAutopilotPayload';
  targetEvent: IEvent;
}

/**
 * Suggestion Result Missing Attendance Payload
 */
export interface ISuggestionResultMissingAttendancePayload {
  __typename: 'SuggestionResultMissingAttendancePayload';
  targetEvent: IEvent;
}

/**
 * Suggestion Result Missing User Configuration Payload
 */
export interface ISuggestionResultMissingUserConfigurationPayload {
  __typename: 'SuggestionResultMissingUserConfigurationPayload';
  configurationType: ConfigurationType;
}

export enum ConfigurationType {
  MeetingColors = 'MeetingColors',
  PersonalCalendarSync = 'PersonalCalendarSync',
  SettingUpTeam = 'SettingUpTeam',
  SettingUpWorkingHours = 'SettingUpWorkingHours',
  ShadowCalendar = 'ShadowCalendar',
  SlackStatusSync = 'SlackStatusSync',
  TravelTime = 'TravelTime',
}

/**
 * Payload for an organizer of a user's recurring 1:1
 */
export interface ISuggestionResultRecurringOneOnOneOrganizerPayload {
  __typename: 'SuggestionResultRecurringOneOnOneOrganizerPayload';
  organizer: string;
}

/**
 * Suggestion Result Running Out Of Focus Time Payload
 */
export interface ISuggestionResultRunningOutOfFocusTimePayload {
  __typename: 'SuggestionResultRunningOutOfFocusTimePayload';
  calendarId: string;
  originalFocusTimeMinutes: number;
  remainingFocusTimeMinutes: number;
  week: string;
}

/**
 * Suggestion Result Shell Payload
 */
export interface ISuggestionResultShellPayload {
  __typename: 'SuggestionResultShellPayload';
  nop: string;
}

/**
 * Suggestion Result Teammate Out Of Office Payload
 */
export interface ISuggestionResultTeammateOutOfOfficePayload {
  __typename: 'SuggestionResultTeammateOutOfOfficePayload';
  calendarId: string;
  maxDate: string;
  minDate: string;
  targetEvent: IEvent;
}

/**
 * Suggestion Result Week In Review Payload
 */
export interface ISuggestionResultWeekInReviewPayload {
  __typename: 'SuggestionResultWeekInReviewPayload';
  calendarId: string;
  rewindHistory: IRewindHistory;
  rewindWeek: string;
}

/**
 * Rewind History
 */
export interface IRewindHistory {
  __typename: 'RewindHistory';

  /**
   * The ID of an object
   */
  id: string;
  snapshotDurationMillis: any;
  snapshots: Array<ICompactCalendarSlotSnapshot>;
}

/**
 * Compact Calendar Slot Snapshot for Week In Review
 */
export interface ICompactCalendarSlotSnapshot {
  __typename: 'CompactCalendarSlotSnapshot';
  days: Array<IDaySnapshot>;
  snapshotMillis: any;
}

/**
 * A collection of Snapshot Bits for a day
 */
export interface IDaySnapshot {
  __typename: 'DaySnapshot';
  focusTime: ISnapshotBits;
  fragmented: ISnapshotBits;
  lunch: ISnapshotBits;
  meetings: ISnapshotBits;
  workingHours: ISnapshotBits;
}

/**
 * Week In Review Snapshot Bits
 */
export interface ISnapshotBits {
  __typename: 'SnapshotBits';
  amBits: number;
  pmBits: number;
}

export interface ISuggestionResultWeeklyAutopilotSummaryPayload {
  __typename: 'SuggestionResultWeeklyAutopilotSummaryPayload';
  dateRangeMax: string;
  dateRangeMin: string;
  historicalDefragBenefitScore: IHistoricalDefragBenefitScore;
}

export interface IHistoricalDefragBenefitScore {
  __typename: 'HistoricalDefragBenefitScore';
  affectedCalendars: Array<string>;
  conflictsResolved: number;
  meetingsMoved: number;
  totalFocusTimeSavings: number;
}

export enum SuggestionType {
  AutopilotAttendee = 'AutopilotAttendee',
  ChromeExtensionTip = 'ChromeExtensionTip',
  ConflictingMeeting = 'ConflictingMeeting',
  ExpensiveMeeting = 'ExpensiveMeeting',
  ExpensiveOrganizer = 'ExpensiveOrganizer',
  LowFocusTime = 'LowFocusTime',
  MeetingNotOnAutopilot = 'MeetingNotOnAutopilot',
  MissingAttendance = 'MissingAttendance',
  MissingUserConfiguration = 'MissingUserConfiguration',
  RecurringOneOnOneOrganizer = 'RecurringOneOnOneOrganizer',
  RunningOutOfFocusTime = 'RunningOutOfFocusTime',
  Shell = 'Shell',
  TeammateOutOfOffice = 'TeammateOutOfOffice',
  WeekInReview = 'WeekInReview',
  WeeklyAutopilotSummary = 'WeeklyAutopilotSummary',
}

/**
 * Text for a feed suggestion
 */
export interface IFeedSuggestionText {
  __typename: 'FeedSuggestionText';
  subHeading: string;
  title: string;
}

export interface IDateRangeInput {
  maxDate: string;
  minDate: string;
}

export type HeatmapResultErrorable = IGraphEntityError | IHeatmapResult;

/**
 * The result of a heatmap fetch
 */
export interface IHeatmapResult {
  __typename: 'HeatmapResult';
  busyHours: Array<ITimeRange>;
  crawledCalendarIds: Array<string>;
  holdHours: Array<ITimeRange>;
  meetingHours: Array<ITimeRange>;
  uncrawledCalendarIds: Array<string>;
}

export type FocusTimeStatsErrorable = IFocusTimeStats | IGraphEntityError;

/**
 * Average focus time stats (for use in Focus Time Sync)
 */
export interface IFocusTimeStats {
  __typename: 'FocusTimeStats';
  minutesPerWeek: number;
}

export type EventParentErrorable = IEventParent | IGraphEntityError;

/**
 * Meeting Hours for a given user id
 */
export interface IMeetingHoursQueryResult {
  __typename: 'MeetingHoursQueryResult';
  calendarMeetingHours: ICalendarMeetingHoursConnection;
  orgMeetingHours: IOrgMeetingHours;
  userMeetingHours: IUserMeetingHoursConnection;
}

export interface ICalendarMeetingHoursOnMeetingHoursQueryResultArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface IUserMeetingHoursOnMeetingHoursQueryResultArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

/**
 * A connection to a list of items.
 */
export interface ICalendarMeetingHoursConnection {
  __typename: 'CalendarMeetingHoursConnection';

  /**
   * A list of edges.
   */
  edges: Array<ICalendarMeetingHoursEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface ICalendarMeetingHoursEdge {
  __typename: 'CalendarMeetingHoursEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: ICalendarMeetingHours;
}

/**
 * Meeting Hours for a given calendar id
 */
export interface ICalendarMeetingHours {
  __typename: 'CalendarMeetingHours';
  calendarId: string;

  /**
   * The ID of an object
   */
  id: string;
  workingHours: IWorkingHours;
}

/**
 * Working Hours
 */
export interface IWorkingHours {
  __typename: 'WorkingHours';
  dayOverrides: Array<IDayOverride>;
  daySettings: Array<IDaySetting>;

  /**
   * The ID of an object
   */
  id: string;
  timeZone: string;

  /**
   * Can be working hours or meeting hours (if you have PreferredFocusTime type, something's wrong...
   */
  type: WorkingHoursTypeEnum;
}

/**
 * An override for a day
 */
export interface IDayOverride {
  __typename: 'DayOverride';
  date: string;
  setting: IWorkingSetting;
}

/**
 * Working time constraints for a time period
 */
export interface IWorkingSetting {
  __typename: 'WorkingSetting';
  excludedSlots: Array<ExcludedSlots>;
  maxSlot: MaxSlot;
  minSlot: MinSlot;
}

export enum ExcludedSlots {
  T_00_00 = 'T_00_00',
  T_00_30 = 'T_00_30',
  T_01_00 = 'T_01_00',
  T_01_30 = 'T_01_30',
  T_02_00 = 'T_02_00',
  T_02_30 = 'T_02_30',
  T_03_00 = 'T_03_00',
  T_03_30 = 'T_03_30',
  T_04_00 = 'T_04_00',
  T_04_30 = 'T_04_30',
  T_05_00 = 'T_05_00',
  T_05_30 = 'T_05_30',
  T_06_00 = 'T_06_00',
  T_06_30 = 'T_06_30',
  T_07_00 = 'T_07_00',
  T_07_30 = 'T_07_30',
  T_08_00 = 'T_08_00',
  T_08_30 = 'T_08_30',
  T_09_00 = 'T_09_00',
  T_09_30 = 'T_09_30',
  T_10_00 = 'T_10_00',
  T_10_30 = 'T_10_30',
  T_11_00 = 'T_11_00',
  T_11_30 = 'T_11_30',
  T_12_00 = 'T_12_00',
  T_12_30 = 'T_12_30',
  T_13_00 = 'T_13_00',
  T_13_30 = 'T_13_30',
  T_14_00 = 'T_14_00',
  T_14_30 = 'T_14_30',
  T_15_00 = 'T_15_00',
  T_15_30 = 'T_15_30',
  T_16_00 = 'T_16_00',
  T_16_30 = 'T_16_30',
  T_17_00 = 'T_17_00',
  T_17_30 = 'T_17_30',
  T_18_00 = 'T_18_00',
  T_18_30 = 'T_18_30',
  T_19_00 = 'T_19_00',
  T_19_30 = 'T_19_30',
  T_20_00 = 'T_20_00',
  T_20_30 = 'T_20_30',
  T_21_00 = 'T_21_00',
  T_21_30 = 'T_21_30',
  T_22_00 = 'T_22_00',
  T_22_30 = 'T_22_30',
  T_23_00 = 'T_23_00',
  T_23_30 = 'T_23_30',
}

export enum MaxSlot {
  T_00_00 = 'T_00_00',
  T_00_30 = 'T_00_30',
  T_01_00 = 'T_01_00',
  T_01_30 = 'T_01_30',
  T_02_00 = 'T_02_00',
  T_02_30 = 'T_02_30',
  T_03_00 = 'T_03_00',
  T_03_30 = 'T_03_30',
  T_04_00 = 'T_04_00',
  T_04_30 = 'T_04_30',
  T_05_00 = 'T_05_00',
  T_05_30 = 'T_05_30',
  T_06_00 = 'T_06_00',
  T_06_30 = 'T_06_30',
  T_07_00 = 'T_07_00',
  T_07_30 = 'T_07_30',
  T_08_00 = 'T_08_00',
  T_08_30 = 'T_08_30',
  T_09_00 = 'T_09_00',
  T_09_30 = 'T_09_30',
  T_10_00 = 'T_10_00',
  T_10_30 = 'T_10_30',
  T_11_00 = 'T_11_00',
  T_11_30 = 'T_11_30',
  T_12_00 = 'T_12_00',
  T_12_30 = 'T_12_30',
  T_13_00 = 'T_13_00',
  T_13_30 = 'T_13_30',
  T_14_00 = 'T_14_00',
  T_14_30 = 'T_14_30',
  T_15_00 = 'T_15_00',
  T_15_30 = 'T_15_30',
  T_16_00 = 'T_16_00',
  T_16_30 = 'T_16_30',
  T_17_00 = 'T_17_00',
  T_17_30 = 'T_17_30',
  T_18_00 = 'T_18_00',
  T_18_30 = 'T_18_30',
  T_19_00 = 'T_19_00',
  T_19_30 = 'T_19_30',
  T_20_00 = 'T_20_00',
  T_20_30 = 'T_20_30',
  T_21_00 = 'T_21_00',
  T_21_30 = 'T_21_30',
  T_22_00 = 'T_22_00',
  T_22_30 = 'T_22_30',
  T_23_00 = 'T_23_00',
  T_23_30 = 'T_23_30',
}

export enum MinSlot {
  T_00_00 = 'T_00_00',
  T_00_30 = 'T_00_30',
  T_01_00 = 'T_01_00',
  T_01_30 = 'T_01_30',
  T_02_00 = 'T_02_00',
  T_02_30 = 'T_02_30',
  T_03_00 = 'T_03_00',
  T_03_30 = 'T_03_30',
  T_04_00 = 'T_04_00',
  T_04_30 = 'T_04_30',
  T_05_00 = 'T_05_00',
  T_05_30 = 'T_05_30',
  T_06_00 = 'T_06_00',
  T_06_30 = 'T_06_30',
  T_07_00 = 'T_07_00',
  T_07_30 = 'T_07_30',
  T_08_00 = 'T_08_00',
  T_08_30 = 'T_08_30',
  T_09_00 = 'T_09_00',
  T_09_30 = 'T_09_30',
  T_10_00 = 'T_10_00',
  T_10_30 = 'T_10_30',
  T_11_00 = 'T_11_00',
  T_11_30 = 'T_11_30',
  T_12_00 = 'T_12_00',
  T_12_30 = 'T_12_30',
  T_13_00 = 'T_13_00',
  T_13_30 = 'T_13_30',
  T_14_00 = 'T_14_00',
  T_14_30 = 'T_14_30',
  T_15_00 = 'T_15_00',
  T_15_30 = 'T_15_30',
  T_16_00 = 'T_16_00',
  T_16_30 = 'T_16_30',
  T_17_00 = 'T_17_00',
  T_17_30 = 'T_17_30',
  T_18_00 = 'T_18_00',
  T_18_30 = 'T_18_30',
  T_19_00 = 'T_19_00',
  T_19_30 = 'T_19_30',
  T_20_00 = 'T_20_00',
  T_20_30 = 'T_20_30',
  T_21_00 = 'T_21_00',
  T_21_30 = 'T_21_30',
  T_22_00 = 'T_22_00',
  T_22_30 = 'T_22_30',
  T_23_00 = 'T_23_00',
  T_23_30 = 'T_23_30',
}

/**
 * A working setting for a day
 */
export interface IDaySetting {
  __typename: 'DaySetting';
  day: Day;
  setting: IWorkingSetting;
}

export enum Day {
  Friday = 'Friday',
  Monday = 'Monday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
  Thursday = 'Thursday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
}

export enum WorkingHoursTypeEnum {
  Base = 'Base',
  Meeting = 'Meeting',
  PreferredFocusTime = 'PreferredFocusTime',
}

/**
 * Meeting hours for the org
 */
export interface IOrgMeetingHours {
  __typename: 'OrgMeetingHours';
  hasBeenSet: boolean;

  /**
   * The ID of an object
   */
  id: string;
  orgId: string;
  workingHours: IWorkingHours;
}

/**
 * A connection to a list of items.
 */
export interface IUserMeetingHoursConnection {
  __typename: 'UserMeetingHoursConnection';

  /**
   * A list of edges.
   */
  edges: Array<IUserMeetingHoursEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IUserMeetingHoursEdge {
  __typename: 'UserMeetingHoursEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IUserMeetingHours;
}

/**
 * Meeting Hours for a given user id
 */
export interface IUserMeetingHours {
  __typename: 'UserMeetingHours';
  hasBeenSet: boolean;

  /**
   * The ID of an object
   */
  id: string;
  userId: string;
  workingHours: IWorkingHours;
}

export type DefragTimeErrorable = IDefragTime | IGraphEntityError;

/**
 * Represents the time in which a defrag will run
 */
export interface IDefragTime {
  __typename: 'DefragTime';
  millis: any;
}

export type EventListErrorable = IEventList | IGraphEntityError;

export interface IEventList {
  __typename: 'EventList';
  list: Array<IEvent>;
}

export type OptimisticSavingsErrorable = IGraphEntityError | IOptimisticSavings;

/**
 * Optimistic savings for a user, used to determine how much time can be saved in a week
 */
export interface IOptimisticSavings {
  __typename: 'OptimisticSavings';
  couldCalculate: boolean;
  savingMinutes: number;
}

export type FlowStateListErrorable = IFlowStateList | IGraphEntityError;

export interface IFlowStateList {
  __typename: 'FlowStateList';
  list: Array<IFlowState>;
}

export type OrgOverviewErrorable = IGraphEntityError | IOrgOverview;

/**
 * Overview of organization-wide Clockwise usage
 */
export interface IOrgOverview {
  __typename: 'OrgOverview';
  conflictsResolvedCount: Array<IIntegerDataPoint>;

  /**
   * False if the org doesn't have enough data to display yet
   */
  doNotShowOverview: boolean;
  focusTimeHoursCreatedCount: Array<IIntegerDataPoint>;
  numUsersByFunction: Array<IIntegerDataPoint>;
  numUsersByRole: Array<IIntegerDataPoint>;
  scheduleAssistCount: Array<IIntegerDataPoint>;
  scheduleAssistLimit: number;
  userCount: Array<IIntegerDataPoint>;
}

/**
 * Label and value pair
 */
export interface IIntegerDataPoint {
  __typename: 'IntegerDataPoint';
  label: string;
  value: number;
}

export enum ActivityFilter {
  CurrentUser = 'CurrentUser',
  CurrentUserOrInvited = 'CurrentUserOrInvited',
  Invited = 'Invited',
}

export enum SortDirection {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export enum SortType {
  FullName = 'FullName',
  Status = 'Status',
}

/**
 * A connection to a list of items.
 */
export interface IOrgInviteConnection {
  __typename: 'OrgInviteConnection';

  /**
   * A list of edges.
   */
  edges: Array<IOrgInviteEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IOrgInviteEdge {
  __typename: 'OrgInviteEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IOrgInvite;
}

/**
 * An invitation to join an org
 */
export interface IOrgInvite {
  __typename: 'OrgInvite';
  createdTime: number;

  /**
   * The ID of an object
   */
  id: string;
  lastInvitingUserId: string;
  lastSentTime: number;
  orgId: string;
  targetCalendarId: string;

  /**
   * @deprecated "Does not exist"
   */
  targetProfile: IUserProfile;
  used: boolean;
}

export type TeamErrorable = IGraphEntityError | ITeam;

/**
 * Your team and its members
 */
export interface ITeam {
  __typename: 'Team';
  createdBy: IUrn;

  /**
   * The ID of an object
   */
  id: string;
  invitedMembers: Array<ITeamMember>;
  settings: TeamSettingsErrorable;
  teamCalendarId: string;
  teamId: string;
  teamMembers: Array<ITeamMember>;
  teamName: string;
  teamRoleAndPersonIds: Array<ITeamRoleAndPersonId>;
  userSettings: TeamUserSettingsErrorable;
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface ITeamMember {
  __typename: 'TeamMember';
  addedBy: string;
  person: IOrgPerson;
  role: TeamMemberRole;
}

export enum TeamMemberRole {
  Admin = 'Admin',
  Member = 'Member',
}

export type TeamSettingsErrorable = IGraphEntityError | ITeamSettings;

/**
 * overall settings about a team
 */
export interface ITeamSettings {
  __typename: 'TeamSettings';

  /**
   * The ID of an object
   */
  id: string;
  lastUpdated: any;
  manageTeamCalendar: boolean;
  minimumMinutesForSyncing: number;
  noMeetingDayEmailOnScheduleOver: boolean;
  noMeetingDayOfWeek: NoMeetingDayOfWeek;
  slackSettings: ITeamSlackSettings;
  syncWFHEvents: boolean;
  useNoMeetingDay: boolean;
}

export enum NoMeetingDayOfWeek {
  Friday = 'Friday',
  Monday = 'Monday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
  Thursday = 'Thursday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface ITeamSlackSettings {
  __typename: 'TeamSlackSettings';
  channelId: string;
  channelName: string;
  notificationEnabled: boolean;
  timeSlot: TimeSlotEnum;
}

export enum TimeSlotEnum {
  T_00_00 = 'T_00_00',
  T_00_30 = 'T_00_30',
  T_01_00 = 'T_01_00',
  T_01_30 = 'T_01_30',
  T_02_00 = 'T_02_00',
  T_02_30 = 'T_02_30',
  T_03_00 = 'T_03_00',
  T_03_30 = 'T_03_30',
  T_04_00 = 'T_04_00',
  T_04_30 = 'T_04_30',
  T_05_00 = 'T_05_00',
  T_05_30 = 'T_05_30',
  T_06_00 = 'T_06_00',
  T_06_30 = 'T_06_30',
  T_07_00 = 'T_07_00',
  T_07_30 = 'T_07_30',
  T_08_00 = 'T_08_00',
  T_08_30 = 'T_08_30',
  T_09_00 = 'T_09_00',
  T_09_30 = 'T_09_30',
  T_10_00 = 'T_10_00',
  T_10_30 = 'T_10_30',
  T_11_00 = 'T_11_00',
  T_11_30 = 'T_11_30',
  T_12_00 = 'T_12_00',
  T_12_30 = 'T_12_30',
  T_13_00 = 'T_13_00',
  T_13_30 = 'T_13_30',
  T_14_00 = 'T_14_00',
  T_14_30 = 'T_14_30',
  T_15_00 = 'T_15_00',
  T_15_30 = 'T_15_30',
  T_16_00 = 'T_16_00',
  T_16_30 = 'T_16_30',
  T_17_00 = 'T_17_00',
  T_17_30 = 'T_17_30',
  T_18_00 = 'T_18_00',
  T_18_30 = 'T_18_30',
  T_19_00 = 'T_19_00',
  T_19_30 = 'T_19_30',
  T_20_00 = 'T_20_00',
  T_20_30 = 'T_20_30',
  T_21_00 = 'T_21_00',
  T_21_30 = 'T_21_30',
  T_22_00 = 'T_22_00',
  T_22_30 = 'T_22_30',
  T_23_00 = 'T_23_00',
  T_23_30 = 'T_23_30',
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface ITeamRoleAndPersonId {
  __typename: 'TeamRoleAndPersonId';
  personId: string;
  role: TeamMemberRole;
}

export type TeamUserSettingsErrorable = IGraphEntityError | ITeamUserSettings;

/**
 * individual user settings for a team
 */
export interface ITeamUserSettings {
  __typename: 'TeamUserSettings';

  /**
   * The ID of an object
   */
  id: string;
  lastUpdated: any;
  useNoMeetingDay: boolean;
  useTeamCalendar: boolean;
}

export type ProfileTimeZoneListErrorable =
  | IGraphEntityError
  | IProfileTimeZoneList;

export interface IProfileTimeZoneList {
  __typename: 'ProfileTimeZoneList';
  list: Array<IProfileTimeZone>;
}

/**
 * A time zone for a profile
 */
export interface IProfileTimeZone {
  __typename: 'ProfileTimeZone';
  calendarId: string;
  timeZone: string;
  userId: string;
}

export type SlackChannelListErrorable = IGraphEntityError | ISlackChannelList;

export interface ISlackChannelList {
  __typename: 'SlackChannelList';
  list: Array<ISlackChannel>;
}

/**
 * Slack channel
 */
export interface ISlackChannel {
  __typename: 'SlackChannel';
  channelId: string;
  name: string;
}

export type SchedulingResponseErrorable =
  | IGraphEntityError
  | ISchedulingResponse;

/**
 * Scheduling Response
 */
export interface ISchedulingResponse {
  __typename: 'SchedulingResponse';
  qualifiers: Array<SchedulingResponseQualifier>;
  queryRecommendation: IQueryRecommendation;
  slots: Array<ISchedulingSlot>;
  uncrawledCalendars: Array<string>;
}

export enum SchedulingResponseQualifier {
  NoConvenientTimesWithRooms = 'NoConvenientTimesWithRooms',
}

/**
 * Recommended date and/or duration of first available suggestion
 */
export interface IQueryRecommendation {
  __typename: 'QueryRecommendation';
  availableDate: string;
  minutesDuration: string;
}

export type RewindHistoryErrorable = IGraphEntityError | IRewindHistory;

export type RoomListErrorable = IGraphEntityError | IRoomList;

export interface IRoomList {
  __typename: 'RoomList';
  list: Array<IRoom>;
}

export interface ISchedulingQueryInput {
  includeConflictSuggestions?: boolean;
  includeRoom: boolean;
  invitees: Array<IInviteeContextInput>;
  limit: number;
  maxSlot: IDayTimeSlotInput;
  minSlot: IDayTimeSlotInput;
  minutesDuration: number;
  roomsOverride: Array<string>;
  seenSlots: Array<IDayTimeSlotInput>;
  timeZone: string;
  traits?: Array<EventTrait>;
}

export interface IInviteeContextInput {
  calendarId: string;
  required: boolean;
}

export enum EventTrait {
  Autopilot = 'Autopilot',
  IndividualHold = 'IndividualHold',
  LinkEvent = 'LinkEvent',
  LunchHold = 'LunchHold',
  OneOnOne = 'OneOnOne',
  TaskHold = 'TaskHold',
}

/**
 * A connection to a list of items.
 */
export interface ISettingConnection {
  __typename: 'SettingConnection';

  /**
   * A list of edges.
   */
  edges: Array<ISettingEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface ISettingEdge {
  __typename: 'SettingEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: ISetting;
}

/**
 * A setting
 */
export interface ISetting {
  __typename: 'Setting';

  /**
   * The ID of an object
   */
  id: string;
  key: ISettingKey;
  type: ISettingType;
  value: Array<string>;
}

/**
 * A setting key
 */
export interface ISettingKey {
  __typename: 'SettingKey';

  /**
   * The ID of an object
   */
  id: string;
  name: string;
  scope: IUrn;
  target: IUrn;
}

/**
 * A setting type
 */
export interface ISettingType {
  __typename: 'SettingType';
  containerType: ContainerType;

  /**
   * The ID of an object
   */
  id: string;
  required: boolean;
  valueType: ValueType;
}

export enum ContainerType {
  Multi = 'Multi',
  Single = 'Single',
}

export enum ValueType {
  Boolean = 'Boolean',
  Long = 'Long',
  Numeric = 'Numeric',
  String = 'String',
}

export type ShadowCalendarSettingsErrorable =
  | IGraphEntityError
  | IShadowCalendarSettings;

/**
 * A container of settings for the users shadow calendar
 */
export interface IShadowCalendarSettings {
  __typename: 'ShadowCalendarSettings';
  enabled: boolean;
  focusTimeExternalTransparency: FocusTimeExternalTransparency;
  focusTimeIdealMinutesPerWeek: number;
  focusTimeRemindersEnabled: boolean;
  focusTimeSyncAll: boolean;
  focusTimeSyncEnabled: boolean;

  /**
   * The ID of an object
   */
  id: string;
  lunchRemindersEnabled: boolean;
  lunchSyncEnabled: boolean;
  shadowCalendarId: string;
  travelTimeRemindersEnabled: boolean;
  travelTimeSyncEnabled: boolean;
}

export enum FocusTimeExternalTransparency {
  None = 'None',
  Opaque = 'Opaque',
  Transparent = 'Transparent',
}

export type SlackAccountErrorable = IGraphEntityError | ISlackAccount;

/**
 * Slack Account
 */
export interface ISlackAccount {
  __typename: 'SlackAccount';

  /**
   * The ID of an object
   */
  id: string;
  needsUpdate: boolean;
  slackTeamId: string;
  slackUserId: string;
  teamDisplayName: string;
}

export type SlackAccountListErrorable = IGraphEntityError | ISlackAccountList;

export interface ISlackAccountList {
  __typename: 'SlackAccountList';
  list: Array<ISlackAccount>;
}

export type SlackSettingsListErrorable = IGraphEntityError | ISlackSettingsList;

export interface ISlackSettingsList {
  __typename: 'SlackSettingsList';
  list: Array<ISlackSettings>;
}

/**
 * Slack calendar sync settings
 */
export interface ISlackSettings {
  __typename: 'SlackSettings';
  calendarStatus: ISlackCalendarStatusSync;

  /**
   * @deprecated "use calendarStatus"
   */
  calendarStatusEnabled: boolean;
  dailyDigestSettings: ISlackDailyDigestSettings;
  dndSettings: ISlackDndSettings;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * @deprecated "use calendarStatus"
   */
  includeMeetingCategory: boolean;

  /**
   * @deprecated "use calendarStatus"
   */
  includeMeetingTitle: boolean;
  notificationSettings: ISlackNotificationSettings;
  slackTeamId: string;
  slackUserId: string;
}

export interface ISlackCalendarStatusSync {
  __typename: 'SlackCalendarStatusSync';
  calendarStatusEnabled: boolean;
  includeMeetingCategory: boolean;
  includeMeetingTitle: boolean;
}

export interface ISlackDailyDigestSettings {
  __typename: 'SlackDailyDigestSettings';
  enabled: boolean;
  timeSlot: DigestTimeSlot;
}

export enum DigestTimeSlot {
  T_00_00 = 'T_00_00',
  T_00_30 = 'T_00_30',
  T_01_00 = 'T_01_00',
  T_01_30 = 'T_01_30',
  T_02_00 = 'T_02_00',
  T_02_30 = 'T_02_30',
  T_03_00 = 'T_03_00',
  T_03_30 = 'T_03_30',
  T_04_00 = 'T_04_00',
  T_04_30 = 'T_04_30',
  T_05_00 = 'T_05_00',
  T_05_30 = 'T_05_30',
  T_06_00 = 'T_06_00',
  T_06_30 = 'T_06_30',
  T_07_00 = 'T_07_00',
  T_07_30 = 'T_07_30',
  T_08_00 = 'T_08_00',
  T_08_30 = 'T_08_30',
  T_09_00 = 'T_09_00',
  T_09_30 = 'T_09_30',
  T_10_00 = 'T_10_00',
  T_10_30 = 'T_10_30',
  T_11_00 = 'T_11_00',
  T_11_30 = 'T_11_30',
  T_12_00 = 'T_12_00',
  T_12_30 = 'T_12_30',
  T_13_00 = 'T_13_00',
  T_13_30 = 'T_13_30',
  T_14_00 = 'T_14_00',
  T_14_30 = 'T_14_30',
  T_15_00 = 'T_15_00',
  T_15_30 = 'T_15_30',
  T_16_00 = 'T_16_00',
  T_16_30 = 'T_16_30',
  T_17_00 = 'T_17_00',
  T_17_30 = 'T_17_30',
  T_18_00 = 'T_18_00',
  T_18_30 = 'T_18_30',
  T_19_00 = 'T_19_00',
  T_19_30 = 'T_19_30',
  T_20_00 = 'T_20_00',
  T_20_30 = 'T_20_30',
  T_21_00 = 'T_21_00',
  T_21_30 = 'T_21_30',
  T_22_00 = 'T_22_00',
  T_22_30 = 'T_22_30',
  T_23_00 = 'T_23_00',
  T_23_30 = 'T_23_30',
}

export interface ISlackDndSettings {
  __typename: 'SlackDndSettings';
  focusTime: boolean;
  meeting: boolean;
}

export interface ISlackNotificationSettings {
  __typename: 'SlackNotificationSettings';
  defragSummary: boolean;
}

export type SmartHoldEventInfoErrorable =
  | IGraphEntityError
  | ISmartHoldEventInfo;

/**
 * Metadata about a Smart Hold event
 */
export interface ISmartHoldEventInfo {
  __typename: 'SmartHoldEventInfo';

  /**
   * The ID of an object
   */
  id: string;
  isMeetingPrep: boolean;
  isPostTravelTime: boolean;
  isPreTravelTime: boolean;
  isTeamCalendarSyncEvent: boolean;
  originEvent: IEvent;
}

export enum SsoIntent {
  Dsync = 'Dsync',
  Sso = 'Sso',
}

export type DefragConflictResolutionErrorable =
  | IDefragConflictResolution
  | IGraphEntityError;

/**
 * Defrag Conflict Resolution
 */
export interface IDefragConflictResolution {
  __typename: 'DefragConflictResolution';
  defragId: string;
  eventMoveSuggestions: Array<IEventMoveSuggestion>;
}

/**
 * The actual suggestion for moving an event within a defrag
 */
export interface IEventMoveSuggestion {
  __typename: 'EventMoveSuggestion';

  /**
   * The proposed minutes added to (or subtracted from) the base event
   */
  addedMinutes: number;

  /**
   * The unique key for the event for this suggestion
   */
  eventKey: IEventKey;

  /**
   * The ID of an object
   */
  id: string;
  meetingDurationInMinutes: number;

  /**
   * The original day time slot of the event
   */
  originalSlot: IDayTimeSlot;

  /**
   * The original time of the event
   */
  originalTime: IEventTime;

  /**
   * The proposed day time slot to move the event to
   */
  proposedSlot: IDayTimeSlot;

  /**
   * The proposed time to move the event to
   */
  proposedTime: IEventTime;

  /**
   * What type of EventMoveSuggestion
   */
  type: TypeEnum;
}

export enum TypeEnum {
  Add = 'Add',
  Move = 'Move',
  Remove = 'Remove',
  UndoMove = 'UndoMove',
}

/**
 * A connection to a list of items.
 */
export interface ISuggestedOrgInviteConnection {
  __typename: 'SuggestedOrgInviteConnection';

  /**
   * A list of edges.
   */
  edges: Array<ISuggestedOrgInviteEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface ISuggestedOrgInviteEdge {
  __typename: 'SuggestedOrgInviteEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: ISuggestedOrgInvite;
}

/**
 * A suggestion to invite someone to join an org
 */
export interface ISuggestedOrgInvite {
  __typename: 'SuggestedOrgInvite';

  /**
   * The ID of an object
   */
  id: string;
  inviteReason: SuggestedOrgInviteReason;
  numberOfOrganizedMeetings: number;
  numberOfSharedMeetings: number;
  orgId: string;
  resolution: SuggestedOrgInviteResolution;
  suggestedTime: number;
  targetCalendarId: string;
  targetProfile: IUserProfile;
  totalScore: number;
}

export enum SuggestedOrgInviteReason {
  AttendSameMeetings = 'AttendSameMeetings',
  MeetsWithYourTeammates = 'MeetsWithYourTeammates',
  OnYourTeam = 'OnYourTeam',
  OrganizedYourMeetings = 'OrganizedYourMeetings',
}

export enum SuggestedOrgInviteResolution {
  Accepted = 'Accepted',
  Dismissed = 'Dismissed',
}

export type SuggestedRoomPreferencesErrorable =
  | IGraphEntityError
  | ISuggestedRoomPreferences;

/**
 * The suggested room preferences for the user
 */
export interface ISuggestedRoomPreferences {
  __typename: 'SuggestedRoomPreferences';

  /**
   * The ID of an object
   */
  id: string;
  suggestedRooms: Array<ISuggestedRoomPreference>;
}

/**
 * A selected or suggested preferred room
 */
export interface ISuggestedRoomPreference {
  __typename: 'SuggestedRoomPreference';

  /**
   * Has the room been selected as a preferred room by the user
   */
  isPreferred: boolean;

  /**
   * Is the room suggested as a preferred room for the user
   */
  isSuggested: boolean;
  room: IRoom;
}

export type SuggestedTeamListErrorable = IGraphEntityError | ISuggestedTeamList;

export interface ISuggestedTeamList {
  __typename: 'SuggestedTeamList';
  list: Array<ISuggestedTeam>;
}

/**
 * A suggested team and its reasons
 */
export interface ISuggestedTeam {
  __typename: 'SuggestedTeam';
  reasons: Array<ISuggestedTeamReason>;
  score: number;
  team: ITeam;
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface ISuggestedTeamReason {
  __typename: 'SuggestedTeamReason';
  type: SuggestedTeamReasonType;
  value: number;
}

export enum SuggestedTeamReasonType {
  NumMeetingsWithOverlap = 'NumMeetingsWithOverlap',
  NumOneOnOnesWithOverlap = 'NumOneOnOnesWithOverlap',
}

/**
 * A summary of working preferences for a teammate
 */
export interface ISummarizedWorkingPreferences {
  __typename: 'SummarizedWorkingPreferences';
  busiestDay: Day;
  calendarId: string;
  mostBookedRoom: string;
  mostFocusTime: Day;
}

export interface IEventTimeInput {
  dateOrDateTime: string;
  millis: number;
  offsetMinutes?: number;
  timeZone?: string;
  type: EventTimeType;
}

export type TailoredDefragCardSummaryErrorable =
  | IGraphEntityError
  | ITailoredDefragCardSummary;

/**
 * A tailored set of defrag moves summarized for the user
 */
export interface ITailoredDefragCardSummary {
  __typename: 'TailoredDefragCardSummary';
  calendarValueStrings: Array<ITailoredDefragCalendarValueStrings>;
  defragTime: any;
  impactedOtherCalendarIds: Array<string>;
  knobs: IDefragValueKnobs;
  persons: PersonListErrorable;
  referencedEvents: Array<IEvent>;
  rooms: RoomListErrorable;
  valueStrings: Array<string>;
  yourEventMoves: Array<IDefragExplainedMove>;
}

export interface ITailoredDefragCalendarValueStrings {
  __typename: 'TailoredDefragCalendarValueStrings';
  calendarId: string;
  valueStrings: Array<string>;
}

/**
 * Defrag Value Knobs
 */
export interface IDefragValueKnobs {
  __typename: 'DefragValueKnobs';
  autopilotMeetingKnobs: Array<IAutopilotMeetingKnob>;
  calendarId: string;
  inviteOrganizerKnobs: Array<IInviteOrganizerKnob>;
  lessenAutopilotConstraintKnobs: Array<ILessenAutopilotConstraintKnob>;
}

/**
 * Affected Goal For Event
 */
export interface IAutopilotMeetingKnob {
  __typename: 'AutopilotMeetingKnob';
  affectedGoals: Array<AffectedGoals>;
  externalEventId: string;
  knobScore: number;
  knobUrn: IUrn;
}

export enum AffectedGoals {
  AttendMeeting = 'AttendMeeting',
  ConvenientMeetingTime = 'ConvenientMeetingTime',
  EmailTriage = 'EmailTriage',
  FocusTask = 'FocusTask',
  FocusTime = 'FocusTime',
  Lunch = 'Lunch',
  MeetingRelief = 'MeetingRelief',
  NoMeetingDay = 'NoMeetingDay',
  PersonalObligation = 'PersonalObligation',
  PreferredWork = 'PreferredWork',
  PrepTime = 'PrepTime',
  Task = 'Task',
  TravelTime = 'TravelTime',
  WithinMeetingHours = 'WithinMeetingHours',
  WithinWorkingHours = 'WithinWorkingHours',
}

/**
 * Inviter Organizer Knob
 */
export interface IInviteOrganizerKnob {
  __typename: 'InviteOrganizerKnob';
  affectedEventIds: Array<string>;
  affectedGoals: Array<AffectedGoals>;
  affectedGoalsForEvents: Array<IAffectedGoalForEvent>;
  knobScore: number;
  knobUrn: IUrn;
  suggestedInvitee: string;
  suggestedInviteePerson: PersonErrorable;
}

/**
 * The event and reasons why it is affected
 */
export interface IAffectedGoalForEvent {
  __typename: 'AffectedGoalForEvent';
  affectedGoals: Array<AffectedGoals>;
  externalEventId: string;
}

/**
 * Lessen Autopilot Constraint Knob
 */
export interface ILessenAutopilotConstraintKnob {
  __typename: 'LessenAutopilotConstraintKnob';
  affectedGoals: Array<AffectedGoals>;
  externalEventId: string;
  knobScore: number;
  knobUrn: IUrn;
}

/**
 * The explanation for a singular move within a defrag
 */
export interface IDefragExplainedMove {
  __typename: 'DefragExplainedMove';
  addedRooms: Array<string>;
  event: IEvent;
  moveReasons: Array<string>;
  newStartTime: IEventTime;
  originalStartTime: IEventTime;
  removedRooms: Array<string>;
}

export type CalendarStatusListErrorable =
  | ICalendarStatusList
  | IGraphEntityError;

export interface ICalendarStatusList {
  __typename: 'CalendarStatusList';
  list: Array<ICalendarStatus>;
}

export type TeamHealthScoreErrorable = IGraphEntityError | ITeamHealthScore;

/**
 * Team Health Score
 */
export interface ITeamHealthScore {
  __typename: 'TeamHealthScore';
  calendarFocusTimeScores: Array<ICalendarFocusTimeScore>;
  myFocusTimeScore: ICalendarFocusTimeScore;
  orgId: string;
  orgPersons: OrgPersonListErrorable;
  persons: PersonListErrorable;
  profileTimeZones: Array<IProfileTimeZone>;
}

export type TeamHealthScoreWithSubTeamsErrorable =
  | IGraphEntityError
  | ITeamHealthScoreWithSubTeams;

/**
 * Team Health Score With Sub Teams
 */
export interface ITeamHealthScoreWithSubTeams {
  __typename: 'TeamHealthScoreWithSubTeams';
  teamCalendars: Array<ITeamCalendarMap>;
  teamHealthScore: ITeamHealthScore;
}

export interface ITeamCalendarMap {
  __typename: 'teamCalendarMap';
  calendarIds: Array<string>;
  teamId: string;
  teamName: string;
}

export type TeamListErrorable = IGraphEntityError | ITeamList;

export interface ITeamList {
  __typename: 'TeamList';
  list: Array<ITeam>;
}

/**
 * The users team as defined by the user
 */
export interface IUserDefinedTeam {
  __typename: 'UserDefinedTeam';
  calendarIds: Array<string>;

  /**
   * The ID of an object
   */
  id: string;
}

/**
 * A connection to a list of items.
 */
export interface IGoalConnection {
  __typename: 'GoalConnection';

  /**
   * A list of edges.
   */
  edges: Array<IGoalEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IGoalEdge {
  __typename: 'GoalEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IGoal;
}

/**
 * A calendar's goal
 */
export interface IGoal {
  __typename: 'Goal';

  /**
   * The id of the goal, used internally by the services
   */
  goalId: string;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * Whether this goal is deleted or not
   */
  isDeleted: boolean;

  /**
   * The name associated with this goal
   */
  name: string;

  /**
   * Options associated with this goal
   */
  options: IGoalOptions;

  /**
   * Additional ids that this goal is associated with
   */
  otherUrns: Array<IUrn>;

  /**
   * The id of this goal's target, generally a calendar id scoped to an org
   */
  targetUrn: IUrn;

  /**
   * The id associated with an object used to represent this goal, e.g. an event id if the goal is represented by an event
   */
  valueUrn: IUrn;
}

/**
 * Options describing a goal
 */
export interface IGoalOptions {
  __typename: 'GoalOptions';
  specificOptions: GoalSpecificOptions;

  /**
   * If this goal is saved on the user's google calendar
   */
  syncToGoogle: boolean;
}

export type GoalSpecificOptions =
  | IGoalMeetingReliefOptions
  | IGoalPreferredWorkOptions
  | IGoalWithinDayOptions
  | IGoalWithinWeekOptions;

/**
 * Options for the preferred work option
 */
export interface IGoalMeetingReliefOptions {
  __typename: 'GoalMeetingReliefOptions';

  /**
   * The number of consecutive minutes of meetings that are allowed before a gap is requested
   */
  fatigueThresholdMinutes: number;

  /**
   * The number of minutes placed between consecutive meetings when fatigue threshold met
   */
  meetingReliefBreakMinutes: number;
}

/**
 * Options for the preferred work option
 */
export interface IGoalPreferredWorkOptions {
  __typename: 'GoalPreferredWorkOptions';

  /**
   * Whether to keep the end of a user's day open, or to schedule meetings until the end
   */
  keepEndOfDayOpen: boolean;

  /**
   * Whether to keep the start of a user's day open, or to schedule meetings at the start
   */
  keepStartOfDayOpen: boolean;

  /**
   * When the user prefers to work
   */
  preferredWorkType: PreferredWorkType;
}

export enum PreferredWorkType {
  Afternoon = 'Afternoon',
  Any = 'Any',
  Morning = 'Morning',
}

/**
 * Options for goals that move within a day
 */
export interface IGoalWithinDayOptions {
  __typename: 'GoalWithinDayOptions';

  /**
   * The ideal and usually maximum duration of the goal
   */
  idealDuration: number;

  /**
   * The maximum time slot in a day that the goal can be in
   */
  maxTimeSlot: TimeSlot;

  /**
   * The minimum duration of the goal
   */
  minimumDuration: number;

  /**
   * The minimum time slot in a day that the goal can be in
   */
  minTimeSlot: TimeSlot;
}

/**
 * Options for creating a goal that operates in the entire week context. e.g. focus time
 */
export interface IGoalWithinWeekOptions {
  __typename: 'GoalWithinWeekOptions';

  /**
   * The maximum duration in minutes of each individual block, where a block is typically an event on the calendar
   */
  maxMinutesPerBlock: number;
}

export type UserSurveyResponseListErrorable =
  | IGraphEntityError
  | IUserSurveyResponseList;

export interface IUserSurveyResponseList {
  __typename: 'UserSurveyResponseList';
  list: Array<IUserSurveyResponse>;
}

/**
 * Onboarding survey reponses from user
 */
export interface IUserSurveyResponse {
  __typename: 'UserSurveyResponse';
  freeformResponse: string;
  questionText: string;
  questionType: QuestionType;
  responseText: string;
  responseType: ResponseType;
}

export enum QuestionType {
  HelpWithFindingUninterruptedTimeToFocus = 'HelpWithFindingUninterruptedTimeToFocus',
  HelpWithSchedulingExternalMeetingsEffortlessly = 'HelpWithSchedulingExternalMeetingsEffortlessly',
  HelpWithSettingHealthyWorkLifeBoundaries = 'HelpWithSettingHealthyWorkLifeBoundaries',
  HelpWithSimplifyingSchedulingWithCoworkers = 'HelpWithSimplifyingSchedulingWithCoworkers',
  HelpWithSomethingElse = 'HelpWithSomethingElse',
  HowDidYouHearAboutClockwise = 'HowDidYouHearAboutClockwise',
  HowManyPeopleOnTeam = 'HowManyPeopleOnTeam',
  WhatIsYourCurrentPosition = 'WhatIsYourCurrentPosition',
  WhatTypeOfWorkDoYouDo = 'WhatTypeOfWorkDoYouDo',
}

export enum ResponseType {
  AccountingOrFinance = 'AccountingOrFinance',
  Administrative = 'Administrative',
  Between10And15 = 'Between10And15',
  Between15And20 = 'Between15And20',
  Between5And9 = 'Between5And9',
  BusinessOwner = 'BusinessOwner',
  Checked = 'Checked',
  CustomerSupport = 'CustomerSupport',
  DesignOrUxResearch = 'DesignOrUxResearch',
  DirectorOrDepartmentLeader = 'DirectorOrDepartmentLeader',
  ExecutiveOrBusinessLeader = 'ExecutiveOrBusinessLeader',
  FoundOnline = 'FoundOnline',
  FriendOrFamily = 'FriendOrFamily',
  IDoNotKnow = 'IDoNotKnow',
  IDoNotWorkOnAnyTeams = 'IDoNotWorkOnAnyTeams',
  IndividualContributor = 'IndividualContributor',
  InternetSearch = 'InternetSearch',
  It = 'It',
  Legal = 'Legal',
  LessThan5 = 'LessThan5',
  Manager = 'Manager',
  Marketing = 'Marketing',
  MoreThan20 = 'MoreThan20',
  MyMeetingsOnAutopilot = 'MyMeetingsOnAutopilot',
  OnlineAdvertisement = 'OnlineAdvertisement',
  Operations = 'Operations',
  Other = 'Other',
  PeopleOpsOrHr = 'PeopleOpsOrHr',
  ProductManagement = 'ProductManagement',
  ProgramOrProjectManagement = 'ProgramOrProjectManagement',
  Recruiting = 'Recruiting',
  SalesOrBusinessDevelopment = 'SalesOrBusinessDevelopment',
  SocialMedia = 'SocialMedia',
  SoftwareEngineering = 'SoftwareEngineering',
  TeammateAutopilot = 'TeammateAutopilot',
  TeammateCalendar = 'TeammateCalendar',
  TeammateFocusTime = 'TeammateFocusTime',
  TeammateInvited = 'TeammateInvited',
  TeammatePersonalCalendar = 'TeammatePersonalCalendar',
  TeammatePrepTime = 'TeammatePrepTime',
  TeammateSlackStatus = 'TeammateSlackStatus',
  TeammateTravelTime = 'TeammateTravelTime',
  Unchecked = 'Unchecked',
  VpOrDivisionLeader = 'VpOrDivisionLeader',
}

export type UserSurveyResponseWrapperErrorable =
  | IGraphEntityError
  | IUserSurveyResponseWrapper;

/**
 * A wrapper for UserSurveyResponses to give them an ID.
 */
export interface IUserSurveyResponseWrapper {
  __typename: 'UserSurveyResponseWrapper';

  /**
   * The ID of an object
   */
  id: string;
  userSurveyResponses: Array<IUserSurveyResponse>;
}

/**
 * Working Hours for a given user id
 */
export interface IUserWorkingHours {
  __typename: 'UserWorkingHours';
  hasBeenSet: boolean;

  /**
   * The ID of an object
   */
  id: string;
  userId: string;
  workingHours: IWorkingHours;
}

export type AddressErrorable = IAddress | IGraphEntityError;

export interface IAddress {
  __typename: 'Address';
  rawAddress: string;
}

/**
 * A connection to a list of items.
 */
export interface IWorkingGroupConnection {
  __typename: 'WorkingGroupConnection';

  /**
   * A list of edges.
   */
  edges: Array<IWorkingGroupEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IWorkingGroupEdge {
  __typename: 'WorkingGroupEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IWorkingGroup;
}

/**
 * a collection of calendarIds in a shared group
 */
export interface IWorkingGroup {
  __typename: 'WorkingGroup';

  /**
   * The people that make up the working group
   */
  calendarIds: Array<string>;

  /**
   * Calendars associated with this org
   */
  calendars: ICalendarConnection;

  /**
   * Calendars associated with this org
   */
  calendarWeeks: ICalendarWeekConnection;

  /**
   * The collection of event move suggestions to carry out a defrag
   */
  defragSuggestions: Array<IDefragSuggestions>;

  /**
   * An org's collection of events from all queried calendars and dates
   */
  eventParents: IEventParentConnection;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The name of the working group
   */
  name: string;

  /**
   * The org id this working group belongs to
   */
  orgId: string;

  /**
   * The list of rooms associated with this working group
   */
  rooms: Array<IRoom>;
}

export interface ICalendarsOnWorkingGroupArguments {
  after?: string;
  before?: string;
  calendarIds?: Array<string>;
  first?: number;
  last?: number;
  orgId?: string;
}

export interface ICalendarWeeksOnWorkingGroupArguments {
  after?: string;
  before?: string;
  calendarIds: Array<string>;
  first?: number;
  last?: number;
  startDate: any;
  timeZone: string;
}

export interface IDefragSuggestionsOnWorkingGroupArguments {
  /**
   * @default "None"
   */
  annotationRestriction?: AnnotationRestriction;
  organizerRestriction: OrganizerRestriction;
  saveResults?: boolean;
  startDate: string;
  timeZone: string;
  weeks?: number;
}

export interface IEventParentsOnWorkingGroupArguments {
  after?: string;
  before?: string;
  calendarIds?: Array<string>;
  dates?: Array<any>;
  eventParentRelayId?: string;
  first?: number;
  last?: number;

  /**
   * @default "America/Los_Angeles"
   */
  timeZone?: string;
}

/**
 * A connection to a list of items.
 */
export interface ICalendarConnection {
  __typename: 'CalendarConnection';

  /**
   * A list of edges.
   */
  edges: Array<ICalendarEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface ICalendarEdge {
  __typename: 'CalendarEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: ICalendar;
}

/**
 * A calendar belonging to someone or thing in the organization
 */
export interface ICalendar {
  __typename: 'Calendar';

  /**
   * The id of the calendar — usually an email address
   */
  calendarId: string;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The id of the organization this calendar belongs to
   */
  orgId: string;
}

/**
 * A connection to a list of items.
 */
export interface ICalendarWeekConnection {
  __typename: 'CalendarWeekConnection';

  /**
   * A list of edges.
   */
  edges: Array<ICalendarWeekEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface ICalendarWeekEdge {
  __typename: 'CalendarWeekEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: ICalendarWeek;
}

/**
 * A week of events for a given calendar id
 */
export interface ICalendarWeek {
  __typename: 'CalendarWeek';
  calendarId: string;
  calendarMeetingHours: ICalendarMeetingHours;
  eventParents: IEventParentConnection;

  /**
   * The ID of an object
   */
  id: string;
  orgId: string;
  startDate: any;
  timeZone: string;
  userId: string;
  userMeetingHours: IUserMeetingHours;
}

export interface IEventParentsOnCalendarWeekArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export enum AnnotationRestriction {
  None = 'None',
  RequiredAnnotation = 'RequiredAnnotation',
}

export enum OrganizerRestriction {
  None = 'None',
  OrganizersInOrg = 'OrganizersInOrg',
  UserOnly = 'UserOnly',
}

/**
 * A set of suggestions for moving events
 */
export interface IDefragSuggestions {
  __typename: 'DefragSuggestions';

  /**
   * New events created as part of this suggestion
   */
  addedEventParents: IEventParentConnection;

  /**
   * New events created as part of this pre suggestion
   */
  addedPreEventParents: IEventParentConnection;

  /**
   * The event query that was used to generate suggestions
   */
  eventsQuery: IEventsQuery;
  goalScoreDelta: ITeamGoalScores;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The pre suggestions for this defrag
   */
  preSuggestions: Array<IDefragSuggestion>;

  /**
   * The suggestions for this defrag
   */
  suggestions: Array<IDefragSuggestion>;
}

export interface IAddedEventParentsOnDefragSuggestionsArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface IAddedPreEventParentsOnDefragSuggestionsArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

export interface IPreSuggestionsOnDefragSuggestionsArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

/**
 * A query object for fetching events
 */
export interface IEventsQuery {
  __typename: 'EventsQuery';

  /**
   * The list of calendarIds used for the query
   */
  calendarIds: Array<string>;

  /**
   * The end time
   */
  endTime: IEventTime;

  /**
   * The ID of an object
   */
  id: string;

  /**
   * The start time
   */
  startTime: IEventTime;
}

/**
 * Scoring of goals for the team for a defrag
 */
export interface ITeamGoalScores {
  __typename: 'TeamGoalScores';
  aggregatedGoalScores: Array<IGoalScore>;
  calendarGoalScores: Array<ICalendarGoalScores>;
}

/**
 * Scoring fitness of a goal for a particular defrag
 */
export interface IGoalScore {
  __typename: 'GoalScore';
  after: number;
  before: number;
  goalId: string;
  units: UnitsEnum;
}

export enum UnitsEnum {
  DaysWith = 'DaysWith',
  Has = 'Has',
  HoursOf = 'HoursOf',
}

/**
 * A collection of Goal Scores for a calendarId
 */
export interface ICalendarGoalScores {
  __typename: 'CalendarGoalScores';
  calendarId: string;
  goalScores: Array<IGoalScore>;
}

/**
 * A suggestion for moving an individual event
 */
export interface IDefragSuggestion {
  __typename: 'DefragSuggestion';

  /**
   * The details of suggested event move
   */
  eventMoveSuggestion: IEventMoveSuggestion;
}

/**
 * Working Hours for a given user id
 */
export interface IWorkingHoursQueryResult {
  __typename: 'WorkingHoursQueryResult';
  userWorkingHours: IUserWorkingHoursConnection;
}

export interface IUserWorkingHoursOnWorkingHoursQueryResultArguments {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
}

/**
 * A connection to a list of items.
 */
export interface IUserWorkingHoursConnection {
  __typename: 'UserWorkingHoursConnection';

  /**
   * A list of edges.
   */
  edges: Array<IUserWorkingHoursEdge>;

  /**
   * Information to aid in pagination.
   */
  pageInfo: IPageInfo;
}

/**
 * An edge in a connection.
 */
export interface IUserWorkingHoursEdge {
  __typename: 'UserWorkingHoursEdge';

  /**
   * A cursor for use in pagination
   */
  cursor: string;

  /**
   * The item at the end of the edge
   */
  node: IUserWorkingHours;
}

export type ZoomAccountErrorable = IGraphEntityError | IZoomAccount;

/**
 * Zoom Account
 */
export interface IZoomAccount {
  __typename: 'ZoomAccount';
  accountId: string;
  connected: string;

  /**
   * Domain of the zoom account that’s been connected
   */
  displayName: string;

  /**
   * boolean whether the user has a valid zoom account for the connected zoom integration
   */
  hasValidZoomAccount: boolean;

  /**
   * The ID of an object
   */
  id: string;
  ownerPersons: PersonListErrorable;

  /**
   * userId of the person who connected the zoom account
   */
  ownerUserId: string;
}

/**
 * Details of a users scheduling link
 */
export interface ISchedulingLink {
  __typename: 'SchedulingLink';

  /**
   * A user's configurable id, like a username, for their scheduling links. Unique among all Clockwise users.
   */
  linkName: string;
  linkUrl: string;
  meetingSettings: IMeetingSettings;
}

export enum UserStatusEnum {
  Active = 'Active',
  Preview = 'Preview',
  Restricted = 'Restricted',
}

export interface IWaitlistedUser {
  __typename: 'WaitlistedUser';
  claimed: boolean;
  email: string;
  function: string;
  waitlistTime: any;
}

export type TeamQueryResultErrorable = IGraphEntityError | ITeamQueryResult;

/**
 * The result of a team query
 */
export interface ITeamQueryResult {
  __typename: 'TeamQueryResult';
  query: string;
  searchId: number;
  teamMembers: Array<ITeamMember>;
  teams: Array<ITeam>;
}

/**
 * A verification for a verification code including the external account that will be connected
 */
export interface IVerificationCodeResponse {
  __typename: 'VerificationCodeResponse';
  slackTeam: string;
  slackUsername: string;
  valid: boolean;
  verificationAttempted: boolean;
  verificationCode: string;
  verificationType: VerificationType;
}

export enum VerificationType {
  Slack = 'Slack',
}

export interface IMutation {
  __typename: 'Mutation';
  acceptDefaultOrgTags: IAcceptDefaultOrgTagsMutationPayload;
  addAdminForBillingGroup: IAddAdminForBillingGroupMutationPayload;
  addAndRemoveOrgTags: IAddAndRemoveOrgTagsMutationPayload;
  addManualMembersForBillingGroup: IAddManualMembersForBillingGroupMutationPayload;
  addOrgTags: IAddOrgTagsMutationPayload;
  addSlackAccount: IAddSlackAccountMutationPayload;
  addSlackAccountForTeam: IAddSlackAccountForTeamMutationPayload;
  addSlackAccountV2: IAddSlackAccountV2MutationPayload;
  addZoomAccount: IAddZoomAccountMutationPayload;
  batchAddAndRemoveOrgTags: IBatchAddAndRemoveOrgTagsMutationPayload;
  batchModifyAutopilotForEvents: IBatchModifyAutopilotForEventsMutationPayload;
  cancelBooking: ICancelBookingMutationPayload;
  createBooking: ICreateBookingMutationPayload;
  createDataSource: ICreateDataSourceMutationPayload;

  /**
   * Create a meeting setting, which represents a scheduling link.
   */
  createMeetingSettings: ICreateMeetingSettingsPayload;
  createOrgSubscriptionStripe: ICreateOrgSubscriptionStripePayload;
  createTeam: ICreateTeamMutationPayload;
  createTeamAndTeamSubscriptionStripe: ICreateTeamAndTeamSubscriptionStripePayload;
  createTeamCalendar: ICreateTeamCalendarMutationPayload;
  createTeamSubscriptionStripe: ICreateTeamSubscriptionStripePayload;
  deactivateUser: IDeactivateUserMutationPayload;

  /**
   * mutation to delete Asana credentials.
   */
  deleteAsanaCredentials: IDeleteAsanaCredentialsPayload;
  deleteDataSource: IDeleteDataSourcePayload;
  deleteEvent: IDeleteEventMutationPayload;

  /**
   * Delete a scheduling link's meeting settings.
   */
  deleteMeetingSettings: IDeleteMeetingSettingsPayload;
  deleteTeam: IDeleteTeamMutationPayload;
  deleteUserLevelZoomAccount: IDeleteUserLevelZoomAccountMutationPayload;
  deleteZoomAccount: IDeleteZoomAccountMutationPayload;
  disableDataSourceSync: IDisableDataSourceSyncPayload;
  enableDataSourceSync: IEnableDataSourceSyncPayload;
  enableOrDisableTeamSyncForEvent: IEnableOrDisableTeamSyncForEventPayload;
  executeCliCommand: IExecuteCliCommandMutationPayload;

  /**
   * Create a user's initial linkName.
   */
  findOrCreateLinkName: IFindOrCreateLinkNamePayload;
  installSlackApp: IInstallSlackAppMutationPayload;
  invitePersonsToTeam: IInvitePersonsToTeamPayload;
  inviteToBillingGroup: IInviteToBillingGroupMutationPayload;
  joinWaitlist: IJoinWaitlistMutationPayload;
  loginInsightsUser: ILoginInsightsUserPayload;
  loginInsightsUserOffline: ILoginInsightsUserOfflinePayload;
  loginMicrosoftUser: ILoginMicrosoftUserMutationPayload;
  loginUser: ILoginUserMutationPayload;
  logoutInsightsUser: ILogoutInsightsUserPayload;
  logoutUser: ILogoutUserMutationPayload;
  markDefragValueKnobsAsAccepted: IMarkDefragValueKnobsAsAcceptedMutationPayload;
  markDefragValueKnobsAsDismissed: IMarkDefragValueKnobsAsDismissedMutationPayload;
  markDefragValueKnobsAsPresented: IMarkDefragValueKnobsAsPresentedMutationPayload;
  markSeenFeedSuggestion: IMarkSeenFeedSuggestionMutationPayload;
  maybeOnboardOrg: IMaybeOnboardOrgMutationPayload;
  overrideSyncStatusForUserEvent: IOverrideSyncStatusForUserEventMutationPayload;
  patchBillingGroup: IPatchBillingGroupMutationPayload;
  patchSlackSettings: IPatchSlackSettingsMutationPayload;
  preAddOrgTags: IPreAddOrgTagsMutationPayload;
  recordChromeInstall: IRecordChromeInstallMutationPayload;
  recordChromeUninstall: IRecordChromeUninstallMutationPayload;
  recordDefragResponse: IRecordDefragResponseMutationPayload;
  recordImpression: IRecordImpressionMutationPayload;
  registerEmailUpdates: IRegisterEmailUpdatesPayload;
  removeAdminForBillingGroup: IRemoveAdminForBillingGroupMutationPayload;
  removeGoal: IRemoveGoalMutationPayload;
  removeLunchHold: IRemoveLunchHoldMutationPayload;
  removeManualMembersForBillingGroup: IRemoveManualMembersForBillingGroupMutationPayload;
  removeOrgTags: IRemoveOrgTagsMutationPayload;
  rescheduleBooking: IRescheduleBookingMutationPayload;
  rescheduleEventParent: IRescheduleEventParentMutationPayload;

  /**
   * mutation to set auth code recieved from Asana via redirect uri upon successful authentication.
   */
  saveAsanaCredentials: ISaveAsanaCredentialsPayload;
  saveTeamSettings: ISaveTeamSettingsMutationPayload;
  saveTeamUserSettings: ISaveTeamUserSettingsMutationPayload;
  saveUninstallResponse: ISaveUninstallResponseMutationPayload;
  scheduleNewEvent: IScheduleNewEventMutationPayload;
  scheduleNewEventV2: IScheduleNewEventV2MutationPayload;
  scheduleNewSmartHold: IScheduleNewSmartHoldMutationPayload;
  sendBillingGroupRequestEmail: ISendBillingGroupRequestEmailMutationPayload;
  sendCalAuditInviteEmail: ISendCalAuditInviteEmailPayload;
  setPreferredRooms: ISetPreferredRoomsMutationPayload;
  setPrimaryTeam: ISetPrimaryTeamMutationPayload;
  setWorkAddressForUser: ISetWorkAddressForUserMutationPayload;
  submitHubspotForm: ISubmitHubspotFormMutationPayload;
  sudoDeactivateUser: ISudoDeactivateUserMutationPayload;
  sudoDeleteEverything: ISudoDeleteEverythingMutationPayload;
  sudoModifyUser: ISudoModifyUserMutationPayload;
  sudoResetFlowState: ISudoResetFlowStateMutationPayload;
  sudoSwitchUser: ISudoSwitchUserMutationPayload;
  sudoTriggerClientUpdate: ISudoTriggerClientUpdateMutationPayload;
  sudoUpdateWaitlist: ISudoUpdateWaitlistMutationPayload;
  sudoUpdateWhitelistedDomains: ISudoUpdateWhitelistedDomainsMutationPayload;
  sudoUpdateWhitelistedEmails: ISudoUpdateWhitelistedEmailsMutationPayload;
  toggleAutoSyncForBillingGroup: IToggleAutoSyncForBillingGroupMutationPayload;
  triggerDefragRun: ITriggerDefragRunMutationPayload;
  unpauseEvent: IUnpauseEventMutationPayload;
  unsubscribeEmail: IUnsubscribeEmailPayload;
  unsyncFocusTimeBlock: IUnsyncFocusTimeBlockMutationPayload;
  unsyncFocusTimeEvent: IUnsyncFocusTimeEventMutationPayload;
  updateDeviceToken: IUpdateDeviceTokenPayload;
  updateEventColoringSettings: IEventColoringSettingsMutationsPayload;
  updateEventResponseStatus: IUpdateEventResponseStatusMutationPayload;
  updateFlagsForUser: IUpdateFlagsForUserMutationPayload;
  updateFlowStateUser: IUpdateUserFlowMutationPayload;
  updateFlowStateUserOrg: IUpdateUserOrgFlowMutationPayload;

  /**
   * Update whether a Clockwise Link is marked active or inactive
   */
  updateLinkActive: IUpdateLinkActivePayload;

  /**
   * Update a meeting setting, which represents a scheduling link.
   */
  updateMeetingSettings: IUpdateMeetingSettingsPayload;
  updateOrgInvite: IUpdateOrgInvitePayload;
  updateSetting: IUpdateSettingMutationPayload;
  updateSettings: IUpdateSettingsMutationPayload;
  updateShadowCalendarSettings: IUpdateShadowCalendarSettingsMutationPayload;
  updateSlackAccount: IUpdateSlackAccountMutationPayload;
  updateSlackAccountForTeam: IUpdateSlackAccountForTeamMutationPayload;
  updateSmartHoldSyncSettings: IUpdateSmartHoldSyncSettingsMutationPayload;
  updateSuggestedOrgInvite: IUpdateSuggestedOrgInvitePayload;
  updateTeam: IUpdateTeamMutationPayload;
  updateToggleEventColoringSettings: IToggleEventColoringSettingsMutationsPayload;
  updateUserDefinedTeam: IUpdateUserDefinedTeamMutationPayload;

  /**
   * Update the user's shortname that is used for generating Clockwise Link URLs.
   */
  updateUserShortname: IUpdateUserShortnamePayload;
  updateWorkingHours: IUpdateWorkingHoursMutationPayload;
  upsertEmailTriage: IUpsertEmailTriageMutationPayload;
  upsertFocusTime: IUpsertFocusTimeMutationPayload;
  upsertLunch: IUpsertLunchMutationPayload;
  upsertMeetingRelief: IUpsertMeetingReliefMutationPayload;
  upsertNonUserDefragEmailResponse: IUpsertNonUserDefragEmailResponsePayload;
  upsertPreferredWork: IUpsertPreferredWorkMutationPayload;
  upsertTravelTime: IUpsertTravelTimeMutationPayload;
  userSurveyResponseMutations: ISaveUserSurveyResponsesMutationPayload;
  userSurveyResponseWrapperMutations: ISaveUserSurveyResponseWrapperMutationPayload;
  validateSlackChannelBySendingMessage: IValidateSlackChannelBySendingMessageMutationPayload;
  weeklySummaryResponse: IWeeklySummaryResponseMutationPayload;
}

export interface IAcceptDefaultOrgTagsOnMutationArguments {
  input: IAcceptDefaultOrgTagsMutationInput;
}

export interface IAddAdminForBillingGroupOnMutationArguments {
  input: IAddAdminForBillingGroupMutationInput;
}

export interface IAddAndRemoveOrgTagsOnMutationArguments {
  input: IAddAndRemoveOrgTagsMutationInput;
}

export interface IAddManualMembersForBillingGroupOnMutationArguments {
  input: IAddManualMembersForBillingGroupMutationInput;
}

export interface IAddOrgTagsOnMutationArguments {
  input: IAddOrgTagsMutationInput;
}

export interface IAddSlackAccountOnMutationArguments {
  input: IAddSlackAccountMutationInput;
}

export interface IAddSlackAccountForTeamOnMutationArguments {
  input: IAddSlackAccountForTeamMutationInput;
}

export interface IAddSlackAccountV2OnMutationArguments {
  input: IAddSlackAccountV2MutationInput;
}

export interface IAddZoomAccountOnMutationArguments {
  input: IAddZoomAccountMutationInput;
}

export interface IBatchAddAndRemoveOrgTagsOnMutationArguments {
  input: IBatchAddAndRemoveOrgTagsMutationInput;
}

export interface IBatchModifyAutopilotForEventsOnMutationArguments {
  input: IBatchModifyAutopilotForEventsMutationInput;
}

export interface ICancelBookingOnMutationArguments {
  input: ICancelBookingMutationInput;
}

export interface ICreateBookingOnMutationArguments {
  input: ICreateBookingMutationInput;
}

export interface ICreateDataSourceOnMutationArguments {
  input: ICreateDataSourceMutationInput;
}

export interface ICreateMeetingSettingsOnMutationArguments {
  input: ICreateMeetingSettingsInput;
}

export interface ICreateOrgSubscriptionStripeOnMutationArguments {
  input: ICreateOrgSubscriptionStripeInput;
}

export interface ICreateTeamOnMutationArguments {
  input: ICreateTeamMutationInput;
}

export interface ICreateTeamAndTeamSubscriptionStripeOnMutationArguments {
  input: ICreateTeamAndTeamSubscriptionStripeInput;
}

export interface ICreateTeamCalendarOnMutationArguments {
  input: ICreateTeamCalendarMutationInput;
}

export interface ICreateTeamSubscriptionStripeOnMutationArguments {
  input: ICreateTeamSubscriptionStripeInput;
}

export interface IDeactivateUserOnMutationArguments {
  input: IDeactivateUserMutationInput;
}

export interface IDeleteAsanaCredentialsOnMutationArguments {
  input: IDeleteAsanaCredentialsInput;
}

export interface IDeleteDataSourceOnMutationArguments {
  input: IDeleteDataSourceInput;
}

export interface IDeleteEventOnMutationArguments {
  input: IDeleteEventMutationInput;
}

export interface IDeleteMeetingSettingsOnMutationArguments {
  input: IDeleteMeetingSettingsInput;
}

export interface IDeleteTeamOnMutationArguments {
  input: IDeleteTeamMutationInput;
}

export interface IDeleteUserLevelZoomAccountOnMutationArguments {
  input: IDeleteUserLevelZoomAccountMutationInput;
}

export interface IDeleteZoomAccountOnMutationArguments {
  input: IDeleteZoomAccountMutationInput;
}

export interface IDisableDataSourceSyncOnMutationArguments {
  input: IDisableDataSourceSyncInput;
}

export interface IEnableDataSourceSyncOnMutationArguments {
  input: IEnableDataSourceSyncInput;
}

export interface IEnableOrDisableTeamSyncForEventOnMutationArguments {
  input: IEnableOrDisableTeamSyncForEventInput;
}

export interface IExecuteCliCommandOnMutationArguments {
  input: IExecuteCliCommandMutationInput;
}

export interface IFindOrCreateLinkNameOnMutationArguments {
  input: IFindOrCreateLinkNameInput;
}

export interface IInstallSlackAppOnMutationArguments {
  input: IInstallSlackAppMutationInput;
}

export interface IInvitePersonsToTeamOnMutationArguments {
  input: IInvitePersonsToTeamInput;
}

export interface IInviteToBillingGroupOnMutationArguments {
  input: IInviteToBillingGroupMutationInput;
}

export interface IJoinWaitlistOnMutationArguments {
  input: IJoinWaitlistMutationInput;
}

export interface ILoginInsightsUserOnMutationArguments {
  input: ILoginInsightsUserInput;
}

export interface ILoginInsightsUserOfflineOnMutationArguments {
  input: ILoginInsightsUserOfflineInput;
}

export interface ILoginMicrosoftUserOnMutationArguments {
  input: ILoginMicrosoftUserMutationInput;
}

export interface ILoginUserOnMutationArguments {
  input: ILoginUserMutationInput;
}

export interface ILogoutInsightsUserOnMutationArguments {
  input: ILogoutInsightsUserInput;
}

export interface ILogoutUserOnMutationArguments {
  input: ILogoutUserMutationInput;
}

export interface IMarkDefragValueKnobsAsAcceptedOnMutationArguments {
  input: IMarkDefragValueKnobsAsAcceptedMutationInput;
}

export interface IMarkDefragValueKnobsAsDismissedOnMutationArguments {
  input: IMarkDefragValueKnobsAsDismissedMutationInput;
}

export interface IMarkDefragValueKnobsAsPresentedOnMutationArguments {
  input: IMarkDefragValueKnobsAsPresentedMutationInput;
}

export interface IMarkSeenFeedSuggestionOnMutationArguments {
  input: IMarkSeenFeedSuggestionMutationInput;
}

export interface IMaybeOnboardOrgOnMutationArguments {
  input: IMaybeOnboardOrgMutationInput;
}

export interface IOverrideSyncStatusForUserEventOnMutationArguments {
  input: IOverrideSyncStatusForUserEventMutationInput;
}

export interface IPatchBillingGroupOnMutationArguments {
  input: IPatchBillingGroupMutationInput;
}

export interface IPatchSlackSettingsOnMutationArguments {
  input: IPatchSlackSettingsMutationInput;
}

export interface IPreAddOrgTagsOnMutationArguments {
  input: IPreAddOrgTagsMutationInput;
}

export interface IRecordChromeInstallOnMutationArguments {
  input: IRecordChromeInstallMutationInput;
}

export interface IRecordChromeUninstallOnMutationArguments {
  input: IRecordChromeUninstallMutationInput;
}

export interface IRecordDefragResponseOnMutationArguments {
  input: IRecordDefragResponseMutationInput;
}

export interface IRecordImpressionOnMutationArguments {
  input: IRecordImpressionMutationInput;
}

export interface IRegisterEmailUpdatesOnMutationArguments {
  input: IRegisterEmailUpdatesInput;
}

export interface IRemoveAdminForBillingGroupOnMutationArguments {
  input: IRemoveAdminForBillingGroupMutationInput;
}

export interface IRemoveGoalOnMutationArguments {
  input: IRemoveGoalMutationInput;
}

export interface IRemoveLunchHoldOnMutationArguments {
  input: IRemoveLunchHoldMutationInput;
}

export interface IRemoveManualMembersForBillingGroupOnMutationArguments {
  input: IRemoveManualMembersForBillingGroupMutationInput;
}

export interface IRemoveOrgTagsOnMutationArguments {
  input: IRemoveOrgTagsMutationInput;
}

export interface IRescheduleBookingOnMutationArguments {
  input: IRescheduleBookingMutationInput;
}

export interface IRescheduleEventParentOnMutationArguments {
  input: IRescheduleEventParentMutationInput;
}

export interface ISaveAsanaCredentialsOnMutationArguments {
  input: ISaveAsanaCredentialsInput;
}

export interface ISaveTeamSettingsOnMutationArguments {
  input: ISaveTeamSettingsMutationInput;
}

export interface ISaveTeamUserSettingsOnMutationArguments {
  input: ISaveTeamUserSettingsMutationInput;
}

export interface ISaveUninstallResponseOnMutationArguments {
  input: ISaveUninstallResponseMutationInput;
}

export interface IScheduleNewEventOnMutationArguments {
  input: IScheduleNewEventMutationInput;
}

export interface IScheduleNewEventV2OnMutationArguments {
  input: IScheduleNewEventV2MutationInput;
}

export interface IScheduleNewSmartHoldOnMutationArguments {
  input: IScheduleNewSmartHoldMutationInput;
}

export interface ISendBillingGroupRequestEmailOnMutationArguments {
  input: ISendBillingGroupRequestEmailMutationInput;
}

export interface ISendCalAuditInviteEmailOnMutationArguments {
  input: ISendCalAuditInviteEmailInput;
}

export interface ISetPreferredRoomsOnMutationArguments {
  input: ISetPreferredRoomsMutationInput;
}

export interface ISetPrimaryTeamOnMutationArguments {
  input: ISetPrimaryTeamMutationInput;
}

export interface ISetWorkAddressForUserOnMutationArguments {
  input: ISetWorkAddressForUserMutationInput;
}

export interface ISubmitHubspotFormOnMutationArguments {
  input: ISubmitHubspotFormMutationInput;
}

export interface ISudoDeactivateUserOnMutationArguments {
  input: ISudoDeactivateUserMutationInput;
}

export interface ISudoDeleteEverythingOnMutationArguments {
  input: ISudoDeleteEverythingMutationInput;
}

export interface ISudoModifyUserOnMutationArguments {
  input: ISudoModifyUserMutationInput;
}

export interface ISudoResetFlowStateOnMutationArguments {
  input: ISudoResetFlowStateMutationInput;
}

export interface ISudoSwitchUserOnMutationArguments {
  input: ISudoSwitchUserMutationInput;
}

export interface ISudoTriggerClientUpdateOnMutationArguments {
  input: ISudoTriggerClientUpdateMutationInput;
}

export interface ISudoUpdateWaitlistOnMutationArguments {
  input: ISudoUpdateWaitlistMutationInput;
}

export interface ISudoUpdateWhitelistedDomainsOnMutationArguments {
  input: ISudoUpdateWhitelistedDomainsMutationInput;
}

export interface ISudoUpdateWhitelistedEmailsOnMutationArguments {
  input: ISudoUpdateWhitelistedEmailsMutationInput;
}

export interface IToggleAutoSyncForBillingGroupOnMutationArguments {
  input: IToggleAutoSyncForBillingGroupMutationInput;
}

export interface ITriggerDefragRunOnMutationArguments {
  input: ITriggerDefragRunMutationInput;
}

export interface IUnpauseEventOnMutationArguments {
  input: IUnpauseEventMutationInput;
}

export interface IUnsubscribeEmailOnMutationArguments {
  input: IUnsubscribeEmailInput;
}

export interface IUnsyncFocusTimeBlockOnMutationArguments {
  input: IUnsyncFocusTimeBlockMutationInput;
}

export interface IUnsyncFocusTimeEventOnMutationArguments {
  input: IUnsyncFocusTimeEventMutationInput;
}

export interface IUpdateDeviceTokenOnMutationArguments {
  input: IUpdateDeviceTokenInput;
}

export interface IUpdateEventColoringSettingsOnMutationArguments {
  input: IEventColoringSettingsMutationsInput;
}

export interface IUpdateEventResponseStatusOnMutationArguments {
  input: IUpdateEventResponseStatusMutationInput;
}

export interface IUpdateFlagsForUserOnMutationArguments {
  input: IUpdateFlagsForUserMutationInput;
}

export interface IUpdateFlowStateUserOnMutationArguments {
  input: IUpdateUserFlowMutationInput;
}

export interface IUpdateFlowStateUserOrgOnMutationArguments {
  input: IUpdateUserOrgFlowMutationInput;
}

export interface IUpdateLinkActiveOnMutationArguments {
  input: IUpdateLinkActiveInput;
}

export interface IUpdateMeetingSettingsOnMutationArguments {
  input: IUpdateMeetingSettingsInput;
}

export interface IUpdateOrgInviteOnMutationArguments {
  input: IUpdateOrgInviteInput;
}

export interface IUpdateSettingOnMutationArguments {
  input: IUpdateSettingMutationInput;
}

export interface IUpdateSettingsOnMutationArguments {
  input: IUpdateSettingsMutationInput;
}

export interface IUpdateShadowCalendarSettingsOnMutationArguments {
  input: IUpdateShadowCalendarSettingsMutationInput;
}

export interface IUpdateSlackAccountOnMutationArguments {
  input: IUpdateSlackAccountMutationInput;
}

export interface IUpdateSlackAccountForTeamOnMutationArguments {
  input: IUpdateSlackAccountForTeamMutationInput;
}

export interface IUpdateSmartHoldSyncSettingsOnMutationArguments {
  input: IUpdateSmartHoldSyncSettingsMutationInput;
}

export interface IUpdateSuggestedOrgInviteOnMutationArguments {
  input: IUpdateSuggestedOrgInviteInput;
}

export interface IUpdateTeamOnMutationArguments {
  input: IUpdateTeamMutationInput;
}

export interface IUpdateToggleEventColoringSettingsOnMutationArguments {
  input: IToggleEventColoringSettingsMutationsInput;
}

export interface IUpdateUserDefinedTeamOnMutationArguments {
  input: IUpdateUserDefinedTeamMutationInput;
}

export interface IUpdateUserShortnameOnMutationArguments {
  input: IUpdateUserShortnameInput;
}

export interface IUpdateWorkingHoursOnMutationArguments {
  input: IUpdateWorkingHoursMutationInput;
}

export interface IUpsertEmailTriageOnMutationArguments {
  input: IUpsertEmailTriageMutationInput;
}

export interface IUpsertFocusTimeOnMutationArguments {
  input: IUpsertFocusTimeMutationInput;
}

export interface IUpsertLunchOnMutationArguments {
  input: IUpsertLunchMutationInput;
}

export interface IUpsertMeetingReliefOnMutationArguments {
  input: IUpsertMeetingReliefMutationInput;
}

export interface IUpsertNonUserDefragEmailResponseOnMutationArguments {
  input: IUpsertNonUserDefragEmailResponseInput;
}

export interface IUpsertPreferredWorkOnMutationArguments {
  input: IUpsertPreferredWorkMutationInput;
}

export interface IUpsertTravelTimeOnMutationArguments {
  input: IUpsertTravelTimeMutationInput;
}

export interface IUserSurveyResponseMutationsOnMutationArguments {
  input: ISaveUserSurveyResponsesMutationInput;
}

export interface IUserSurveyResponseWrapperMutationsOnMutationArguments {
  input: ISaveUserSurveyResponseWrapperMutationInput;
}

export interface IValidateSlackChannelBySendingMessageOnMutationArguments {
  input: IValidateSlackChannelBySendingMessageMutationInput;
}

export interface IWeeklySummaryResponseOnMutationArguments {
  input: IWeeklySummaryResponseMutationInput;
}

export interface IAcceptDefaultOrgTagsMutationInput {
  clientMutationId?: string;
  suggestedAnnotationRelayId: string;
}

export interface IAcceptDefaultOrgTagsMutationPayload {
  __typename: 'AcceptDefaultOrgTagsMutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface IAddAdminForBillingGroupMutationInput {
  billingGroupId: string;
  clientMutationId?: string;
  orgRelayId: string;
  userId: string;
}

export interface IAddAdminForBillingGroupMutationPayload {
  __typename: 'AddAdminForBillingGroupMutationPayload';
  clientMutationId: string;
}

export interface IAddAndRemoveOrgTagsMutationInput {
  calendarId?: string;
  clientMutationId?: string;
  eventParentRelayId: string;
  moveRangeV3?: IMoveRangeInLocalTimeV3Input;
  orgRelayId: string;
  tagNamesToRemove: Array<string>;
  tagsToAdd?: Array<ITagInput>;
}

export interface IMoveRangeInLocalTimeV3Input {
  allowedEndTimeInclusive?: IDayOffsetTimeSlotInput;
  allowedStartTimeInclusive?: IDayOffsetTimeSlotInput;
}

export interface IDayOffsetTimeSlotInput {
  dayOffset: number;
  timeSlot: TimeSlot;
}

export interface ITagInput {
  name: string;
  value?: string;
}

export interface IAddAndRemoveOrgTagsMutationPayload {
  __typename: 'AddAndRemoveOrgTagsMutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface IAddManualMembersForBillingGroupMutationInput {
  addExclusions?: boolean;
  billingGroupId: string;
  clientMutationId?: string;
  orgRelayId: string;
  personIds: Array<string>;
}

export interface IAddManualMembersForBillingGroupMutationPayload {
  __typename: 'AddManualMembersForBillingGroupMutationPayload';
  clientMutationId: string;
}

export interface IAddOrgTagsMutationInput {
  calendarId?: string;
  clientMutationId?: string;
  debuggingReason?: string;
  eventParentRelayId: string;
  moveRangeV3?: IMoveRangeInLocalTimeV3Input;
  orgRelayId: string;
  tags?: Array<ITagInput>;
}

export interface IAddOrgTagsMutationPayload {
  __typename: 'AddOrgTagsMutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface IAddSlackAccountMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  redirect: string;
  verificationCode: string;
}

export interface IAddSlackAccountMutationPayload {
  __typename: 'AddSlackAccountMutationPayload';
  clientMutationId: string;
}

export interface IAddSlackAccountForTeamMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  redirect: string;
  teamId: string;
  verificationCode: string;
}

export interface IAddSlackAccountForTeamMutationPayload {
  __typename: 'AddSlackAccountForTeamMutationPayload';
  clientMutationId: string;
}

export interface IAddSlackAccountV2MutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  redirect: string;
  verificationCode: string;
}

export interface IAddSlackAccountV2MutationPayload {
  __typename: 'AddSlackAccountV2MutationPayload';
  clientMutationId: string;
}

export interface IAddZoomAccountMutationInput {
  clientMutationId?: string;

  /**
   * @default false
   */
  isUserLevelZoomAccount?: boolean;
  orgRelayId: string;
  redirect: string;
  verificationCode: string;
}

export interface IAddZoomAccountMutationPayload {
  __typename: 'AddZoomAccountMutationPayload';
  clientMutationId: string;
}

export interface IBatchAddAndRemoveOrgTagsMutationInput {
  clientMutationId?: string;
  tagPairings?: Array<IBatchAddAndRemoveOrgTagsTagPairings>;
}

export interface IBatchAddAndRemoveOrgTagsTagPairings {
  calendarId?: string;
  eventParentRelayId: string;
  moveRangeV3?: IMoveRangeInLocalTimeV3Input;
  orgRelayId: string;
  tagNamesToRemove: Array<string>;
  tagsToAdd?: Array<ITagInput>;
}

export interface IBatchAddAndRemoveOrgTagsMutationPayload {
  __typename: 'BatchAddAndRemoveOrgTagsMutationPayload';
  clientMutationId: string;
  eventParents: Array<IEventParent>;
}

export interface IBatchModifyAutopilotForEventsMutationInput {
  clientMutationId?: string;
  events?: Array<IAutopilotEventToModify>;
  orgRelayId: string;
}

/**
 * Events to modify
 */
export interface IAutopilotEventToModify {
  externalEventId: string;
  movementType: ModifyAutopilotForEventMovementTypeEnum;
}

export enum ModifyAutopilotForEventMovementTypeEnum {
  Day = 'Day',
  None = 'None',
  Week = 'Week',
}

export interface IBatchModifyAutopilotForEventsMutationPayload {
  __typename: 'BatchModifyAutopilotForEventsMutationPayload';
  clientMutationId: string;
}

export interface ICancelBookingMutationInput {
  /**
   * Id of booking.
   */
  bookingId: string;
  clientMutationId?: string;

  /**
   * Optional reason for cancellation.
   */
  reason?: string;
}

export interface ICancelBookingMutationPayload {
  __typename: 'CancelBookingMutationPayload';
  clientMutationId: string;
}

export interface ICreateBookingMutationInput {
  body: ICreateBookingBody;
  clientMutationId?: string;

  /**
   * fetchSchedulingLinkDetails formatted request to refetch the date of booking to ensure slot is still available.
   */
  dateRefetchBody?: IZonedDayTimeSlotRangeInput;

  /**
   * A user's configurable id, like a username, for their scheduling links. Unique among all Clockwise users.
   */
  linkName: string;
  slug: string;
}

export interface ICreateBookingBody {
  attendeeEmailAddress: string;
  attendeeName: string;
  eventStartMillis: any;
  notes?: string;
  timeZone: string;
}

export interface ICreateBookingMutationPayload {
  __typename: 'CreateBookingMutationPayload';
  bookedDateOrDateTime: string;
  bookedTimeZone: string;
  clientMutationId: string;
}

export interface ICreateDataSourceMutationInput {
  clientMutationId?: string;
  googleOfflineCode: string;

  /**
   * @default false
   */
  isPersonal?: boolean;

  /**
   * The front-end ID of the organization the work calendar is part of
   */
  orgRelayId: string;
}

export interface ICreateDataSourceMutationPayload {
  __typename: 'CreateDataSourceMutationPayload';
  clientMutationId: string;

  /**
   * The new data source that was just created
   */
  dataSource: IDataSource;
  dataSourceAuthError: DataSourceAuthError;
  user: IUser;
}

export enum DataSourceAuthError {
  GenericError = 'GenericError',
  SameDomainError = 'SameDomainError',
}

export interface ICreateMeetingSettingsInput {
  body?: IMeetingSettingsInput;
  clientMutationId?: string;
}

export interface IMeetingSettingsInput {
  allowFifteenMinuteMarkBooking: boolean;
  availabilityType: AvailabilityType;
  bookableHours: BookableHoursEnum;
  bookingTimeBufferHours: number;
  bookOnNoMeetingDay: boolean;
  description?: string;
  highlightBestTimes: boolean;
  id?: string;
  isActive: boolean;
  locationSettings: ILocationSettingsInput;
  name: string;
  slug: string;
  timingSettings: ITimingSettingsInput;
}

export interface ILocationSettingsInput {
  locationDetails?: string;
  locationType: LinkLocationType;
}

export interface ITimingSettingsInput {
  durationMinutes: number;
  periodType: PeriodType;
  timeRange: ILinkTimeRangeInput;
}

export interface ILinkTimeRangeInput {
  rangeEndTimeMillis?: number;
  rangeStartTimeMillis?: number;
  timeZone?: string;
  windowSizeDays: number;
}

export interface ICreateMeetingSettingsPayload {
  __typename: 'CreateMeetingSettingsPayload';
  clientMutationId: string;
  meetingSettingsErrorable: MeetingSettingsErrorable;
}

export interface ICreateOrgSubscriptionStripeInput {
  clientMutationId?: string;
  initialUsers: Array<string>;
  numSeats?: number;
  orgRelayId: string;
  recurrence: CreateOrgSubscriptionStripeRecurrenceEnum;
  stripePaymentMethodId: string;
}

export enum CreateOrgSubscriptionStripeRecurrenceEnum {
  Monthly = 'Monthly',
  Yearly = 'Yearly',
}

export interface ICreateOrgSubscriptionStripePayload {
  __typename: 'CreateOrgSubscriptionStripePayload';
  clientMutationId: string;
  stripeError: IStripeError;
}

/**
 * Stripe error
 */
export interface IStripeError {
  __typename: 'StripeError';
  code: string;
  message: string;
}

export interface ICreateTeamMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  setAsPrimary?: boolean;
  teamMembers: Array<ITeamRoleAndPersonIdInput>;
  teamName: string;
  teamType: TeamType;
}

export interface ITeamRoleAndPersonIdInput {
  personId: string;
  role: TeamMemberRole;
}

export enum TeamType {
  Managerial = 'Managerial',
  Other = 'Other',
}

export interface ICreateTeamMutationPayload {
  __typename: 'createTeamMutationPayload';
  clientMutationId: string;
  team: ITeam;
}

export interface ICreateTeamAndTeamSubscriptionStripeInput {
  clientMutationId?: string;
  orgRelayId: string;
  personId: string;
  seatCount: number;
  stripePaymentMethodId: string;
  subscriptionInterval: SubscriptionInterval;
  userEmail: string;
}

export enum SubscriptionInterval {
  Monthly = 'Monthly',
  Yearly = 'Yearly',
}

export interface ICreateTeamAndTeamSubscriptionStripePayload {
  __typename: 'CreateTeamAndTeamSubscriptionStripePayload';
  clientMutationId: string;
  stripeError: IStripeError;
  team: ITeam;
}

export interface ICreateTeamCalendarMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  teamId: string;
}

export interface ICreateTeamCalendarMutationPayload {
  __typename: 'createTeamCalendarMutationPayload';
  clientMutationId: string;
}

export interface ICreateTeamSubscriptionStripeInput {
  clientMutationId?: string;
  orgRelayId: string;
  seatCount: number;
  stripePaymentMethodId: string;
  subscriptionInterval: SubscriptionInterval;
  teamId: string;
}

export interface ICreateTeamSubscriptionStripePayload {
  __typename: 'CreateTeamSubscriptionStripePayload';
  clientMutationId: string;
  stripeError: IStripeError;
  team: ITeam;
}

export interface IDeactivateUserMutationInput {
  clientMutationId?: string;
}

export interface IDeactivateUserMutationPayload {
  __typename: 'DeactivateUserMutationPayload';
  clientMutationId: string;
  user: IUser;
  viewer: IViewer;
}

export interface IDeleteAsanaCredentialsInput {
  clientMutationId?: string;

  /**
   * org id
   */
  orgRelayId: string;
}

export interface IDeleteAsanaCredentialsPayload {
  __typename: 'DeleteAsanaCredentialsPayload';
  clientMutationId: string;
}

export interface IDeleteDataSourceInput {
  clientMutationId?: string;

  /**
   * The relay ID of the organization the calendar is part of
   */
  orgRelayId: string;

  /**
   * The ID of the calendar that is being deleted
   */
  sourceCalId: string;
}

export interface IDeleteDataSourcePayload {
  __typename: 'DeleteDataSourcePayload';
  clientMutationId: string;
}

export interface IDeleteEventMutationInput {
  calendarId: string;
  clientMutationId?: string;
  emailAddress: string;
  externalEventId: string;

  /**
   * optional, when true will delete an entire recurrence
   * @default false
   */
  isRecurring?: boolean;
}

export interface IDeleteEventMutationPayload {
  __typename: 'DeleteEventMutationPayload';
  clientMutationId: string;
}

export interface IDeleteMeetingSettingsInput {
  clientMutationId?: string;
  meetingSettingsId: string;
}

export interface IDeleteMeetingSettingsPayload {
  __typename: 'DeleteMeetingSettingsPayload';
  clientMutationId: string;
}

export interface IDeleteTeamMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  teamId: string;
}

export interface IDeleteTeamMutationPayload {
  __typename: 'deleteTeamMutationPayload';
  clientMutationId: string;
  deletedTeamId: string;
}

export interface IDeleteUserLevelZoomAccountMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
}

export interface IDeleteUserLevelZoomAccountMutationPayload {
  __typename: 'DeleteUserLevelZoomAccountMutationPayload';
  clientMutationId: string;
}

export interface IDeleteZoomAccountMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  zoomAccountId: string;
}

export interface IDeleteZoomAccountMutationPayload {
  __typename: 'DeleteZoomAccountMutationPayload';
  clientMutationId: string;
  deletedZoomAccountId: string;
}

export interface IDisableDataSourceSyncInput {
  clientMutationId?: string;

  /**
   * The relay ID of the organization the calendar is part of
   */
  orgRelayId: string;

  /**
   * The ID of the calendar that is being disabled
   */
  sourceCalId: string;
}

export interface IDisableDataSourceSyncPayload {
  __typename: 'DisableDataSourceSyncPayload';
  clientMutationId: string;
  dataSource: IDataSource;
}

export interface IEnableDataSourceSyncInput {
  clientMutationId?: string;

  /**
   * The front-end ID of the organization the work calendar is part of
   */
  orgRelayId: string;

  /**
   * The ID of the calendar that is being synced to the work calendar
   */
  sourceCalId: string;
}

export interface IEnableDataSourceSyncPayload {
  __typename: 'EnableDataSourceSyncPayload';
  clientMutationId: string;
  dataSource: IDataSource;
}

export interface IEnableOrDisableTeamSyncForEventInput {
  calendarId: string;
  clientMutationId?: string;
  disable: boolean;
  externalEventId: string;
  orgRelayId: string;
}

export interface IEnableOrDisableTeamSyncForEventPayload {
  __typename: 'EnableOrDisableTeamSyncForEventPayload';
  clientMutationId: string;
  disabled: boolean;
}

export interface IExecuteCliCommandMutationInput {
  clientMutationId?: string;
  command: string;
  options?: Array<ICliOptionInput>;
}

export interface ICliOptionInput {
  name: string;
  values: Array<string>;
}

export interface IExecuteCliCommandMutationPayload {
  __typename: 'ExecuteCliCommandMutationPayload';
  clientMutationId: string;
  result: string;
}

export interface IFindOrCreateLinkNameInput {
  clientMutationId?: string;
}

export interface IFindOrCreateLinkNamePayload {
  __typename: 'FindOrCreateLinkNamePayload';
  clientMutationId: string;
  linkName: string;
  schedulingLinks: Array<ISchedulingLink>;
}

export interface IInstallSlackAppMutationInput {
  clientMutationId?: string;
  installSlackAppCode: string;
  redirect: string;

  /**
   * @default false
   */
  useOAuthV2: boolean;
}

export interface IInstallSlackAppMutationPayload {
  __typename: 'InstallSlackAppMutationPayload';
  clientMutationId: string;
  installSlackAppResult: boolean;
  viewer: IViewer;
}

export interface IInvitePersonsToTeamInput {
  clientMutationId?: string;
  orgRelayId: string;
  personIds: Array<string>;
  teamId: string;
}

export interface IInvitePersonsToTeamPayload {
  __typename: 'InvitePersonsToTeamPayload';
  clientMutationId: string;
}

export interface IInviteToBillingGroupMutationInput {
  billingGroupId: string;
  clientMutationId?: string;
  orgRelayId: string;
  personIds: Array<string>;
}

export interface IInviteToBillingGroupMutationPayload {
  __typename: 'InviteToBillingGroupMutationPayload';
  clientMutationId: string;
}

export interface IJoinWaitlistMutationInput {
  clientMutationId?: string;
  email: string;
  role?: string;
}

export interface IJoinWaitlistMutationPayload {
  __typename: 'JoinWaitlistMutationPayload';
  clientMutationId: string;
  response: WaitlistSignupResponse;
}

export enum WaitlistSignupResponse {
  Allowed = 'Allowed',
  Claimed = 'Claimed',
  Queued = 'Queued',
}

export interface ILoginInsightsUserInput {
  accessToken: string;
  clientMutationId?: string;
  oauthType: OAuthType;
}

export enum OAuthType {
  Google = 'Google',
  Office365 = 'Office365',
}

export interface ILoginInsightsUserPayload {
  __typename: 'LoginInsightsUserPayload';
  clientMutationId: string;
}

export interface ILoginInsightsUserOfflineInput {
  clientMutationId?: string;
  codeOrToken: string;
  oauthType: OAuthType;
}

export interface ILoginInsightsUserOfflinePayload {
  __typename: 'LoginInsightsUserOfflinePayload';
  clientMutationId: string;
  email: string;
  externalImageUrl: string;
  familyName: string;
  givenName: string;
  name: string;
}

export interface ILoginMicrosoftUserMutationInput {
  clientMutationId?: string;
  microsoftAuthCode: string;
  redirectUri: string;
}

export interface ILoginMicrosoftUserMutationPayload {
  __typename: 'LoginMicrosoftUserMutationPayload';
  clientMutationId: string;
  loginStatus: LoginStatus;
  operation: RefreshTokenResultOperationEnum;
  orgOperation: OrganizationOperationResultEnum;
  verifyCodeMessage: string;
  verifyCodeResult: boolean;
  viewer: IViewer;
}

export enum LoginStatus {
  BlockedByAdmin = 'BlockedByAdmin',
  Deactivating = 'Deactivating',
  GoogleApiAccessDisabled = 'GoogleApiAccessDisabled',
  GoogleContactSharingDisabled = 'GoogleContactSharingDisabled',
  NoCrawl = 'NoCrawl',
  PersonalEmail = 'PersonalEmail',
  SecondaryDomainUsed = 'SecondaryDomainUsed',
  Success = 'Success',
  WaitlistedEmail = 'WaitlistedEmail',
}

export enum RefreshTokenResultOperationEnum {
  Create = 'Create',
  Fetch = 'Fetch',
}

export enum OrganizationOperationResultEnum {
  Add = 'Add',
  Create = 'Create',
  Delete = 'Delete',
  Fetch = 'Fetch',
}

export interface ILoginUserMutationInput {
  clientMutationId?: string;
  deviceIdAndRegistrationToken?: IDeviceIdAndRegistrationToken;
  googleOfflineCode: string;
  verificationType?: VerificationType;
}

export interface IDeviceIdAndRegistrationToken {
  /**
   * A unique id that identifies the calling device
   */
  deviceId: string;

  /**
   * The registration token of the device that allows sending push notifications
   */
  registrationToken: string;
}

export interface ILoginUserMutationPayload {
  __typename: 'LoginUserMutationPayload';
  clientMutationId: string;
  loginStatus: LoginStatus;
  operation: RefreshTokenResultOperationEnum;
  orgOperation: OrganizationOperationResultEnum;
  verifyCodeMessage: string;
  verifyCodeResult: boolean;
  viewer: IViewer;
}

export interface ILogoutInsightsUserInput {
  clientMutationId?: string;
}

export interface ILogoutInsightsUserPayload {
  __typename: 'LogoutInsightsUserPayload';
  clientMutationId: string;
}

export interface ILogoutUserMutationInput {
  clientMutationId?: string;
}

export interface ILogoutUserMutationPayload {
  __typename: 'LogoutUserMutationPayload';
  clientMutationId: string;
  user: IUser;
  viewer: IViewer;
}

export interface IMarkDefragValueKnobsAsAcceptedMutationInput {
  clientMutationId?: string;
  knobUrns: Array<IUrnInput>;
  orgRelayId: string;
}

export interface IUrnInput {
  id: string;
  scope?: IUrnInput;
  type?: UrnType;
  value?: string;
}

export interface IMarkDefragValueKnobsAsAcceptedMutationPayload {
  __typename: 'MarkDefragValueKnobsAsAcceptedMutationPayload';
  clientMutationId: string;
}

export interface IMarkDefragValueKnobsAsDismissedMutationInput {
  clientMutationId?: string;
  knobUrns: Array<IUrnInput>;
  orgRelayId: string;
}

export interface IMarkDefragValueKnobsAsDismissedMutationPayload {
  __typename: 'MarkDefragValueKnobsAsDismissedMutationPayload';
  clientMutationId: string;
}

export interface IMarkDefragValueKnobsAsPresentedMutationInput {
  clientMutationId?: string;
  knobUrns: Array<IUrnInput>;
  orgRelayId: string;
}

export interface IMarkDefragValueKnobsAsPresentedMutationPayload {
  __typename: 'MarkDefragValueKnobsAsPresentedMutationPayload';
  clientMutationId: string;
}

export interface IMarkSeenFeedSuggestionMutationInput {
  clientMutationId?: string;

  /**
   * The front-end ID for the feed suggestion to mark as seen
   */
  feedSuggestionRelayId: string;

  /**
   * Optional enum value of primary action taken resulting in this suggestion being marked seen
   */
  primaryActionTaken?: SuggestionActionResultType;
}

export interface IMarkSeenFeedSuggestionMutationPayload {
  __typename: 'MarkSeenFeedSuggestionMutationPayload';
  clientMutationId: string;
}

export interface IMaybeOnboardOrgMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
}

export interface IMaybeOnboardOrgMutationPayload {
  __typename: 'MaybeOnboardOrgMutationPayload';
  clientMutationId: string;
  crawlTest: OrgCrawlTest;
}

export interface IOverrideSyncStatusForUserEventMutationInput {
  clientMutationId?: string;
  externalEventId: string;
}

export interface IOverrideSyncStatusForUserEventMutationPayload {
  __typename: 'OverrideSyncStatusForUserEventMutationPayload';
  clientMutationId: string;
}

export interface IPatchBillingGroupMutationInput {
  billingGroupId: string;
  clientMutationId?: string;
  invitePolicy: InvitePolicyEnum;
  name?: string;
  orgRelayId: string;
}

export interface IPatchBillingGroupMutationPayload {
  __typename: 'PatchBillingGroupMutationPayload';
  clientMutationId: string;
}

export interface IPatchSlackSettingsMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  slackSettings: ISlackSettingsInput;
}

export interface ISlackSettingsInput {
  calendarStatus?: ISlackCalendarStatusSyncInput;
  calendarStatusEnabled: boolean;
  dailyDigestSettings?: ISlackDailyDigestSettingsInput;
  dndSettings?: ISlackDndSettingsInput;
  id: string;
  includeMeetingCategory: boolean;
  includeMeetingTitle: boolean;
  notificationSettings?: ISlackNotificationSettingsInput;
  slackTeamId: string;
  slackUserId: string;
}

export interface ISlackCalendarStatusSyncInput {
  calendarStatusEnabled: boolean;
  includeMeetingCategory: boolean;
  includeMeetingTitle: boolean;
}

export interface ISlackDailyDigestSettingsInput {
  enabled: boolean;
  timeSlot: DigestTimeSlot;
}

export interface ISlackDndSettingsInput {
  focusTime: boolean;
  meeting: boolean;
}

export interface ISlackNotificationSettingsInput {
  defragSummary: boolean;
}

export interface IPatchSlackSettingsMutationPayload {
  __typename: 'PatchSlackSettingsMutationPayload';
  clientMutationId: string;
  slackSettings: ISlackSettings;
}

export interface IPreAddOrgTagsMutationInput {
  clientMutationId?: string;
  moveRangeV3?: IMoveRangeInLocalTimeV3Input;
  orgRelayId: string;
  preAnnotation: IPreAnnotationInput;
}

export interface IPreAnnotationInput {
  attendees: Array<string>;
  calendarId: string;
  description?: string;
  endTime: IInferredEventTimeInput;
  startTime: IInferredEventTimeInput;
  tags: Array<ITagInput>;
  title?: string;
}

export interface IInferredEventTimeInput {
  date: string;
  time: string;
  timeZone?: string;
}

export interface IPreAddOrgTagsMutationPayload {
  __typename: 'PreAddOrgTagsMutationPayload';
  clientMutationId: string;
}

export interface IRecordChromeInstallMutationInput {
  clientMutationId?: string;
}

export interface IRecordChromeInstallMutationPayload {
  __typename: 'RecordChromeInstallMutationPayload';
  clientMutationId: string;
}

export interface IRecordChromeUninstallMutationInput {
  clientMutationId?: string;
}

export interface IRecordChromeUninstallMutationPayload {
  __typename: 'RecordChromeUninstallMutationPayload';
  clientMutationId: string;
}

export interface IRecordDefragResponseMutationInput {
  clientMutationId?: string;
  feedback?: DefragFeedback;
  orgId: string;
  otherReason?: string;
  preDefinedReasons?: Array<string>;
  responseKeys: Array<any>;
}

export enum DefragFeedback {
  LooksGood = 'LooksGood',
  Maybe = 'Maybe',
  Meh = 'Meh',
}

export interface IRecordDefragResponseMutationPayload {
  __typename: 'RecordDefragResponseMutationPayload';
  clientMutationId: string;
  response: boolean;
}

export interface IRecordImpressionMutationInput {
  clientMutationId?: string;
  name: string;
  treatmentType: string;
}

export interface IRecordImpressionMutationPayload {
  __typename: 'RecordImpressionMutationPayload';
  clientMutationId: string;
}

export interface IRegisterEmailUpdatesInput {
  clientMutationId?: string;
  email: string;
}

export interface IRegisterEmailUpdatesPayload {
  __typename: 'RegisterEmailUpdatesPayload';
  clientMutationId: string;
}

export interface IRemoveAdminForBillingGroupMutationInput {
  billingGroupId: string;
  clientMutationId?: string;
  forceDelete?: boolean;
  orgRelayId: string;
  userId: string;
}

export interface IRemoveAdminForBillingGroupMutationPayload {
  __typename: 'RemoveAdminForBillingGroupMutationPayload';
  clientMutationId: string;
}

export interface IRemoveGoalMutationInput {
  clientMutationId?: string;
  goal: IGoalInput;
  orgId: string;
}

export interface IGoalInput {
  goalId: string;
  id: string;
  isDeleted: boolean;
  name: string;
  options?: IGoalOptionsInput;
  otherUrns: Array<IUrnInput>;
  targetUrn: IUrnInput;
  valueUrn: IUrnInput;
}

export interface IGoalOptionsInput {
  syncToGoogle: boolean;
}

export interface IRemoveGoalMutationPayload {
  __typename: 'RemoveGoalMutationPayload';
  clientMutationId: string;
  goal: IGoal;
}

export interface IRemoveLunchHoldMutationInput {
  calendarId?: string;
  clientMutationId?: string;
  isPaused?: boolean;
  lunchEventId: string;
  orgRelayId: string;
}

export interface IRemoveLunchHoldMutationPayload {
  __typename: 'RemoveLunchHoldMutationPayload';
  clientMutationId: string;
}

export interface IRemoveManualMembersForBillingGroupMutationInput {
  addExclusions?: boolean;
  billingGroupId: string;
  clientMutationId?: string;
  orgRelayId: string;
  personIds: Array<string>;
}

export interface IRemoveManualMembersForBillingGroupMutationPayload {
  __typename: 'RemoveManualMembersForBillingGroupMutationPayload';
  clientMutationId: string;
}

export interface IRemoveOrgTagsMutationInput {
  clientMutationId?: string;
  eventParentRelayId: string;
  orgRelayId: string;
  tagNames: Array<string>;
}

export interface IRemoveOrgTagsMutationPayload {
  __typename: 'RemoveOrgTagsMutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface IRescheduleBookingMutationInput {
  body: IRescheduleBookingBody;

  /**
   * Id of booking
   */
  bookingId: string;
  clientMutationId?: string;
}

export interface IRescheduleBookingBody {
  /**
   * End time of event in ISO 8601 format.
   */
  eventEndTime: string;

  /**
   * Start time of event in ISO 8601 format.
   */
  eventStartTime: string;

  /**
   * Optional reason for reschedule.
   */
  reasonForReschedule?: string;
}

export interface IRescheduleBookingMutationPayload {
  __typename: 'RescheduleBookingMutationPayload';
  clientMutationId: string;
}

export interface IRescheduleEventParentMutationInput {
  clientMutationId?: string;
  eventTime: IEventTimeInput;
  externalEventId: string;
  orgRelayId: string;
}

export interface IRescheduleEventParentMutationPayload {
  __typename: 'RescheduleEventParentMutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface ISaveAsanaCredentialsInput {
  clientMutationId?: string;

  /**
   * Code recieved from Asana via redirect uri upon successful authentication.
   */
  code: string;

  /**
   * org id
   */
  orgRelayId: string;

  /**
   * Redirect uri generated and used by the frontend for Oath. Required for future token exchange.
   */
  redirectUri: string;
}

export interface ISaveAsanaCredentialsPayload {
  __typename: 'SaveAsanaCredentialsPayload';
  clientMutationId: string;
}

export interface ISaveTeamSettingsMutationInput {
  clientMutationId?: string;
  manageTeamCalendar?: boolean;
  minimumMinutesForSyncing?: number;
  noMeetingDayEmailOnScheduleOver?: boolean;
  noMeetingDayOfWeek?: NoMeetingDayOfWeek;
  orgRelayId: string;
  slackChannelId?: string;
  slackChannelName?: string;
  slackNotificationEnabled?: boolean;
  slackNotificationTimeSlot?: TimeSlotEnum;
  syncWFHEvents?: boolean;
  teamId: string;
  useNoMeetingDay?: boolean;
}

export interface ISaveTeamSettingsMutationPayload {
  __typename: 'saveTeamSettingsMutationPayload';
  clientMutationId: string;
  teamSettings: ITeamSettings;
}

export interface ISaveTeamUserSettingsMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  teamId: string;
  useNoMeetingDay?: boolean;
  useTeamCalendar?: boolean;
}

export interface ISaveTeamUserSettingsMutationPayload {
  __typename: 'saveTeamUserSettingsMutationPayload';
  clientMutationId: string;
  teamUserSettings: ITeamUserSettings;
}

export interface ISaveUninstallResponseMutationInput {
  clientMutationId?: string;
  considerClockwiseFuture?: string;
  more?: string;
  name?: string;
  reason?: string;
}

export interface ISaveUninstallResponseMutationPayload {
  __typename: 'SaveUninstallResponseMutationPayload';
  clientMutationId: string;
}

export interface IScheduleNewEventMutationInput {
  /**
   * @default []
   */
  attendees?: Array<string>;
  calendarId: string;
  clientMutationId?: string;
  conflictResolutionDefragId?: string;
  description?: string;
  duration: number;
  includeZoomLink?: boolean;
  location?: string;
  orgRelayId: string;

  /**
   * @default "None"
   */
  recurrenceOption: RecurrenceOption;
  startSlot: IDayTimeSlotInput;
  timeZone: string;
  title?: string;
}

export enum RecurrenceOption {
  Bimonthly = 'Bimonthly',
  Biweekly = 'Biweekly',
  Monthly = 'Monthly',
  None = 'None',
  Weekly = 'Weekly',
}

export interface IScheduleNewEventMutationPayload {
  __typename: 'ScheduleNewEventMutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface IScheduleNewEventV2MutationInput {
  /**
   * @default []
   */
  attendees?: Array<string>;
  calendarId: string;
  clientMutationId?: string;
  conflictResolutionDefragId?: string;
  description?: string;
  includeZoomLink?: boolean;
  location?: string;
  orgRelayId: string;

  /**
   * @default "None"
   */
  recurrenceOption: RecurrenceOption;
  timeRange: ITimeRangeInput;
  timeZone: string;
  title?: string;
}

export interface ITimeRangeInput {
  endTime?: IEventTimeInput;
  id: string;
  startTime?: IEventTimeInput;
}

export interface IScheduleNewEventV2MutationPayload {
  __typename: 'ScheduleNewEventV2MutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface IScheduleNewSmartHoldMutationInput {
  calendarId: string;
  clientMutationId?: string;
  description?: string;
  orgRelayId: string;
  smartHoldType: SmartHoldTypeEnum;
  timeRange: ITimeRangeInput;
}

export enum SmartHoldTypeEnum {
  FocusTime = 'FocusTime',
  Lunch = 'Lunch',
  MissingPriority = 'MissingPriority',
  NoMeetingDay = 'NoMeetingDay',
  OnboardingTestEvent = 'OnboardingTestEvent',
  Pain = 'Pain',
  PrepTime = 'PrepTime',
  ShadowCalendar = 'ShadowCalendar',
  SyncedEvent = 'SyncedEvent',
  TeamEvent = 'TeamEvent',
  TravelTime = 'TravelTime',
}

export interface IScheduleNewSmartHoldMutationPayload {
  __typename: 'ScheduleNewSmartHoldMutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface ISendBillingGroupRequestEmailMutationInput {
  billingGroupId: string;
  clientMutationId?: string;
  orgRelayId: string;
  personId: string;
}

export interface ISendBillingGroupRequestEmailMutationPayload {
  __typename: 'SendBillingGroupRequestEmailMutationPayload';
  clientMutationId: string;
}

export interface ISendCalAuditInviteEmailInput {
  clientMutationId?: string;
  recipientEmail: string;
}

export interface ISendCalAuditInviteEmailPayload {
  __typename: 'SendCalAuditInviteEmailPayload';
  clientMutationId: string;
}

export interface ISetPreferredRoomsMutationInput {
  clientMutationId?: string;
  orgRelayId?: string;
  preferredRoomIds: Array<string>;
}

export interface ISetPreferredRoomsMutationPayload {
  __typename: 'SetPreferredRoomsMutationPayload';
  clientMutationId: string;
  suggestedRoomPreferences: ISuggestedRoomPreferences;
}

export interface ISetPrimaryTeamMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  teamId: string;
}

export interface ISetPrimaryTeamMutationPayload {
  __typename: 'setPrimaryTeamMutationPayload';
  clientMutationId: string;
  team: ITeam;
}

export interface ISetWorkAddressForUserMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  rawAddress?: string;
}

export interface ISetWorkAddressForUserMutationPayload {
  __typename: 'SetWorkAddressForUserMutationPayload';
  address: IAddress;
  clientMutationId: string;
}

export interface ISubmitHubspotFormMutationInput {
  clientMutationId?: string;
  formFields: Array<IHubspotFormFieldInput>;
  formId: string;
}

export interface IHubspotFormFieldInput {
  key: string;
  value: string;
}

export interface ISubmitHubspotFormMutationPayload {
  __typename: 'SubmitHubspotFormMutationPayload';
  clientMutationId: string;
}

export interface ISudoDeactivateUserMutationInput {
  clientMutationId?: string;
  email: string;
}

export interface ISudoDeactivateUserMutationPayload {
  __typename: 'SudoDeactivateUserMutationPayload';
  clientMutationId: string;
}

export interface ISudoDeleteEverythingMutationInput {
  clientMutationId?: string;
}

export interface ISudoDeleteEverythingMutationPayload {
  __typename: 'SudoDeleteEverythingMutationPayload';
  clientMutationId: string;
}

export interface ISudoModifyUserMutationInput {
  clientMutationId?: string;
  status: string;
}

export interface ISudoModifyUserMutationPayload {
  __typename: 'SudoModifyUserMutationPayload';
  clientMutationId: string;
  user: IUser;
  viewer: IViewer;
}

export interface ISudoResetFlowStateMutationInput {
  clientMutationId?: string;
  flowKey: string;
  orgRelayId?: string;
}

export interface ISudoResetFlowStateMutationPayload {
  __typename: 'SudoResetFlowStateMutationPayload';
  clientMutationId: string;
  flowState: IFlowState;
  viewer: IViewer;
}

export interface ISudoSwitchUserMutationInput {
  clientMutationId?: string;
  email: string;
  jwtType: string;
}

export interface ISudoSwitchUserMutationPayload {
  __typename: 'SudoSwitchUserMutationPayload';
  clientMutationId: string;
  user: IUser;
  viewer: IViewer;
}

export interface ISudoTriggerClientUpdateMutationInput {
  clientMutationId?: string;
  force: boolean;
}

export interface ISudoTriggerClientUpdateMutationPayload {
  __typename: 'SudoTriggerClientUpdateMutationPayload';
  clientMutationId: string;
  sudo: ISudo;
}

export interface ISudoUpdateWaitlistMutationInput {
  clientMutationId?: string;
  enabled: boolean;
}

export interface ISudoUpdateWaitlistMutationPayload {
  __typename: 'SudoUpdateWaitlistMutationPayload';
  clientMutationId: string;
  sudo: ISudo;
}

export interface ISudoUpdateWhitelistedDomainsMutationInput {
  action?: Action;
  clientMutationId?: string;
  domain: string;
}

export enum Action {
  Add = 'Add',
  Remove = 'Remove',
}

export interface ISudoUpdateWhitelistedDomainsMutationPayload {
  __typename: 'SudoUpdateWhitelistedDomainsMutationPayload';
  clientMutationId: string;
  sudo: ISudo;
}

export interface ISudoUpdateWhitelistedEmailsMutationInput {
  action: UpdateWhitelistEmailsAction;
  clientMutationId?: string;
  email: string;
}

export enum UpdateWhitelistEmailsAction {
  Add = 'Add',
  Remove = 'Remove',
}

export interface ISudoUpdateWhitelistedEmailsMutationPayload {
  __typename: 'SudoUpdateWhitelistedEmailsMutationPayload';
  clientMutationId: string;
  sudo: ISudo;
}

export interface IToggleAutoSyncForBillingGroupMutationInput {
  billingGroupId: string;
  clientMutationId?: string;
  isAutoSyncEnabled: boolean;
  orgRelayId: string;
  teamId?: string;
}

export interface IToggleAutoSyncForBillingGroupMutationPayload {
  __typename: 'ToggleAutoSyncForBillingGroupMutationPayload';
  clientMutationId: string;
}

export interface ITriggerDefragRunMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
}

export interface ITriggerDefragRunMutationPayload {
  __typename: 'TriggerDefragRunMutationPayload';
  clientMutationId: string;
}

export interface IUnpauseEventMutationInput {
  clientMutationId?: string;
  externalEventId: string;
  orgRelayId: string;
}

export interface IUnpauseEventMutationPayload {
  __typename: 'UnpauseEventMutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface IUnsubscribeEmailInput {
  clientMutationId?: string;
  email: string;
  token: string;
}

export interface IUnsubscribeEmailPayload {
  __typename: 'UnsubscribeEmailPayload';
  clientMutationId: string;
}

export interface IUnsyncFocusTimeBlockMutationInput {
  calendarId?: string;
  clientMutationId?: string;
  email?: string;
  externalEventId?: string;
  isPaused?: boolean;
  orgRelayId: string;
  timeRange: ITimeRangeInput;
}

export interface IUnsyncFocusTimeBlockMutationPayload {
  __typename: 'UnsyncFocusTimeBlockMutationPayload';
  clientMutationId: string;
}

export interface IUnsyncFocusTimeEventMutationInput {
  calendarId?: string;
  clientMutationId?: string;
  email?: string;
  externalEventId?: string;
  isPaused?: boolean;
  orgRelayId: string;
  timeRange: ITimeRangeInput;
}

export interface IUnsyncFocusTimeEventMutationPayload {
  __typename: 'UnsyncFocusTimeEventMutationPayload';
  clientMutationId: string;
}

export interface IUpdateDeviceTokenInput {
  clientMutationId?: string;

  /**
   * A unique identifier for the calling device
   */
  deviceId: string;

  /**
   * The registration token that allows us to send push notifications to the device
   */
  registrationToken: string;
}

export interface IUpdateDeviceTokenPayload {
  __typename: 'UpdateDeviceTokenPayload';
  clientMutationId: string;
}

export interface IEventColoringSettingsMutationsInput {
  clientMutationId?: string;
  eventCategoryColorings: Array<IEventCategoryColoringInput>;

  /**
   * The front-end ID for the organization we're saving event coloring settings for
   */
  orgRelayId: string;
}

export interface IEventCategoryColoringInput {
  active: boolean;
  eventCategory: string;
  googleColorKey?: string;
  isUserSet: boolean;
}

export interface IEventColoringSettingsMutationsPayload {
  __typename: 'EventColoringSettingsMutationsPayload';
  clientMutationId: string;
  eventColoringSettings: IEventColoringSettings;
}

export interface IUpdateEventResponseStatusMutationInput {
  calendarId: string;
  clientMutationId?: string;
  externalEventId: string;
  orgRelayId: string;
  responseStatus: ResponseStatusEnum;
}

export interface IUpdateEventResponseStatusMutationPayload {
  __typename: 'UpdateEventResponseStatusMutationPayload';
  clientMutationId: string;
  eventParent: IEventParent;
}

export interface IUpdateFlagsForUserMutationInput {
  clientMutationId?: string;
  enable: boolean;
  flagNames: Array<string>;
  flagNamespace: string;
}

export interface IUpdateFlagsForUserMutationPayload {
  __typename: 'UpdateFlagsForUserMutationPayload';
  clientMutationId: string;
  flags: IFlags;
}

export interface IUpdateUserFlowMutationInput {
  clientMutationId?: string;
  flowKey: string;
  newState: string;
}

export interface IUpdateUserFlowMutationPayload {
  __typename: 'UpdateUserFlowMutationPayload';
  clientMutationId: string;
  flowState: IFlowState;
}

export interface IUpdateUserOrgFlowMutationInput {
  clientMutationId?: string;
  flowKey: string;
  newState: string;
  orgRelayId: string;
}

export interface IUpdateUserOrgFlowMutationPayload {
  __typename: 'UpdateUserOrgFlowMutationPayload';
  clientMutationId: string;
  flowState: IFlowState;
  org: IOrg;
  user: IUser;
}

export interface IUpdateLinkActiveInput {
  clientMutationId?: string;
  isActive: boolean;
  slug: string;
}

export interface IUpdateLinkActivePayload {
  __typename: 'updateLinkActivePayload';
  clientMutationId: string;
  meetingSettingsErrorable: MeetingSettingsErrorable;
}

export interface IUpdateMeetingSettingsInput {
  body?: IMeetingSettingsInput;
  clientMutationId?: string;
}

export interface IUpdateMeetingSettingsPayload {
  __typename: 'UpdateMeetingSettingsPayload';
  clientMutationId: string;
  meetingSettingsErrorable: MeetingSettingsErrorable;
}

export interface IUpdateOrgInviteInput {
  calendarIds?: Array<string>;
  clientMutationId?: string;
  note?: string;
  orgRelayId: string;

  /**
   * @default "Defrag"
   */
  productType?: ProductType;
}

export enum ProductType {
  Defender = 'Defender',
  Defrag = 'Defrag',
}

export interface IUpdateOrgInvitePayload {
  __typename: 'UpdateOrgInvitePayload';
  calendarIds: Array<string>;
  clientMutationId: string;
  org: IOrg;
}

export interface IUpdateSettingMutationInput {
  clientMutationId?: string;
  setting: ISettingInput;
  value: Array<string>;
}

export interface ISettingInput {
  id: string;
  key?: ISettingKeyInput;
  type?: ISettingTypeInput;
  value?: Array<string>;
}

export interface ISettingKeyInput {
  id: string;
  name?: string;
  scope?: IUrnInput;
  target?: IUrnInput;
}

export interface ISettingTypeInput {
  containerType?: ContainerType;
  id: string;
  required?: boolean;
  valueType?: ValueType;
}

export interface IUpdateSettingMutationPayload {
  __typename: 'UpdateSettingMutationPayload';
  clientMutationId: string;
  setting: ISetting;
}

export interface IUpdateSettingsMutationInput {
  clientMutationId?: string;
  settings: Array<ISettingInput>;
}

export interface IUpdateSettingsMutationPayload {
  __typename: 'UpdateSettingsMutationPayload';
  clientMutationId: string;
  setting: Array<ISetting>;
}

export interface IUpdateShadowCalendarSettingsMutationInput {
  clientMutationId?: string;
  enabled: boolean;

  /**
   * The front-end ID for the organization we're saving shadow cal settings for
   */
  orgRelayId: string;
}

export interface IUpdateShadowCalendarSettingsMutationPayload {
  __typename: 'UpdateShadowCalendarSettingsMutationPayload';
  clientMutationId: string;
  shadowCalendarSettings: IShadowCalendarSettings;
}

export interface IUpdateSlackAccountMutationInput {
  action: SlackUpdateAction;
  clientMutationId?: string;
  slackAccountRelayId: string;
}

export enum SlackUpdateAction {
  Delete = 'Delete',
}

export interface IUpdateSlackAccountMutationPayload {
  __typename: 'UpdateSlackAccountMutationPayload';
  clientMutationId: string;
}

export interface IUpdateSlackAccountForTeamMutationInput {
  action: SlackForTeamUpdateAction;
  clientMutationId?: string;
  orgRelayId: string;
  teamId: string;
}

export enum SlackForTeamUpdateAction {
  Delete = 'Delete',
}

export interface IUpdateSlackAccountForTeamMutationPayload {
  __typename: 'UpdateSlackAccountForTeamMutationPayload';
  clientMutationId: string;
}

export interface IUpdateSmartHoldSyncSettingsMutationInput {
  clientMutationId?: string;
  focusTimeExternalTransparency?: FocusTimeExternalTransparency;
  focusTimeIdealMinutesPerWeek?: number;
  focusTimeRemindersEnabled?: boolean;
  focusTimeSyncAll?: boolean;
  focusTimeSyncEnabled?: boolean;
  lunchRemindersEnabled?: boolean;
  lunchSyncEnabled?: boolean;
  orgRelayId: string;
  travelTimeRemindersEnabled?: boolean;
  travelTimeSyncEnabled?: boolean;
}

export interface IUpdateSmartHoldSyncSettingsMutationPayload {
  __typename: 'UpdateSmartHoldSyncSettingsMutationPayload';
  clientMutationId: string;
  shadowCalendarSettings: IShadowCalendarSettings;
}

export interface IUpdateSuggestedOrgInviteInput {
  calendarIds?: Array<string>;
  clientMutationId?: string;
  note?: string;
  orgRelayId: string;
  resolution?: SuggestedOrgInviteResolution;
}

export interface IUpdateSuggestedOrgInvitePayload {
  __typename: 'UpdateSuggestedOrgInvitePayload';
  calendarIds: Array<string>;
  clientMutationId: string;
  org: IOrg;
}

export interface IUpdateTeamMutationInput {
  addedTeamMembers?: Array<ITeamRoleAndPersonIdInput>;
  clientMutationId?: string;
  newTeamName?: string;
  orgRelayId: string;
  removedTeamMembers?: Array<string>;
  teamId: string;
}

export interface IUpdateTeamMutationPayload {
  __typename: 'updateTeamMutationPayload';
  clientMutationId: string;
  team: ITeam;
}

export interface IToggleEventColoringSettingsMutationsInput {
  clientMutationId?: string;
  enabled: boolean;
  eventCategoryColorings: Array<IEventCategoryColoringInput>;

  /**
   * The front-end ID for the organization we're saving event coloring settings for
   */
  orgRelayId: string;
}

export interface IToggleEventColoringSettingsMutationsPayload {
  __typename: 'ToggleEventColoringSettingsMutationsPayload';
  clientMutationId: string;
  eventColoringSettings: IEventColoringSettings;
}

export interface IUpdateUserDefinedTeamMutationInput {
  calendarIds: Array<string>;
  clientMutationId?: string;
  userDefinedTeamRelayId: string;
}

export interface IUpdateUserDefinedTeamMutationPayload {
  __typename: 'UpdateUserDefinedTeamMutationPayload';
  clientMutationId: string;
  userDefinedTeam: IUserDefinedTeam;
}

export interface IUpdateUserShortnameInput {
  clientMutationId?: string;

  /**
   * The new shortname for the User.
   */
  shortname: string;
}

export interface IUpdateUserShortnamePayload {
  __typename: 'UpdateUserShortnamePayload';
  clientMutationId: string;
  userErrorable: UserErrorable;
}

export type UserErrorable = IGraphEntityError | IUser;

export interface IUpdateWorkingHoursMutationInput {
  clientMutationId?: string;

  /**
   * if true, will be saved as both Base and Meeting, regardless of the type on the working hours argument
   */
  saveMeetingAndWorkingHours?: boolean;
  workingHours: IWorkingHoursInput;
}

export interface IWorkingHoursInput {
  dayOverrides?: Array<IDayOverrideInput>;
  daySettings?: Array<IDaySettingInput>;
  id: string;
  timeZone?: string;
  type?: WorkingHoursTypeEnum;
}

export interface IDayOverrideInput {
  date?: string;
  setting?: IWorkingSettingInput;
}

export interface IWorkingSettingInput {
  excludedSlots?: Array<ExcludedSlots>;
  maxSlot?: MaxSlot;
  minSlot?: MinSlot;
}

export interface IDaySettingInput {
  day?: Day;
  setting?: IWorkingSettingInput;
}

export interface IUpdateWorkingHoursMutationPayload {
  __typename: 'UpdateWorkingHoursMutationPayload';
  clientMutationId: string;
  updatedOrgMeetingHours: IWorkingHours;
  updatedUserMeetingHours: IWorkingHours;
  updatedUserWorkingHours: IWorkingHours;
  userWorkingHours: IUserWorkingHours;
}

export interface IUpsertEmailTriageMutationInput {
  clientMutationId?: string;
  options: IGoalPatchEmailTriageOptionsInput;
  orgId: string;
}

export interface IGoalPatchEmailTriageOptionsInput {
  minutesDuration?: number;
  nameOverride?: string;
  syncToGoogle?: boolean;
}

export interface IUpsertEmailTriageMutationPayload {
  __typename: 'UpsertEmailTriageMutationPayload';
  clientMutationId: string;
  goal: IGoal;
  goalEdge: IGoalEdge;
  org: IOrg;
}

export interface IUpsertFocusTimeMutationInput {
  clientMutationId?: string;
  options: IGoalPatchFocusTimeOptionsInput;
  orgId: string;
}

export interface IGoalPatchFocusTimeOptionsInput {
  maxMinutesPerBlock?: number;
  syncToGoogle?: boolean;
}

export interface IUpsertFocusTimeMutationPayload {
  __typename: 'UpsertFocusTimeMutationPayload';
  clientMutationId: string;
  goal: IGoal;
  goalEdge: IGoalEdge;
  org: IOrg;
}

export interface IUpsertLunchMutationInput {
  clientMutationId?: string;
  options: IGoalPatchLunchOptionsInput;
  orgId: string;
}

export interface IGoalPatchLunchOptionsInput {
  idealMinutesDuration?: number;
  maxTimeSlot?: TimeSlot;
  minimumMinutesDuration?: number;
  minTimeSlot?: TimeSlot;
  syncToGoogle?: boolean;
}

export interface IUpsertLunchMutationPayload {
  __typename: 'UpsertLunchMutationPayload';
  clientMutationId: string;
  goal: IGoal;
  goalEdge: IGoalEdge;
  org: IOrg;
}

export interface IUpsertMeetingReliefMutationInput {
  clientMutationId?: string;
  options: IGoalPatchMeetingReliefOptionsInput;
  orgId: string;
}

export interface IGoalPatchMeetingReliefOptionsInput {
  fatigueThresholdMinutes?: number;
  meetingReliefBreakMinutes?: number;
}

export interface IUpsertMeetingReliefMutationPayload {
  __typename: 'UpsertMeetingReliefMutationPayload';
  clientMutationId: string;
  goal: IGoal;
  goalEdge: IGoalEdge;
  org: IOrg;
}

export interface IUpsertNonUserDefragEmailResponseInput {
  additionalInformation?: string;
  clientMutationId?: string;
  emailInstanceId: string;
  responseType: NonUserDefragEmailResponseType;
}

export interface IUpsertNonUserDefragEmailResponsePayload {
  __typename: 'UpsertNonUserDefragEmailResponsePayload';
  clientMutationId: string;
  nonUserDefragEmailResponse: INonUserDefragEmailResponse;
}

export interface IUpsertPreferredWorkMutationInput {
  clientMutationId?: string;
  options: IGoalPatchPreferredWorkOptionsInput;
  orgId: string;
}

export interface IGoalPatchPreferredWorkOptionsInput {
  keepEndOfDayOpen?: boolean;
  keepStartOfDayOpen?: boolean;
  preferredWorkType?: PreferredWorkType;
}

export interface IUpsertPreferredWorkMutationPayload {
  __typename: 'UpsertPreferredWorkMutationPayload';
  clientMutationId: string;
  goal: IGoal;
  goalEdge: IGoalEdge;
  org: IOrg;
}

export interface IUpsertTravelTimeMutationInput {
  clientMutationId?: string;
  options: IGoalPatchTravelTimeOptionsInput;
  orgId: string;
}

export interface IGoalPatchTravelTimeOptionsInput {
  syncToGoogle?: boolean;
}

export interface IUpsertTravelTimeMutationPayload {
  __typename: 'UpsertTravelTimeMutationPayload';
  clientMutationId: string;
  goal: IGoal;
  goalEdge: IGoalEdge;
  org: IOrg;
}

export interface ISaveUserSurveyResponsesMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  userSurveyResponses?: Array<IUserSurveyResponseInput>;
}

export interface IUserSurveyResponseInput {
  freeformResponse?: string;
  questionText?: string;
  questionType?: QuestionType;
  responseText?: string;
  responseType?: ResponseType;
}

export interface ISaveUserSurveyResponsesMutationPayload {
  __typename: 'SaveUserSurveyResponsesMutationPayload';
  clientMutationId: string;
  userSurveyResponse: IUserSurveyResponse;
}

export interface ISaveUserSurveyResponseWrapperMutationInput {
  clientMutationId?: string;
  userSurveyResponseWrapper: IUserSurveyResponseWrapperInput;
}

export interface IUserSurveyResponseWrapperInput {
  id: string;
  userSurveyResponses: Array<IUserSurveyResponseInput>;
}

export interface ISaveUserSurveyResponseWrapperMutationPayload {
  __typename: 'SaveUserSurveyResponseWrapperMutationPayload';
  clientMutationId: string;
  userSurveyResponseWrapper: IUserSurveyResponseWrapper;
}

export interface IValidateSlackChannelBySendingMessageMutationInput {
  clientMutationId?: string;
  orgRelayId: string;
  slackChannelName: string;
  teamId: string;
}

export interface IValidateSlackChannelBySendingMessageMutationPayload {
  __typename: 'ValidateSlackChannelBySendingMessageMutationPayload';
  clientMutationId: string;
  validateSlackChannelResponse: IValidateSlackChannelResponse;
}

/**
 * DESCRIBE NEW FIELD HERE
 */
export interface IValidateSlackChannelResponse {
  __typename: 'ValidateSlackChannelResponse';
  channel: ISlackChannel;

  /**
   * The ID of an object
   */
  id: string;
  isValid: boolean;
}

export interface IWeeklySummaryResponseMutationInput {
  clientMutationId?: string;
  feedback?: WeeklySummaryFeedback;
  maxDate: string;
  minDate: string;
  orgRelayId: string;
}

export enum WeeklySummaryFeedback {
  No = 'No',
  NoOpinion = 'NoOpinion',
  Yes = 'Yes',
}

export interface IWeeklySummaryResponseMutationPayload {
  __typename: 'WeeklySummaryResponseMutationPayload';
  clientMutationId: string;
  response: boolean;
}

export interface ISubscription {
  __typename: 'Subscription';
  updateEventInNearbyWeekChanges: IEventInNearbyWeekChange;
  updateEventParentBaseWrapper: IEventParentBaseWrapper;
  updateEventParentWrappers: IUpdateEventParentWrappersSubscriptionPayload;
  updateFlowState: IUpdateFlowStateSubscriptionPayload;
  updateOrgFlowState: IUpdateOrgFlowStateSubscriptionPayload;
  updateTeamCalendarState: IUpdateTeamCalendarStateSubscriptionPayload;
}

export interface IUpdateEventInNearbyWeekChangesOnSubscriptionArguments {
  input: IUpdateEventInNearbyWeekChangeInput;
}

export interface IUpdateEventParentBaseWrapperOnSubscriptionArguments {
  input: IUpdateEventParentBaseWrappersSubscriptionInput;
}

export interface IUpdateEventParentWrappersOnSubscriptionArguments {
  input: IUpdateEventParentWrappersSubscriptionInput;
}

export interface IUpdateFlowStateOnSubscriptionArguments {
  input: IUpdateFlowStateSubscriptionInput;
}

export interface IUpdateOrgFlowStateOnSubscriptionArguments {
  input: IUpdateOrgFlowStateSubscriptionInput;
}

export interface IUpdateTeamCalendarStateOnSubscriptionArguments {
  input: IUpdateTeamCalendarStateSubscriptionInput;
}

export interface IUpdateEventInNearbyWeekChangeInput {
  calendarIds: Array<string>;
  orgRelayId: string;
}

export interface IEventInNearbyWeekChange {
  __typename: 'EventInNearbyWeekChange';
  affectedWeeks: Array<string>;
  calendarId: string;
  currentMillis: number;
}

export interface IUpdateEventParentBaseWrappersSubscriptionInput {
  calendarIds: Array<string>;
  dates: Array<any>;
  includeAllDay?: boolean;
  orgRelayId: string;
  timeZone: string;
}

export interface IUpdateEventParentWrappersSubscriptionInput {
  calendarIds: Array<string>;
  dates: Array<any>;
  includeAllDay?: boolean;
  orgRelayId: string;
  timeZone: string;
}

export interface IUpdateEventParentWrappersSubscriptionPayload {
  __typename: 'UpdateEventParentWrappersSubscriptionPayload';
  eventParentBases: Array<IEventParentBase>;
  eventParentWrappers: Array<IEventParentWrapper>;
}

export interface IUpdateFlowStateSubscriptionInput {
  flowStateRelayId: string;
}

export interface IUpdateFlowStateSubscriptionPayload {
  __typename: 'UpdateFlowStateSubscriptionPayload';
  flowState: IFlowState;
}

export interface IUpdateOrgFlowStateSubscriptionInput {
  flowStateRelayId: string;
}

export interface IUpdateOrgFlowStateSubscriptionPayload {
  __typename: 'UpdateOrgFlowStateSubscriptionPayload';
  flowState: IFlowState;
}

export interface IUpdateTeamCalendarStateSubscriptionInput {
  orgRelayId: string;
  teamId: string;
}

export interface IUpdateTeamCalendarStateSubscriptionPayload {
  __typename: 'UpdateTeamCalendarStateSubscriptionPayload';
  teamCalendarState: ITeamCalendarState;
}

/**
 * The state of a team calendar
 */
export interface ITeamCalendarState {
  __typename: 'TeamCalendarState';
  calendarId: string;
}

// eslint-enable
